import React, {useState, useEffect, useRef, useMemo} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Edit,
  MoreVertical,
  ArrowUp,
  Loader2,
  File,
  Clock,
  Check,
  ChevronsUpDown,
  Info,
} from 'lucide-react';
import {Tabs, TabsList, TabsTrigger, TabsContent} from '@/shared/ui/tabs';
import HistoryTimeline from '@/shared/components/HistoryTimeline';
import {useSessionTypeHistoryQuery} from '@/shared/hooks/useSessionTypeHistoryQuery';
import {queryKeys} from '@/lib/query-keys';
import {Button} from '@/shared/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/shared/ui/tooltip';
import {cn} from '@/lib/utils';
import {PageLoader} from '@/shared/ui/page-loader';
import {Card, CardContent} from '@/shared/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {Input} from '@/shared/ui/input';
import {DateInput} from '@/shared/ui/date-input';
import {Label} from '@/shared/ui/label';
import {Switch} from '@/shared/ui/switch';
import {Checkbox} from '@/shared/ui/checkbox';
import {Textarea} from '@/shared/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import {Badge} from '@/shared/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {ToggleGroup, ToggleGroupItem} from '@/shared/ui/toggle-group';
import {useToast} from '@/hooks/use-toast';
import {
  sessionTypesDetailedByProjectQueryKey,
  useDetailedSessionTypesByProjectQuery,
  useTasksByProjectQuery,
  useJobsByProjectQuery,
  fetchDetailedSessionTypesByProject,
} from '@/features/schedules';
import {schedulerApi} from '@/features/scheduler';
import {getProjectPermissionFlags} from '@/shared/lib/project-permissions';
import {format, parseISO} from 'date-fns';
import {useProjectRole} from '@/hooks/use-project-role';
import {useAuth} from '@/contexts/AuthContext';
import {CountryStateCityDropdown} from '@/shared/components';
import {
  formatSessionTimezoneDisplay,
  getTimezoneForLocationNames,
  getTimezoneOptions,
} from '@/utils/stateTimezone';
import {
  MeetingPointsList,
  formatMeetingPointAddress,
  sortMeetingPoints,
  type MeetingPointRow,
} from './MeetingPointsList';
import {MeetingPointHistory} from './MeetingPointHistory';
import {isBookingOptionalModeratorEnabled} from '@/config/env';
import {
  formatHourOnlyForDisplay,
  getTimeSelectTriggerClass,
} from '@/utils/timeDisplayFormat';
import {
  type SchedulingRules,
  type BreakHour,
  type BlockedDate,
  type SlotGenerationMode,
  DAY_LABELS,
  BREAK_TAG_PRESETS,
  emptySchedulingRules,
  rulesFromSessionType,
  rulesToPayload,
  DEFAULT_SLOT_GENERATION_MODE,
  MODERATOR_AVAILABILITY_MODE,
  SESSION_RULES_MODE,
  SLOT_MODE_BANNERS,
  slotGenerationModeFromSessionType,
  isModeratorAvailabilityMode,
} from '../model/schedulingRules';
import {bookingRulesFromSessionType} from '../model/bookingRules';
import {
  commitCalendarDateChange,
  formatSessionTypeCalendarDate,
  invalidTypedDateMessage,
  markCalendarDateValidity,
  sessionTypeDateFieldError,
  sessionTypeDateRangeError,
} from '../model/sessionTypeCalendarDate';
import {
  emptyWindowCapForm,
  resolveAllowMultipleBookings,
  validateWindowCapForm,
  windowCapFormFromBookingRules,
  windowCapFormToPayload,
  type WindowCapFormState,
} from '../model/participantWindowCap';
import {ParticipantWindowCapRule} from './ParticipantWindowCapRule';

// Constants
const MAX_ATTACHMENT_FILES = 3;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024; // 10MB in bytes

// Info tooltip shown next to the "Assign Moderators" section heading.
const MODERATOR_AVAILABILITY_SYNC_TOOLTIP =
  'A moderator can only be assigned to slots that fully match their configured availability. If a moderator\'s availability does not overlap with any generated slot, no sessions can be booked with them even if assigned. Review moderator availability under Schedule > Moderator\'s Availability before assigning.';

// Per-slot capacity: "Participants per Slot" field copy (flat per-slot cap)
const PARTICIPANTS_PER_SLOT_HINT =
  'Set a limit on how many participants can book the same time slot. Leave blank to allow unlimited bookings per slot. Enter a number to restrict bookings — e.g. enter 3 to allow only 3 participants per slot.';

// OD-1097 (+ OD-1171): "Allow multiple bookings per participant" toggle copy.
// Unlike the no-show limit, the max here is REQUIRED once enabled — a
// participant "more than one at once" with no cap isn't a meaningful setting.
const ALLOW_MULTI_BOOKING_HINT =
  'When enabled, the same participant can be booked into more than one session of this type at the same time, up to the configured maximum. When disabled, a participant is limited to a single session of this type, as today.';

// OD-1102: "No-show / cancellation limit" toggle copy. Unlike the multi-
// booking and moderator-concurrency toggles, a blank value here doesn't mean
// "unlimited" — it's required once enabled, since an alert threshold with no
// limit would never fire.
const GROUP_SIZE_CAP_HINT =
  'Limits how many participants can share one group booking for this session type. When off, group booking size is unlimited.';
const NO_SHOW_LIMIT_HINT =
  'When enabled, the PM is alerted once a participant reaches this many no-shows and cancellations for this session type (all no-shows and cancellations count equally). No sessions are auto-blocked — the PM decides what to do.';

// OD-1111: "Allow concurrent moderator bookings" toggle copy. Unlike the
// multi-booking max, the limit here is REQUIRED once enabled — a moderator
// running "more than one at once" with no cap isn't a meaningful setting.
const CONCURRENT_MODERATOR_HINT =
  'When enabled, a single moderator may be assigned to more than one session of this type in the exact same time slot, up to the configured maximum. When disabled, a moderator can run only one session of this type per slot, as today.';

// "Self-Booking Lead Time" field copy — applies only to the self-booking calendar, not scheduler manual booking.
const SELF_BOOKING_LEAD_TIME_HINT =
  'Hours from now before a participant can book a slot. Slots within this window are hidden.';

// Small "(i)" hover tooltip used next to field labels
const InfoHint = ({text}: {text: string}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          tabIndex={-1}
          aria-label="More information"
          className="inline-flex items-center text-muted-foreground hover:text-foreground focus:outline-none"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-relaxed">
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// One toggle + its paired numeric field, shared by the Booking Rules section
// in both the create form and the view/edit card (OD-1097 / OD-1102 / OD-1111).
// The field stays visible (not conditionally rendered) but disables when its
// toggle is off, per the reference design.
/** Display value for a Booking Rule's toggle: the in-progress edit while editing, else the stored flag. */
const resolveBookingRuleToggle = (
  isEditing: boolean,
  editValue: boolean,
  rawValue: unknown,
): boolean => (isEditing ? editValue : rawValue === true);

/** Display value for a Booking Rule's toggle when the record has no dedicated flag column — presence of the value is the toggle. */
const resolveBookingRuleToggleFromPresence = (
  isEditing: boolean,
  editValue: boolean,
  rawValue: unknown,
): boolean => (isEditing ? editValue : rawValue != null);

/** Display value for a Booking Rule's numeric field: the in-progress edit while editing, else the stored number as a string. */
const resolveBookingRuleFieldValue = (
  isEditing: boolean,
  editValue: string,
  rawValue: number | null | undefined,
): string => {
  if (isEditing) return editValue;
  return rawValue != null ? String(rawValue) : '';
};

const BookingRuleRow = ({
  id,
  title,
  description,
  hint,
  checked,
  onCheckedChange,
  isEditing,
  fieldLabel,
  fieldValue,
  onFieldChange,
  fieldPlaceholder,
  fieldMin,
  fieldFallback = '—',
  control = 'switch',
  toggleDisabled = false,
}: {
  id: string;
  title: string;
  description: string;
  hint: string;
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  isEditing: boolean;
  fieldLabel: string;
  fieldValue: string;
  onFieldChange?: (value: string) => void;
  fieldPlaceholder: string;
  fieldMin: string;
  fieldFallback?: string;
  /** Cap group booking size uses a checkbox; other booking rules use a switch. */
  control?: 'switch' | 'checkbox';
  toggleDisabled?: boolean;
}) => {
  const handleCheckedChange =
    isEditing && onCheckedChange
      ? (value: boolean | 'indeterminate') => onCheckedChange(value === true)
      : undefined;

  return (
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3 flex-1">
        {control === 'checkbox' ? (
          <Checkbox
            id={id}
            checked={checked}
            disabled={!isEditing || toggleDisabled}
            onCheckedChange={handleCheckedChange}
            // Blue when ticked, rather than the shared Checkbox's `bg-primary`
            // navy. Overridden here so other checkboxes keep the theme colour.
            className="mt-1 border-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
          />
        ) : (
          <Switch
            id={id}
            checked={checked}
            disabled={!isEditing || toggleDisabled}
            onCheckedChange={handleCheckedChange}
            className="mt-0.5"
          />
        )}
        <div>
          <Label
            htmlFor={id}
            className="flex items-center gap-1.5 text-sm font-medium text-foreground"
          >
            {title}
            <InfoHint text={hint} />
          </Label>
          <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>
      <div className="w-full sm:w-52 shrink-0 space-y-1.5">
        <Label className="text-sm font-normal text-foreground">
          {fieldLabel}
        </Label>
        {isEditing ? (
          <Input
            type="number"
            min={fieldMin}
            placeholder={fieldPlaceholder}
            value={fieldValue}
            disabled={!checked}
            onChange={(e) => onFieldChange?.(e.target.value)}
            className={checked ? 'bg-white' : 'bg-gray-50 text-gray-400'}
          />
        ) : (
          <Input
            readOnly
            disabled
            value={checked ? fieldValue || fieldFallback : '—'}
            className="bg-gray-50 text-gray-700"
          />
        )}
      </div>
    </div>
  );
};

// Helper function to validate file size
const validateFileSize = (file: File): {valid: boolean; error?: string} => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `File "${file.name}" is ${fileSizeMB}MB. Maximum file size is ${MAX_FILE_SIZE_MB}MB.`,
    };
  }
  return {valid: true};
};

// Helper functions for file attachments
const getFileCount = (files: File[], existingPaths: string[] = []): number => {
  return files.length + existingPaths.length;
};

const getFileStatusText = (
  files: File[],
  existingPaths: string[] = [],
): string => {
  const count = getFileCount(files, existingPaths);
  return count > 0 ? `${count} file(s) selected` : 'No file chosen';
};

const getRemainingSlots = (
  files: File[],
  existingPaths: string[] = [],
): number => {
  return MAX_ATTACHMENT_FILES - getFileCount(files, existingPaths);
};

// Helper function to create FormData from payload and files
const createFormData = (payload: any, files: File[] = []): FormData => {
  const formData = new FormData();
  
  // Append all fields to FormData
  Object.keys(payload).forEach((key) => {
    const value = payload[key];
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        // Arrays need to be stringified for FormData
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value.toString());
      }
    }
  });
  
  // Append files with the name "attachment" (backend expects this)
  files.forEach((file) => {
    formData.append('attachment', file);
  });

  return formData;
};

/**
 * Chooses the request body for a session type update: multipart FormData when
 * new files are attached, FormData with an explicit blank attachment path
 * when removing the existing attachment, or the plain JSON payload otherwise.
 */
function resolveFinalUpdatePayload(
  payload: any,
  hasFiles: boolean,
  files: File[],
  removeAttachment: boolean,
): any {
  if (hasFiles && files.length > 0) {
    return createFormData(payload, files);
  }
  if (removeAttachment) {
    // Explicitly removing attachment - send email_attachment_path as null
    const formData = createFormData(payload);
    formData.append('email_attachment_path', '');
    return formData;
  }
  // No file changes, send as regular JSON
  return payload;
}

const formatTimezoneDisplay = formatSessionTimezoneDisplay;

const getMeetingPointLocationValidationError = (
  country: string,
  state: string,
  city: string,
): string | null => {
  if (!country.trim()) return 'Country is required';
  if (!state.trim()) return 'State is required';
  if (!city.trim()) return 'City is required';
  return null;
};

function SessionTimezoneField({
  timezone,
  onTimezoneChange,
  showHelper = false,
  disabled = false,
}: Readonly<{
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  showHelper?: boolean;
  disabled?: boolean;
}>) {
  // Full IANA timezone list from the runtime tz database. The auto-resolved
  // zone is always included, even when it's an alias the runtime omits from its
  // canonical list (e.g. Asia/Kolkata vs Asia/Calcutta), so it stays selectable.
  const timezoneOptions = useMemo(() => {
    const base = getTimezoneOptions();
    if (timezone && !base.some((o) => o.value === timezone)) {
      return [{value: timezone, label: formatTimezoneDisplay(timezone)}, ...base];
    }
    return base;
  }, [timezone]);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Manual filtering (cmdk's shouldFilter is off) so we can cap the rendered
  // rows — the full IANA list is ~400 entries.
  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return timezoneOptions.slice(0, 250);
    return timezoneOptions
      .filter((o) => o.label.toLowerCase().includes(q))
      .slice(0, 500);
  }, [timezoneOptions, search]);

  const displayLabel = timezone ? formatTimezoneDisplay(timezone) : '';

  return (
    <div className="space-y-1.5 mt-4 pt-4 border-t border-gray-200">
      <Label className="text-sm font-normal text-foreground">
        Session timezone (to display in all notifications)
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between bg-white font-normal"
          >
            {displayLabel ? (
              <span className="truncate">{displayLabel}</span>
            ) : (
              <span className="text-muted-foreground">
                Auto-populated from meeting point location
              </span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] max-w-[400px] p-0"
          align="start"
          sideOffset={4}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <Command
            shouldFilter={false}
            className="max-h-[min(320px,50vh)] overflow-hidden"
          >
            <CommandInput
              placeholder="Search timezone..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList className="max-h-[min(280px,45vh)] min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y">
              <CommandEmpty>No timezone found.</CommandEmpty>
              <CommandGroup className="overflow-visible">
                {filteredOptions.map((tz) => (
                  <CommandItem
                    key={tz.value}
                    value={tz.value}
                    onSelect={() => {
                      onTimezoneChange(tz.value);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        timezone === tz.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {tz.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showHelper ? (
        <p className="text-xs text-muted-foreground">
          Auto-filled from meeting point state (or country when needed). You can
          change it if needed.
        </p>
      ) : null}
    </div>
  );
}

// Session type duration options (in minutes)
const SESSION_DURATION_OPTIONS = [
  {value: '30', label: '30 minutes'},
  {value: '45', label: '45 minutes'},
  {value: '60', label: '60 minutes'},
  {value: '90', label: '90 minutes'},
  {value: '120', label: '120 minutes'},
  {value: '150', label: '150 minutes'},
  {value: '180', label: '180 minutes'},
  {value: '210', label: '210 minutes'},
  {value: '240', label: '240 minutes'},
  {value: '270', label: '270 minutes'},
  {value: '300', label: '300 minutes'},
  {value: '330', label: '330 minutes'},
  {value: '360', label: '360 minutes'},
] as const;

/** Normalize DB/API TIME (e.g. 09:00:00) for `<input type="time" />`. */
const bookingDailyTimeToInput = (v: unknown): string => {
  if (v == null || v === '') return '';
  const s = unknownFieldToString(v);
  const m = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!m) return '';
  return `${m[1].padStart(2, '0')}:${m[2]}`;
};

/** When both dates are the same, require start time &lt; end time. */
const validateDailyTimeRangeOrder = (
  startDate: string,
  endDate: string,
  start: string,
  end: string,
): {ok: true} | {ok: false; message: string} => {
  const ts = start.trim();
  const te = end.trim();
  if (!ts || !te) return {ok: true};
  const startToMin = (t: string) => {
    const [hStr, mStr = '0'] = t.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    if (h > 23) return null;
    return h * 60 + m;
  };
  const endToMin = (t: string) => {
    const [hStr, mStr = '0'] = t.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    if (h === 24) return m === 0 ? 24 * 60 : null;
    if (h > 23) return null;
    return h * 60 + m;
  };
  const sm = startToMin(ts);
  const em = endToMin(te);
  if (sm === null || em === null) {
    return {
      ok: false,
      message:
        sm === null
          ? 'Invalid start time.'
          : 'End of day must be 00:00 (minutes must be 00 when hour is 00).',
    };
  }
  if (startDate.trim() === endDate.trim() && em <= sm) {
    return {
      ok: false,
      message: 'Start time must be before end time.',
    };
  }
  return {ok: true};
};

const DAILY_RANGE_DEFAULT_START = {h: '00', m: '00'} as const;
const DAILY_RANGE_DEFAULT_END = {h: '24', m: '00'} as const;

const DAILY_RANGE_HOUR_OPTIONS = Array.from({length: 24}, (_, i) =>
  i.toString().padStart(2, '0'),
);

function DailyRangeHourSelect({
  value,
  onValueChange,
  disabled,
}: Readonly<{
  value: string;
  onValueChange: (v: string) => void;
  disabled?: boolean;
}>) {
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger
        className={getTimeSelectTriggerClass(
          disabled
            ? 'text-foreground disabled:bg-white data-[disabled]:bg-white disabled:text-foreground data-[disabled]:text-foreground'
            : '',
        )}
      >
        <SelectValue>{formatHourOnlyForDisplay(value)}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {DAILY_RANGE_HOUR_OPTIONS.map((hourVal) => (
          <SelectItem key={hourVal} value={hourVal}>
            {formatHourOnlyForDisplay(hourVal)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function getDailyTimePartsForSelect(
  timeStr: string,
  defaultH: string,
  defaultM: string,
) {
  const t = timeStr.trim();
  if (!t || !t.includes(':')) {
    return {hours: defaultH, minutes: defaultM};
  }
  const [h = '00', m = '00'] = t.split(':');
  return {
    hours: h.padStart(2, '0').slice(0, 2),
    minutes: m.padStart(2, '0').slice(0, 2),
  };
}

/** Override one part (hours/minutes) of a time, keeping the other from `parts`. */
function resolveTimePart(
  parts: {hours: string; minutes: string},
  field: 'hours' | 'minutes',
  value: string,
): {hours: string; minutes: string} {
  return {
    hours: field === 'hours' ? value : parts.hours,
    minutes: field === 'minutes' ? value : parts.minutes,
  };
}

function applyDailyTimeSelectChange(
  currentStart: string,
  currentEnd: string,
  kind: 'start' | 'end',
  field: 'hours' | 'minutes',
  value: string,
): {start: string; end: string} {
  const sp = getDailyTimePartsForSelect(
    currentStart,
    DAILY_RANGE_DEFAULT_START.h,
    DAILY_RANGE_DEFAULT_START.m,
  );
  const ep = getDailyTimePartsForSelect(
    currentEnd,
    DAILY_RANGE_DEFAULT_END.h,
    DAILY_RANGE_DEFAULT_END.m,
  );
  let nextStart = currentStart.trim();
  let nextEnd = currentEnd.trim();

  if (kind === 'start') {
    const r = resolveTimePart(sp, field, value);
    nextStart = `${r.hours}:${r.minutes}`;
    if (!nextEnd) nextEnd = `${ep.hours}:${ep.minutes}`;
  } else {
    const r = resolveTimePart(ep, field, value);
    nextEnd = r.hours === '00' ? '24:00' : `${r.hours}:${r.minutes}`;
    if (!nextStart) nextStart = `${sp.hours}:${sp.minutes}`;
  }
  return {start: nextStart, end: nextEnd};
}

function SessionTypeDailyTimeRangeField({
  startTime,
  endTime,
  onTimesChange,
  disabled = false,
  helperText,
}: Readonly<{
  startTime: string;
  endTime: string;
  onTimesChange: (next: {start: string; end: string}) => void;
  disabled?: boolean;
  helperText?: string;
}>) {
  const unset = !startTime.trim() && !endTime.trim();

  if (disabled && unset) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-normal text-foreground">
          Time Range{' '}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <div className="flex flex-wrap items-center gap-2 opacity-80">
          <div
            className={`${getTimeSelectTriggerClass()} rounded-md border px-3 flex items-center`}
          >
            {formatHourOnlyForDisplay('00')}
          </div>
          <span className="text-muted-foreground">:</span>
          <div className="w-16 h-9 rounded-md border bg-white px-3 flex items-center">
            00
          </div>
          <Clock className="h-4 w-4 text-muted-foreground ml-1 shrink-0" />
          <span className="text-sm text-muted-foreground px-0.5">to</span>
          <div
            className={`${getTimeSelectTriggerClass()} rounded-md border px-3 flex items-center`}
          >
            {formatHourOnlyForDisplay('00')}
          </div>
          <span className="text-muted-foreground">:</span>
          <div className="w-16 h-9 rounded-md border bg-white px-3 flex items-center">
            00
          </div>
          <Clock className="h-4 w-4 text-muted-foreground ml-1 shrink-0" />
        </div>
        {helperText ? (
          <p className="text-xs text-muted-foreground">{helperText}</p>
        ) : null}
      </div>
    );
  }

  const startParts = getDailyTimePartsForSelect(
    startTime,
    DAILY_RANGE_DEFAULT_START.h,
    DAILY_RANGE_DEFAULT_START.m,
  );
  const endPartsRaw = getDailyTimePartsForSelect(
    endTime,
    DAILY_RANGE_DEFAULT_END.h,
    DAILY_RANGE_DEFAULT_END.m,
  );
  const endParts =
    endPartsRaw.hours === '24'
      ? {hours: '00', minutes: '00'}
      : endPartsRaw;

  const update = (
    kind: 'start' | 'end',
    field: 'hours' | 'minutes',
    value: string,
  ) => {
    if (disabled) return;
    onTimesChange(
      applyDailyTimeSelectChange(startTime, endTime, kind, field, value),
    );
  };

  const minuteTriggerClass = getTimeSelectTriggerClass(
    disabled
      ? 'text-foreground disabled:bg-white data-[disabled]:bg-white disabled:text-foreground data-[disabled]:text-foreground'
      : '',
  );

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-normal text-foreground">
        Time Range{' '}
        <span className="text-muted-foreground font-normal">(optional)</span>
      </Label>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <DailyRangeHourSelect
            value={startParts.hours}
            onValueChange={(v) => update('start', 'hours', v)}
            disabled={disabled}
          />
          <span className="text-muted-foreground">:</span>
          <Select
            value={startParts.minutes}
            onValueChange={(v) => update('start', 'minutes', v)}
            disabled={disabled}
          >
            <SelectTrigger className={minuteTriggerClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({length: 60}, (_, i) => (
                <SelectItem
                  key={i}
                  value={i.toString().padStart(2, '0')}
                >
                  {i.toString().padStart(2, '0')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Clock className="h-4 w-4 text-muted-foreground ml-1 shrink-0" />
        </div>
        <span className="text-sm text-muted-foreground px-0.5">to</span>
        <div className="flex items-center gap-1">
          <DailyRangeHourSelect
            value={endParts.hours}
            onValueChange={(v) => update('end', 'hours', v)}
            disabled={disabled}
          />
          <span className="text-muted-foreground">:</span>
          <Select
            value={endParts.minutes}
            onValueChange={(v) => update('end', 'minutes', v)}
            disabled={disabled || endParts.hours === '00'}
          >
            <SelectTrigger className={minuteTriggerClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({length: 60}, (_, i) => (
                <SelectItem
                  key={i}
                  value={i.toString().padStart(2, '0')}
                >
                  {i.toString().padStart(2, '0')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Clock className="h-4 w-4 text-muted-foreground ml-1 shrink-0" />
        </div>
      </div>
      {helperText ? (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      ) : null}
    </div>
  );
}

const OTHER_TAG_VALUE = '__other__';

// Hour (00-23) + minute (00-59) picker for a break range bound. Stores 'HH:MM'.
function BreakTimeSelect({
  value,
  onChange,
  disabled,
}: Readonly<{
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}>) {
  const parts = getDailyTimePartsForSelect(value, '00', '00');
  const setPart = (field: 'hours' | 'minutes', v: string) => {
    const next = resolveTimePart(parts, field, v);
    onChange(`${next.hours}:${next.minutes}`);
  };
  const triggerClass = getTimeSelectTriggerClass(
    disabled
      ? 'text-foreground disabled:bg-white data-[disabled]:bg-white disabled:text-foreground data-[disabled]:text-foreground'
      : '',
  );
  return (
    <div className="flex items-center gap-1">
      <Select
        value={parts.hours}
        onValueChange={(v) => setPart('hours', v)}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClass}>
          <SelectValue>{formatHourOnlyForDisplay(parts.hours)}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map(
            (h) => (
              <SelectItem key={h} value={h}>
                {formatHourOnlyForDisplay(h)}
              </SelectItem>
            ),
          )}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">:</span>
      <Select
        value={parts.minutes}
        onValueChange={(v) => setPart('minutes', v)}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClass}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Array.from({length: 60}, (_, i) => i.toString().padStart(2, '0')).map(
            (m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ),
          )}
        </SelectContent>
      </Select>
      <Clock className="h-4 w-4 text-muted-foreground ml-1 shrink-0" />
    </div>
  );
}

/**
 * Optional scheduling-rules inputs (Working Days, Break Hours, Blocked Dates).
 * All fields are optional; leaving them blank keeps existing booking behavior.
 * Read-only when `disabled` (non PM/Admin or view mode).
 */
const getBreakTagSelectValue = (
  tag: string | undefined,
  showOther: boolean,
): string => {
  if (showOther) return OTHER_TAG_VALUE;
  if (tag && BREAK_TAG_PRESETS.includes(tag as (typeof BREAK_TAG_PRESETS)[number])) {
    return tag;
  }
  return '';
};

// Assign a stable client-only React key to break/blocked-date rows loaded
// from the backend, which has no id for them, so list edits (add/remove)
// don't rely on array index as the React key.
const withStableRuleKeys = (rules: SchedulingRules): SchedulingRules => ({
  ...rules,
  breakHours: rules.breakHours.map((b) => ({key: crypto.randomUUID(), ...b})),
  blockedDates: rules.blockedDates.map((bd) => ({key: crypto.randomUUID(), ...bd})),
});

// Re-index the "Other" tracking set around a removed break row.
const reindexOtherRowsAfterRemoval = (
  rows: Set<number>,
  removedIndex: number,
): Set<number> => {
  const next = new Set<number>();
  rows.forEach((i) => {
    if (i < removedIndex) next.add(i);
    else if (i > removedIndex) next.add(i - 1);
  });
  return next;
};

function BreakHourRow({
  breakHour,
  index,
  disabled,
  showOther,
  selectValue,
  onUpdate,
  onRemove,
  onTagSelect,
}: Readonly<{
  breakHour: BreakHour;
  index: number;
  disabled: boolean;
  showOther: boolean;
  selectValue: string;
  onUpdate: (index: number, patch: Partial<BreakHour>) => void;
  onRemove: (index: number) => void;
  onTagSelect: (index: number, v: string) => void;
}>) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <BreakTimeSelect
        value={breakHour.start}
        onChange={(v) => onUpdate(index, {start: v})}
        disabled={disabled}
      />
      <span className="text-sm text-muted-foreground px-0.5">to</span>
      <BreakTimeSelect
        value={breakHour.end}
        onChange={(v) => onUpdate(index, {end: v})}
        disabled={disabled}
      />
      <Select
        value={selectValue}
        onValueChange={(v) => onTagSelect(index, v)}
        disabled={disabled}
      >
        <SelectTrigger className="w-[150px] bg-white">
          <SelectValue placeholder="Tag (optional)" />
        </SelectTrigger>
        <SelectContent>
          {BREAK_TAG_PRESETS.map((preset) => (
            <SelectItem key={preset} value={preset}>
              {preset}
            </SelectItem>
          ))}
          <SelectItem value={OTHER_TAG_VALUE}>Other</SelectItem>
        </SelectContent>
      </Select>
      {showOther && (
        <Input
          value={breakHour.tag ?? ''}
          onChange={(e) => onUpdate(index, {tag: e.target.value})}
          placeholder="Custom tag"
          className="w-[150px] bg-white"
          disabled={disabled}
        />
      )}
      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Remove break"
          onClick={() => onRemove(index)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function BlockedDateRow({
  blockedDate,
  index,
  disabled,
  idPrefix,
  onUpdate,
  onRemove,
}: Readonly<{
  blockedDate: BlockedDate;
  index: number;
  disabled: boolean;
  idPrefix: string;
  onUpdate: (index: number, patch: Partial<BlockedDate>) => void;
  onRemove: (index: number) => void;
}>) {
  return (
    <div className="flex items-center gap-2">
      <DateInput
        value={blockedDate.date}
        onChange={(e) => onUpdate(index, {date: e.target.value})}
        name={`${idPrefix}-blocked-date-${index}`}
        disabled={disabled}
        className="w-44 shrink-0 bg-white"
      />
      <Input
        value={blockedDate.label ?? ''}
        onChange={(e) => onUpdate(index, {label: e.target.value})}
        placeholder="Label (optional)"
        className="flex-1 min-w-0 bg-white"
        disabled={disabled}
      />
      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Remove blocked date"
          onClick={() => onRemove(index)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

/**
 * OD-1002: "Generate slots based on" selector + info banner. PM/Admin only;
 * defaults to Session Rules. Switching modes never clears the rule fields
 * (their values live in separate state), satisfying AC8.
 */
const SLOT_MODE_OPTIONS: ReadonlyArray<{
  mode: SlotGenerationMode;
  title: string;
  subtitle: string;
}> = [
  {
    mode: SESSION_RULES_MODE,
    title: 'Session Rules',
    subtitle: 'Use the time window and rules below',
  },
  {
    mode: MODERATOR_AVAILABILITY_MODE,
    title: 'Moderator Availability',
    subtitle: 'Derive slots from assigned moderators',
  },
];

function SlotGenerationModeField({
  value,
  onChange,
  disabled = false,
  idPrefix = 'slot-mode',
}: Readonly<{
  value: SlotGenerationMode;
  onChange: (next: SlotGenerationMode) => void;
  disabled?: boolean;
  idPrefix?: string;
}>) {
  return (
    <div className="space-y-3">
      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Generate slots based on
      </Label>
      <div
        role="radiogroup"
        aria-label="Generate slots based on"
        className="grid gap-4 sm:grid-cols-2"
      >
        {SLOT_MODE_OPTIONS.map((option) => {
          const selected = value === option.mode;
          const optionId = `${idPrefix}-${option.mode}`;
          return (
            <label
              key={option.mode}
              htmlFor={optionId}
              aria-label={`${option.title}: ${option.subtitle}`}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
                selected
                  ? 'border-primary bg-primary/5'
                  : 'border-input hover:bg-muted/40',
                disabled && 'cursor-not-allowed opacity-60',
              )}
            >
              <input
                type="radio"
                id={optionId}
                name={idPrefix}
                value={option.mode}
                checked={selected}
                disabled={disabled}
                onChange={() => onChange(option.mode)}
                className="mt-0.5 h-5 w-5 shrink-0 accent-primary"
              />
              <span className="flex flex-col">
                <span className="text-base font-semibold text-foreground">
                  {option.title}
                </span>
                <span className="text-sm text-muted-foreground">
                  {option.subtitle}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      <p className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{SLOT_MODE_BANNERS[value]}</span>
      </p>
    </div>
  );
}

function SchedulingRulesFields({
  value,
  onChange,
  disabled = false,
  idPrefix,
}: Readonly<{
  value: SchedulingRules;
  onChange: (next: SchedulingRules) => void;
  disabled?: boolean;
  idPrefix: string;
}>) {
  // Track break rows whose tag dropdown is set to "Other" (free-text mode),
  // including the case where the free-text value is still empty.
  const [otherRows, setOtherRows] = useState<Set<number>>(new Set());

  const update = (patch: Partial<SchedulingRules>) => {
    if (disabled) return;
    onChange({...value, ...patch});
  };

  const setWorkingDays = (vals: string[]) =>
    update({
      workingDays: vals
        .map((v) => Number(v))
        .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
        .sort((a, b) => a - b),
    });

  const addBreak = () =>
    update({
      breakHours: [
        ...value.breakHours,
        {start: '12:00', end: '13:00', key: crypto.randomUUID()},
      ],
    });

  const updateBreak = (index: number, patch: Partial<BreakHour>) => {
    const next = value.breakHours.map((b, i) =>
      i === index ? {...b, ...patch} : b,
    );
    update({breakHours: next});
  };

  const removeBreak = (index: number) => {
    update({breakHours: value.breakHours.filter((_, i) => i !== index)});
    setOtherRows((prev) => reindexOtherRowsAfterRemoval(prev, index));
  };

  const handleBreakTagSelect = (index: number, v: string) => {
    if (v === OTHER_TAG_VALUE) {
      setOtherRows((prev) => new Set(prev).add(index));
      updateBreak(index, {tag: ''});
      return;
    }
    setOtherRows((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
    updateBreak(index, {tag: v});
  };

  const addBlockedDate = () =>
    update({
      blockedDates: [
        ...value.blockedDates,
        {date: '', label: '', key: crypto.randomUUID()},
      ],
    });

  const updateBlockedDate = (index: number, patch: Partial<BlockedDate>) => {
    const next = value.blockedDates.map((bd, i) =>
      i === index ? {...bd, ...patch} : bd,
    );
    update({blockedDates: next});
  };

  const removeBlockedDate = (index: number) =>
    update({blockedDates: value.blockedDates.filter((_, i) => i !== index)});

  const isOtherTag = (index: number, tag: string | undefined) =>
    otherRows.has(index) ||
    (!!tag && !BREAK_TAG_PRESETS.includes(tag as (typeof BREAK_TAG_PRESETS)[number]));

  return (
    <div className="space-y-5">
      {/* Working Days */}
      <div className="space-y-1.5">
        <Label className="text-sm font-normal text-foreground">
          Working Days{' '}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <ToggleGroup
          type="multiple"
          value={value.workingDays.map((d) => String(d))}
          onValueChange={setWorkingDays}
          className="flex flex-wrap justify-start gap-2"
          disabled={disabled}
        >
          {DAY_LABELS.map((label, day) => (
            <ToggleGroupItem
              key={label}
              value={String(day)}
              aria-label={label}
              disabled={disabled}
              className="h-9 px-4 rounded-md border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            >
              {label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {/* Break Hours */}
      <div className="space-y-1.5">
        <Label className="text-sm font-normal text-foreground">
          Break Hours{' '}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <div className="space-y-2">
          {value.breakHours.map((b, i) => {
            const showOther = isOtherTag(i, b.tag);
            return (
              <BreakHourRow
                key={b.key ?? i}
                breakHour={b}
                index={i}
                disabled={disabled}
                showOther={showOther}
                selectValue={getBreakTagSelectValue(b.tag, showOther)}
                onUpdate={updateBreak}
                onRemove={removeBreak}
                onTagSelect={handleBreakTagSelect}
              />
            );
          })}
        </div>
        {!disabled && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="px-0 text-primary"
            onClick={addBreak}
          >
            <Plus className="h-4 w-4 mr-1" /> Add Break
          </Button>
        )}
      </div>

      {/* Blocked Dates */}
      <div className="space-y-1.5">
        <Label className="text-sm font-normal text-foreground">
          Blocked Dates{' '}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <div className="space-y-2">
          {value.blockedDates.map((bd, i) => (
            <BlockedDateRow
              key={bd.key ?? i}
              blockedDate={bd}
              index={i}
              disabled={disabled}
              idPrefix={idPrefix}
              onUpdate={updateBlockedDate}
              onRemove={removeBlockedDate}
            />
          ))}
        </div>
        {!disabled && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="px-0 text-primary"
            onClick={addBlockedDate}
          >
            <Plus className="h-4 w-4 mr-1" /> Add Blocked Date
          </Button>
        )}
      </div>
    </div>
  );
}

interface OneFormaJob {
  job_id: string;
  job_name: string;
}

const formatSessionTypeForDisplay = (type: any) => ({
  id: String(type.id),
  name: type.name,
  duration: type.duration_minutes,
  description: type.description || '',
  locations: type.locations || [],
  locationsCount: type.locations?.length || 0,
  enabled: type.is_active,
  location_ids: type.locations?.map((loc: any) => loc.id.toString()) || [],
  moderator_ids: type.moderator_ids || [],
  active_moderator_count:
    typeof type.active_moderator_count === 'number' ? type.active_moderator_count : undefined,
  enable_walkin: type.enable_walkin || false,
  sessionTypeStartDate:
    type.sessionTypeStartDate || type.schedulingStartDate || type.start_date || null,
  sessionTypeEndDate:
    type.sessionTypeEndDate || type.schedulingEndDate || type.end_date || null,
  bookingDailyStartTime: bookingDailyTimeToInput(
    type.booking_daily_start_time ?? type.bookingDailyStartTime,
  ),
  bookingDailyEndTime: bookingDailyTimeToInput(
    type.booking_daily_end_time ?? type.bookingDailyEndTime,
  ),
  minimum_notice: type.minimum_notice || null,
  buffer_before: type.buffer_before || null,
  buffer_after: type.buffer_after || null,
  participants_per_slot: type.participants_per_slot ?? null,
  allow_multiple_bookings_per_participant:
    type.allow_multiple_bookings_per_participant || false,
  max_sessions_per_participant: type.max_sessions_per_participant ?? null,
  no_show_cancellation_limit: type.no_show_cancellation_limit ?? null,
  allow_concurrent_moderator_bookings:
    type.allow_concurrent_moderator_bookings || false,
  max_concurrent_moderator_sessions:
    type.max_concurrent_moderator_sessions ?? null,
  cap_group_booking_size: type.cap_group_booking_size || false,
  max_group_size: type.max_group_size ?? null,
  cap_participant_sessions_in_window:
    type.cap_participant_sessions_in_window || false,
  window_limit_basis: type.window_limit_basis ?? null,
  window_days: type.window_days ?? null,
  window_capacity: type.window_capacity ?? null,
  max_hours_per_day: type.max_hours_per_day ?? null,
  scheduling_rules: type.scheduling_rules ?? type.schedulingRules ?? null,
  slot_generation_mode:
    type.slot_generation_mode ?? type.slotGenerationMode ?? null,
  timezone: type.timezone || 'America/Los_Angeles',
  participant_booking_rules: type.participant_booking_rules || false,
  task_ids: type.task_ids || [],
  tasks: type.tasks || [],
  disabled_by_name: type.disabled_by_name || null,
  email_attachment_path: type.email_attachment_path || null,
  disabled_at: type.disabled_at || null,
  session_type: type.session_type || 'onsite',
  job_id:
    type.job_id !== undefined && type.job_id !== null ? String(type.job_id) : '',
  job_name: type.job_name || '',
});

interface SessionTypesTabProps {
  selectedProject: string;
  projectIdFromUrl: string | null;
  locations: any[];
  projectModerators: any[];
  projectModeratorsLoading?: boolean;
  projectsState: any;
  // When the Schedule page's dynamic filter is active in one of the four
  // session-type dimensions (Session Type / Session Name / Location /
  // Moderator), the parent passes a Set of session-type names that survived
  // that filter and this tab only renders those rows. `undefined` means no
  // page-level filter is active and all session types are rendered.
  allowedSessionTypeNames?: Set<string>;
}

/** Shape of the fields validated by the session-type validators below. */
type SessionTypeValidationFields = {
  name: string;
  jobId: string;
  startDate: string;
  endDate: string;
  minimumNotice: string;
  duration: string;
  bufferBefore: string;
  bufferAfter: string;
  selectedTasks: string;
  availableTasksCount: number;
  selectedModerators?: string[];
  meetingPointName?: string;
  meetingPointStreetAddress?: string;
  meetingPointCountry?: string;
  meetingPointState?: string;
  meetingPointCity?: string;
  existingLocationIds?: string[];
  sessionType?: 'onsite' | 'offsite';
};

const isNonNegativeIntInput = (raw: string): boolean =>
  !raw?.trim() || (!Number.isNaN(Number.parseInt(raw)) && Number.parseInt(raw) >= 0);

// Stricter than isNonNegativeIntInput: rejects fractional values (e.g. "1.5") instead of silently truncating them.
const isNonNegativeWholeNumberInput = (raw: string): boolean => {
  const trimmed = raw?.trim();
  if (!trimmed) return true;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 0;
};

function validateBasicSessionFields(
  f: SessionTypeValidationFields,
): string | undefined {
  if (!f.name.trim()) return 'Session type name is required';
  if (!f.jobId.trim()) return 'Job is required';
  const startError = sessionTypeDateFieldError('Start date', f.startDate);
  if (startError) return startError;
  const endError = sessionTypeDateFieldError('End date', f.endDate);
  if (endError) return endError;
  return sessionTypeDateRangeError(f.startDate.trim(), f.endDate.trim());
}

function validateTimingFields(
  f: SessionTypeValidationFields,
): string | undefined {
  if (!isNonNegativeWholeNumberInput(f.minimumNotice)) {
    return 'Self-Booking Lead Time must be 0 or a positive whole number';
  }
  if (!f.duration || Number.parseInt(f.duration) <= 0) {
    return 'Duration is required and must be greater than 0';
  }
  if (!isNonNegativeIntInput(f.bufferBefore)) {
    return 'Buffer time before session must be 0 or greater';
  }
  if (!isNonNegativeIntInput(f.bufferAfter)) {
    return 'Buffer time after session must be 0 or greater';
  }
  return undefined;
}

function validateNewMeetingPointFields(
  f: SessionTypeValidationFields,
  isOnsite: boolean,
): string | undefined {
  if (isOnsite) {
    if (!f.meetingPointName!.trim()) {
      return 'Meeting Point Name is required for onsite sessions';
    }
    if (!f.meetingPointStreetAddress!.trim()) {
      return 'Meeting Point Street Address is required for onsite sessions';
    }
    return (
      getMeetingPointLocationValidationError(
        f.meetingPointCountry ?? '',
        f.meetingPointState ?? '',
        f.meetingPointCity ?? '',
      ) || undefined
    );
  }
  if (f.meetingPointName!.trim() && !f.meetingPointStreetAddress!.trim()) {
    return 'Meeting Point Street Address is required when Meeting Point Name is provided';
  }
  if (!f.meetingPointName!.trim() && f.meetingPointStreetAddress!.trim()) {
    return 'Meeting Point Name is required when Street Address is provided';
  }
  return undefined;
}

function validateMeetingPointFields(
  f: SessionTypeValidationFields,
): string | undefined {
  const isOnsite = f.sessionType === 'onsite';
  // Create handler passes new meeting-point fields; update handler passes existing ids.
  if (
    f.meetingPointName !== undefined &&
    f.meetingPointStreetAddress !== undefined
  ) {
    return validateNewMeetingPointFields(f, isOnsite);
  }
  if (
    f.existingLocationIds !== undefined &&
    isOnsite &&
    (!f.existingLocationIds || f.existingLocationIds.length === 0)
  ) {
    return 'At least one meeting point is required for onsite sessions';
  }
  return undefined;
}

function validateModeratorsAndTasks(
  f: SessionTypeValidationFields,
): string | undefined {
  if (
    f.selectedModerators !== undefined &&
    (!f.selectedModerators || f.selectedModerators.length === 0)
  ) {
    return 'At least one moderator is required';
  }
  if (!f.selectedTasks || f.selectedTasks.trim() === '') {
    return 'Task is required';
  }
  return undefined;
}

/** Return a copy of the array with the element at `index` removed. */
function removeAt<T>(arr: T[], index: number): T[] {
  return arr.filter((_, i) => i !== index);
}

/** Patch the matching location on a session type; returns it unchanged if no match. */
function patchSessionTypeLocation(
  st: any,
  locationId: string | number,
  patch: Record<string, unknown>,
): any {
  const locations = st.locations || [];
  const matches = (loc: any) => String(loc.id) === String(locationId);
  if (!locations.some(matches)) return st;
  return {
    ...st,
    locations: locations.map((loc: any) =>
      matches(loc) ? {...loc, ...patch} : loc,
    ),
  };
}

/** Find a moderator by id in a moderator list. */
function findModeratorById(moderators: any[], id: string): any {
  return moderators.find((m: any) => m.id === id);
}

/** True when a moderator user id is in the selected list (string-compared). */
function isModeratorSelected(
  selectedIds: any[],
  moderatorUserId: string,
): boolean {
  return selectedIds.some((id: any) => String(id) === moderatorUserId);
}

/** Normalize a session type's job_id to a string ('' when unset). */
function sessionTypeJobIdString(jobId: unknown): string {
  // Only primitives are stringified; objects (and null/undefined) yield '' rather than '[object Object]'.
  if (typeof jobId === 'string' || typeof jobId === 'number' || typeof jobId === 'boolean') {
    return String(jobId);
  }
  return '';
}

/** Stringify a field pulled from an API/DB record of unknown shape; only primitives are stringified. */
function unknownFieldToString(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : '';
}

/** Format a session-type DATE to yyyy-MM-dd without shifting the calendar day. */
function formatYmdOrEmpty(iso: string | undefined | null): string {
  return formatSessionTypeCalendarDate(iso, 'yyyy-MM-dd');
}

/** First location id of a session type as a string ('' when none). */
function firstLocationIdString(st: any): string {
  return st.location_ids && st.location_ids.length > 0
    ? String(st.location_ids[0])
    : '';
}

/** First selected task id (supports task_ids array, legacy tasks, or string). */
function firstTaskIdFromSessionType(st: any): string | null {
  const taskIds = st.task_ids || st.tasks || [];
  if (Array.isArray(taskIds) && taskIds.length > 0) return taskIds[0];
  return typeof taskIds === 'string' ? taskIds : null;
}

/**
 * Session type moderator ids to seed the edit form with (OD-1065): only ids
 * that still resolve to an active project moderator — the edit checkbox list
 * only renders rows for the current roster, so a stale id would otherwise sit
 * invisibly in state and get resubmitted on Save. Skips the filter while the
 * roster hasn't loaded yet (empty array) — filtering against an empty set
 * would wrongly drop every real id if Edit is opened before the moderators
 * fetch resolves.
 */
function resolveEditModeratorIds(sessionType: any, projectModerators: any[]): string[] {
  const activeModeratorIdsForEdit =
    projectModerators.length > 0
      ? new Set(projectModerators.map((m: any) => String(m.user_id || m.id)))
      : null;
  return (sessionType.moderator_ids || [])
    .map((id: any) => String(id))
    .filter(
      (id: string) => !activeModeratorIdsForEdit || activeModeratorIdsForEdit.has(id),
    );
}

/** Convert a minutes total to a form value+unit, preferring whole hours. */
function minutesToValueUnit(totalMinutes: number): {
  value: string;
  unit: 'hours' | 'minutes';
} {
  if (totalMinutes >= 60 && totalMinutes % 60 === 0) {
    return {value: (totalMinutes / 60).toString(), unit: 'hours'};
  }
  return {value: totalMinutes.toString(), unit: 'minutes'};
}

/** Parse a stored email_attachment_path (JSON string, array, or single path) to a string[]. */
function parseAttachmentPaths(raw: unknown): string[] {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [raw as string];
  }
}

/** Daily booking window: blank/blank means the whole day (00:00–24:00). */
function computeDailyBookingTimes(
  startRaw: string,
  endRaw: string,
): {start: string; end: string} {
  const ds = startRaw.trim();
  const de = endRaw.trim();
  if (!ds && !de) return {start: '00:00', end: '24:00'};
  return {start: ds, end: de};
}

/** Display label for a session type's start/end date range on the list card. */
function getSessionTypeDateRangeLabel(sessionType: any): string {
  const startDate = formatSessionTypeCalendarDate(
    sessionType.sessionTypeStartDate,
    'MMM dd, yyyy',
  );
  const endDate = formatSessionTypeCalendarDate(
    sessionType.sessionTypeEndDate,
    'MMM dd, yyyy',
  );
  if (startDate && endDate) return `${startDate} – ${endDate}`;
  if (startDate) return `From ${startDate}`;
  if (endDate) return `Until ${endDate}`;
  return 'No date range set';
}

function CalendarDateInput({
  name,
  value,
  onValueChange,
  invalidRef,
  min,
  minFromStartDate,
}: Readonly<{
  name: string;
  value: string;
  onValueChange: (value: string) => void;
  invalidRef: {current: boolean};
  min?: string;
  minFromStartDate?: string;
}>) {
  return (
    <DateInput
      name={name}
      value={value}
      min={min}
      minFromStartDate={minFromStartDate}
      onInput={(e) => {
        markCalendarDateValidity(e.currentTarget.validity.badInput, invalidRef);
      }}
      onChange={(e) => {
        commitCalendarDateChange(
          e.target.validity.badInput,
          e.target.value,
          invalidRef,
          onValueChange,
        );
      }}
    />
  );
}

/**
 * Moderators-count badge value for the list card (OD-1065): prefer the
 * server-resolved count, else show a loading placeholder while the roster is
 * still fetching, else fall back to the client-side roster intersection.
 * Mirrors the three-way branch that used to live inline in the list-card map.
 */
function getModeratorsCountDisplay(
  sessionType: any,
  activeModeratorIdsForCount: Set<string> | null,
  projectModeratorsLoading: boolean,
): number | string {
  if (typeof sessionType.active_moderator_count === 'number') {
    return sessionType.active_moderator_count;
  }
  if (projectModeratorsLoading) return '…';
  return new Set(
    (sessionType.moderator_ids || [])
      .map((id: any) => String(id))
      .filter(
        (id: string) =>
          !activeModeratorIdsForCount || activeModeratorIdsForCount.has(id),
      ),
  ).size;
}

/** "Disabled" badge + by-whom/when caption shown on a disabled session type's list card. */
function DisabledStatusBadge({sessionType}: Readonly<{sessionType: any}>) {
  return (
    <div className="flex items-center gap-2">
      <Badge
        variant="secondary"
        className="font-normal bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs"
      >
        Disabled
      </Badge>
      {sessionType.disabled_by_name && sessionType.disabled_at && (
        <span className="text-xs text-muted-foreground">
          By {sessionType.disabled_by_name}{' '}
          {format(parseISO(sessionType.disabled_at), 'MMM dd, yyyy')}
        </span>
      )}
    </div>
  );
}

/** Session type change-log (OD-892 "History" tab): timeline + a compact pager. */
const SessionTypeHistoryPanel: React.FC<{sessionTypeId: string}> = ({sessionTypeId}) => {
  const history = useSessionTypeHistoryQuery(sessionTypeId);

  return (
    <div className="space-y-3">
      <HistoryTimeline
        entries={history.rows}
        isLoading={history.isLoading}
        isError={history.isError}
        showFilter={false}
        emptyLabel="No history yet for this session type."
      />
      {history.pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-1">
          <Button
            variant="outline"
            size="sm"
            disabled={history.page <= 1}
            onClick={() => history.setPage(history.page - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {history.page} of {history.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={history.page >= history.pages}
            onClick={() => history.setPage(history.page + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

/** Marks a meeting point that exists only on this screen until the save. */
const PENDING_MEETING_POINT_PREFIX = 'pending-';
const NEW_SECONDARY_MEETING_POINT = 'new-secondary';
const NEW_PRIMARY_MEETING_POINT_ID = 'new-primary';

const SessionTypesTab: React.FC<SessionTypesTabProps> = ({
  selectedProject,
  projectIdFromUrl,
  locations,
  projectModerators,
  projectModeratorsLoading = false,
  projectsState,
  allowedSessionTypeNames,
}) => {
  const queryClient = useQueryClient();
  const {toast} = useToast();
  const {user} = useAuth();

  // Get project-specific role using RBAC hook
  const {role: projectRole, loading: roleLoading} = useProjectRole(
    projectIdFromUrl || undefined,
  );

  const {isProjectManager, isModerator} = getProjectPermissionFlags({
    projectRole,
    roleLoading,
    userRole: user?.role,
    userEmail: user?.email,
  });

  // Expanded session types state
  const [expandedSessionTypes, setExpandedSessionTypes] = useState<Set<string>>(
    new Set(),
  );

  // Session types state. Seeded from the shared React Query cache (see the sync
  // effect below) but kept as local state, not derived directly from the query,
  // because several mutation flows below optimistically patch it in place
  // (e.g. patchMeetingPointInSessionTypes) independent of a fresh fetch.
  const [sessionTypes, setSessionTypes] = useState<any[]>([]);
  const [loadingSessionTypes, setLoadingSessionTypes] = useState(true);

  const taskAndJobProjectId =
    selectedProject || projectsState.currentProject?.id || projectIdFromUrl;

  // Available tasks / jobs - plain React Query reads (no local mutation of
  // these elsewhere in the file), so cached data survives this tab unmounting
  // and remounting on tab switch instead of always refetching.
  const {data: availableTasks = []} = useTasksByProjectQuery(taskAndJobProjectId);
  const {data: availableJobs = [] as OneFormaJob[], isLoading: loadingJobs} =
    useJobsByProjectQuery(taskAndJobProjectId);

  // Add Session Type Inline state
  const [isCreatingNewSessionType, setIsCreatingNewSessionType] =
    useState(false);
  const [newSessionTypeName, setNewSessionTypeName] = useState('');
  const [newSessionTypeJobId, setNewSessionTypeJobId] = useState('');
  const [newSessionTypeDescription, setNewSessionTypeDescription] =
    useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [newSessionTypeDuration, setNewSessionTypeDuration] = useState('');
  const [newSessionTypeDurationUnit, setNewSessionTypeDurationUnit] =
    useState('minutes');
  const [newSessionTypeStartDate, setNewSessionTypeStartDate] = useState('');
  const newStartDateInvalidRef = useRef(false);
  const newEndDateInvalidRef = useRef(false);
  const [newSessionTypeEndDate, setNewSessionTypeEndDate] = useState('');
  const [newSessionTypeDailyStartTime, setNewSessionTypeDailyStartTime] =
    useState('');
  const [newSessionTypeDailyEndTime, setNewSessionTypeDailyEndTime] =
    useState('');
  const [newSessionTypeMinimumNotice, setNewSessionTypeMinimumNotice] =
    useState('');
  const [newSessionTypeMinimumNoticeUnit, setNewSessionTypeMinimumNoticeUnit] =
    useState('minutes');
  const [newSessionTypeBufferBefore, setNewSessionTypeBufferBefore] =
    useState('');
  const [newSessionTypeBufferBeforeUnit, setNewSessionTypeBufferBeforeUnit] =
    useState('minutes');
  const [newSessionTypeBufferAfter, setNewSessionTypeBufferAfter] =
    useState('');
  const [newSessionTypeBufferAfterUnit, setNewSessionTypeBufferAfterUnit] =
    useState('minutes');
  // Per-slot capacity: "Participants per Slot" (N). Empty = no per-slot limit.
  const [
    newSessionTypeParticipantsPerSlot,
    setNewSessionTypeParticipantsPerSlot,
  ] = useState('');
  const [newSessionTypeTimezone, setNewSessionTypeTimezone] = useState('');
  const [
    newSessionTypeParticipantBookingRules,
    setNewSessionTypeParticipantBookingRules,
  ] = useState(false);
  // OD-1097: allow a participant to hold more than one session of this type at
  // once. Max is mandatory (>= 2) only while the toggle is on.
  const [
    newSessionTypeAllowMultiBooking,
    setNewSessionTypeAllowMultiBooking,
  ] = useState(false);
  const [
    newSessionTypeMaxSessionsPerParticipant,
    setNewSessionTypeMaxSessionsPerParticipant,
  ] = useState('');
  // OD-1102: no-show/cancellation limit. Unlike the toggle above, the limit is
  // MANDATORY once enabled — there's no sensible "unlimited" interpretation
  // for an alert threshold (it would mean the alert never fires). The toggle
  // itself is FE-only; the backend column is just nullable (null = disabled).
  // Group booking size cap. Unchecked means UNLIMITED for this session type
  // (not "fall back to a default"), so the max is mandatory once ticked.
  const [
    newSessionTypeCapGroupBookingSize,
    setNewSessionTypeCapGroupBookingSize,
  ] = useState(false);
  const [newSessionTypeMaxGroupSize, setNewSessionTypeMaxGroupSize] =
    useState('');
  const [
    newSessionTypeEnableNoShowLimit,
    setNewSessionTypeEnableNoShowLimit,
  ] = useState(false);
  const [
    newSessionTypeNoShowCancellationLimit,
    setNewSessionTypeNoShowCancellationLimit,
  ] = useState('');
  // OD-1111: allow one moderator to run more than one session of this type in
  // the same slot at once. Unlike the multi-booking max, the max here is
  // MANDATORY (>= 2) once the toggle is on — there's no "unlimited" reading.
  const [
    newSessionTypeAllowConcurrentModeratorBookings,
    setNewSessionTypeAllowConcurrentModeratorBookings,
  ] = useState(false);
  const [
    newSessionTypeMaxConcurrentModeratorSessions,
    setNewSessionTypeMaxConcurrentModeratorSessions,
  ] = useState('');
  const [newSessionTypeWindowCap, setNewSessionTypeWindowCap] =
    useState<WindowCapFormState>(emptyWindowCapForm());
  const [newSessionTypeLocationType, setNewSessionTypeLocationType] = useState<
    'onsite' | 'offsite'
  >('onsite');
  const [
    newSessionTypeSchedulingRules,
    setNewSessionTypeSchedulingRules,
  ] = useState<SchedulingRules>(emptySchedulingRules());
  // OD-1002: slot generation mode for the create form (defaults to Session Rules).
  const [newSessionTypeSlotMode, setNewSessionTypeSlotMode] =
    useState<SlotGenerationMode>(DEFAULT_SLOT_GENERATION_MODE);
  const [selectedModeratorsForSession, setSelectedModeratorsForSession] =
    useState<string[]>([]);
  const [selectedTasksForSession, setSelectedTasksForSession] = useState<
    string | null
  >(null); // Store single task ID
  const [moderatorSearchCreate, setModeratorSearchCreate] = useState('');
  
  // Meeting Point form fields
  const [showAddMeetingPointDialog, setShowAddMeetingPointDialog] =
    useState(false);
  const [isEditingMeetingPoint, setIsEditingMeetingPoint] = useState(false);
  const [meetingPointName, setMeetingPointName] = useState('');
  const [meetingPointStreetAddress, setMeetingPointStreetAddress] =
    useState('');
  const [meetingPointCity, setMeetingPointCity] = useState('');
  const [meetingPointState, setMeetingPointState] = useState('');
  const [meetingPointZipCode, setMeetingPointZipCode] = useState('');
  const [meetingPointCountry, setMeetingPointCountry] = useState('');
  const [meetingPointInstructions, setMeetingPointInstructions] = useState('');
  const [meetingPointNameDraft, setMeetingPointNameDraft] = useState('');
  const [meetingPointStreetAddressDraft, setMeetingPointStreetAddressDraft] =
    useState('');
  const [meetingPointCityDraft, setMeetingPointCityDraft] = useState('');
  const [meetingPointStateDraft, setMeetingPointStateDraft] = useState('');
  const [meetingPointZipCodeDraft, setMeetingPointZipCodeDraft] = useState('');
  const [meetingPointCountryDraft, setMeetingPointCountryDraft] = useState('');
  const [meetingPointInstructionsDraft, setMeetingPointInstructionsDraft] =
    useState('');
  // Secondary meeting points drafted on the create form (OD-1325). The primary
  // stays in the meetingPoint* fields above, which every create-path check
  // reads; these are created alongside it when the session type is saved.
  const [newSecondaryMeetingPoints, setNewSecondaryMeetingPoints] = useState<
    any[]
  >([]);
  // Which secondary the add/edit dialog is working on while creating: null when
  // the dialog is for the primary, NEW_SECONDARY_MEETING_POINT when adding one.
  const [newSecondaryMeetingPointTarget, setNewSecondaryMeetingPointTarget] =
    useState<string | null>(null);

  // Inline Edit Session Type state
  const [editingSessionTypeId, setEditingSessionTypeId] = useState<
    string | null
  >(null);
  const [editSessionTypeName, setEditSessionTypeName] = useState('');
  const [editSessionTypeJobId, setEditSessionTypeJobId] = useState('');
  const [editSessionTypeDescription, setEditSessionTypeDescription] =
    useState('');
  const [editSessionTypeDuration, setEditSessionTypeDuration] = useState('');
  const [editSessionTypeDurationUnit, setEditSessionTypeDurationUnit] =
    useState('minutes');
  const [editSessionTypeStartDate, setEditSessionTypeStartDate] = useState('');
  const editStartDateInvalidRef = useRef(false);
  const editEndDateInvalidRef = useRef(false);
  const [editSessionTypeEndDate, setEditSessionTypeEndDate] = useState('');
  const [
    editSessionTypeSchedulingRules,
    setEditSessionTypeSchedulingRules,
  ] = useState<SchedulingRules>(emptySchedulingRules());
  // OD-1002: slot generation mode for the edit form (seeded from the session type).
  const [editSessionTypeSlotMode, setEditSessionTypeSlotMode] =
    useState<SlotGenerationMode>(DEFAULT_SLOT_GENERATION_MODE);
  const [editSessionTypeDailyStartTime, setEditSessionTypeDailyStartTime] =
    useState('');
  const [editSessionTypeDailyEndTime, setEditSessionTypeDailyEndTime] =
    useState('');
  const [editSessionTypeMinimumNotice, setEditSessionTypeMinimumNotice] =
    useState('');
  const [
    editSessionTypeMinimumNoticeUnit,
    setEditSessionTypeMinimumNoticeUnit,
  ] = useState('minutes');
  const [editSessionTypeBufferBefore, setEditSessionTypeBufferBefore] =
    useState('');
  const [editSessionTypeBufferBeforeUnit, setEditSessionTypeBufferBeforeUnit] =
    useState('minutes');
  const [editSessionTypeBufferAfter, setEditSessionTypeBufferAfter] =
    useState('');
  const [editSessionTypeBufferAfterUnit, setEditSessionTypeBufferAfterUnit] =
    useState('minutes');
  // Per-slot capacity: "Participants per Slot" (N). Empty = no per-slot limit.
  const [
    editSessionTypeParticipantsPerSlot,
    setEditSessionTypeParticipantsPerSlot,
  ] = useState('');
  const [editSessionTypeTimezone, setEditSessionTypeTimezone] = useState('');
  const [
    editSessionTypeParticipantBookingRules,
    setEditSessionTypeParticipantBookingRules,
  ] = useState(false);
  // OD-1097: allow a participant to hold more than one session of this type at
  // once. Max is mandatory (>= 2) only while the toggle is on.
  const [
    editSessionTypeAllowMultiBooking,
    setEditSessionTypeAllowMultiBooking,
  ] = useState(false);
  const [
    editSessionTypeMaxSessionsPerParticipant,
    setEditSessionTypeMaxSessionsPerParticipant,
  ] = useState('');
  // OD-1102: see create-form twin above for why this one is mandatory once enabled.
  // Group booking size cap — see create-form twin above.
  const [
    editSessionTypeCapGroupBookingSize,
    setEditSessionTypeCapGroupBookingSize,
  ] = useState(false);
  const [editSessionTypeMaxGroupSize, setEditSessionTypeMaxGroupSize] =
    useState('');
  const [
    editSessionTypeEnableNoShowLimit,
    setEditSessionTypeEnableNoShowLimit,
  ] = useState(false);
  const [
    editSessionTypeNoShowCancellationLimit,
    setEditSessionTypeNoShowCancellationLimit,
  ] = useState('');
  // OD-1111: see create-form twin above for why this max is mandatory once enabled.
  const [
    editSessionTypeAllowConcurrentModeratorBookings,
    setEditSessionTypeAllowConcurrentModeratorBookings,
  ] = useState(false);
  const [
    editSessionTypeMaxConcurrentModeratorSessions,
    setEditSessionTypeMaxConcurrentModeratorSessions,
  ] = useState('');
  const [editSessionTypeWindowCap, setEditSessionTypeWindowCap] =
    useState<WindowCapFormState>(emptyWindowCapForm());
  const [editSessionTypeLocationType, setEditSessionTypeLocationType] =
    useState<'onsite' | 'offsite'>('onsite');
  const [editSelectedLocationForSession, setEditSelectedLocationForSession] =
    useState<string>('');
  const [
    editSelectedModeratorsForSession,
    setEditSelectedModeratorsForSession,
  ] = useState<string[]>([]);
  const [editSelectedTasksForSession, setEditSelectedTasksForSession] =
    useState<string | null>(null);
  const [editAttachedFiles, setEditAttachedFiles] = useState<File[]>([]);
  const [editExistingAttachmentPaths, setEditExistingAttachmentPaths] =
    useState<string[]>([]);
  const [moderatorSearchEdit, setModeratorSearchEdit] = useState<
    Record<string, string>
  >({});

  // Edit Meeting Point Dialog state
  const [showEditMeetingPointDialog, setShowEditMeetingPointDialog] =
    useState(false);
  const [editingMeetingPoint, setEditingMeetingPoint] = useState<any>(null);
  const [newlyCreatedLocationId, setNewlyCreatedLocationId] = useState<
    number | null
  >(null);

  // Confirmation dialog state for session type updates
  const [showSessionTypeUpdateConfirm, setShowSessionTypeUpdateConfirm] =
    useState(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState<any>(null);
  const [isUpdatingSessionType, setIsUpdatingSessionType] = useState(false);
  const [isCreatingSessionType, setIsCreatingSessionType] = useState(false);
  const [editMeetingPointName, setEditMeetingPointName] = useState('');
  const [editMeetingPointStreetAddress, setEditMeetingPointStreetAddress] =
    useState('');
  const [editMeetingPointCity, setEditMeetingPointCity] = useState('');
  const [editMeetingPointState, setEditMeetingPointState] = useState('');
  const [editMeetingPointZipCode, setEditMeetingPointZipCode] = useState('');
  const [editMeetingPointCountry, setEditMeetingPointCountry] = useState('');
  const [editMeetingPointInstructions, setEditMeetingPointInstructions] =
    useState('');
  const [isSavingMeetingPoint, setIsSavingMeetingPoint] = useState(false);
  /** When true, closing the edit dialog must not revert fields to stale location data. */
  const meetingPointDialogCommittedRef = useRef(false);
  const sessionTimezoneUserOverrideRef = useRef(false);

  useEffect(() => {
    if (loadingJobs || newSessionTypeJobId) {
      return;
    }

    const defaultJob = availableJobs.find((job) => String(job.job_id) === '0');
    if (defaultJob) {
      setNewSessionTypeJobId(String(defaultJob.job_id));
    }
  }, [availableJobs, loadingJobs, newSessionTypeJobId]);

  // Session types list is cached via useDetailedSessionTypesByProjectQuery
  // (same query key the mutation handlers below already invalidate), so
  // switching tabs away and back within staleTime shows cached data instantly
  // instead of refetching. Synced into local `sessionTypes` state (rather than
  // read directly from the query) because several handlers below patch
  // `sessionTypes` in place for optimistic UI updates.
  const {data: rawSessionTypes, isLoading: sessionTypesQueryLoading} =
    useDetailedSessionTypesByProjectQuery(selectedProject);
  useEffect(() => {
    if (!selectedProject) {
      setSessionTypes([]);
      setLoadingSessionTypes(false);
      return;
    }
    if (!sessionTypesQueryLoading) {
      setSessionTypes((rawSessionTypes || []).map(formatSessionTypeForDisplay));
      setLoadingSessionTypes(false);
    }
  }, [selectedProject, rawSessionTypes, sessionTypesQueryLoading]);

  const getJobOptions = (
    currentJobId?: string | null,
    currentJobName?: string | null,
  ) => {
    const normalizedCurrentJobId =
      currentJobId !== undefined && currentJobId !== null && currentJobId !== ''
        ? String(currentJobId)
        : '';
    if (
      normalizedCurrentJobId &&
      !availableJobs.some((job) => job.job_id === normalizedCurrentJobId)
    ) {
      return [
        {
          job_id: normalizedCurrentJobId,
          job_name: currentJobName || '',
        },
        ...availableJobs,
      ];
    }
    return availableJobs;
  };

  const getAssignableJobOptions = (
    currentJobId?: string | null,
    currentJobName?: string | null,
  ) => {
    const normalizedCurrentJobId =
      currentJobId !== undefined && currentJobId !== null && currentJobId !== ''
        ? String(currentJobId)
        : '';

    const assignedJobIds = new Set(
      sessionTypes
        .map((sessionType) => sessionType.job_id)
        .filter(
          (jobId) =>
            jobId !== undefined &&
            jobId !== null &&
            jobId !== '' &&
            String(jobId) !== '0' &&
            String(jobId) !== normalizedCurrentJobId,
        )
        .map((jobId) => String(jobId)),
    );

    return getJobOptions(currentJobId, currentJobName).filter(
      (job) =>
        job.job_id === '0' ||
        job.job_id === normalizedCurrentJobId ||
        !assignedJobIds.has(String(job.job_id)),
    );
  };

  const getJobDisplayName = (
    jobId?: string | null,
    fallbackJobName?: string | null,
  ) => {
    if (jobId === undefined || jobId === null || jobId === '') return '';
    const normalizedJobId = String(jobId);
    const matchingJob = getJobOptions(jobId, fallbackJobName).find(
      (job) => job.job_id === normalizedJobId,
    );
    const jobName = matchingJob?.job_name || fallbackJobName || normalizedJobId;
    return `${jobName} - ${normalizedJobId}`;
  };

  // Filter moderators for create mode - show all project moderators (no timezone/location filtering)
  const getFilteredModeratorsForCreate = (): any[] => {
    // Return all project moderators, optionally filtered by search
    let filtered = projectModerators;

    // Apply search filter if provided
    const searchTerm = moderatorSearchCreate.trim();
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((moderator: any) => {
        const name = (moderator.name || '').toLowerCase();
        const firstName = (moderator.first_name || '').toLowerCase();
        const lastName = (moderator.last_name || '').toLowerCase();
        return (
          name.includes(searchLower) ||
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          `${firstName} ${lastName}`.trim().includes(searchLower)
        );
      });
    }

    return filtered;
  };

  // Filter moderators for edit mode - show all project moderators (no timezone/location filtering)
  const getFilteredModeratorsForEdit = (sessionTypeId?: string): any[] => {
    // Return all project moderators, optionally filtered by search
    let filtered = projectModerators;

    // Apply search filter if provided for this session type
    if (sessionTypeId && moderatorSearchEdit[sessionTypeId]) {
      const searchTerm = moderatorSearchEdit[sessionTypeId].trim();
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter((moderator: any) => {
          const name = (moderator.name || '').toLowerCase();
          const firstName = (moderator.first_name || '').toLowerCase();
          const lastName = (moderator.last_name || '').toLowerCase();
          return (
            name.includes(searchLower) ||
            firstName.includes(searchLower) ||
            lastName.includes(searchLower) ||
            `${firstName} ${lastName}`.trim().includes(searchLower)
      );
    });
      }
    }

    return filtered;
  };

  // Filter moderators for read-only mode - show all project moderators with selected ones first
  const getFilteredModeratorsForReadOnly = (
    startDate: string | null,
    endDate: string | null,
    selectedModeratorIds: string[] = [],
  ): any[] => {
    // Return all project moderators with selected ones at the top
    const selected = projectModerators.filter((mod: any) => {
      const moderatorUserId = String(mod.user_id || mod.id || '');
      return selectedModeratorIds.includes(moderatorUserId);
    });

    const unselected = projectModerators.filter((mod: any) => {
      const moderatorUserId = String(mod.user_id || mod.id || '');
      return !selectedModeratorIds.includes(moderatorUserId);
    });

    return [...selected, ...unselected];
  };

  // Shared validation function for session type fields
  const validateSessionTypeFields = (
    fields: SessionTypeValidationFields,
  ): {isValid: boolean; error?: string} => {
    const error =
      validateBasicSessionFields(fields) ??
      validateTimingFields(fields) ??
      validateMeetingPointFields(fields) ??
      validateModeratorsAndTasks(fields);
    return error ? {isValid: false, error} : {isValid: true};
  };

  // Validate moderator timezone conflicts
  const validateModeratorTimezoneConflicts = (
    moderatorIds: string[],
    timezone: string,
    excludeSessionTypeId: string | null = null,
  ): {isValid: boolean; error?: string} => {
    if (!moderatorIds || moderatorIds.length === 0 || !timezone) {
      return {isValid: true}; // No moderators or no timezone - no validation needed
    }

    // Get all session types in the current project (excluding the one being edited)
    // IMPORTANT: Only check conflicts with ACTIVE (enabled) session types
    // Disabled session types don't block moderator assignment
    const projectId =
      selectedProject || projectsState.currentProject?.id || projectIdFromUrl;

    const otherSessionTypes = sessionTypes.filter(
      (st: any) => st.id !== excludeSessionTypeId && st.enabled === true
    );

    // Build a map of moderator_id -> existing timezones and emails
    const moderatorTimezoneMap = new Map<string, Set<string>>();
    const moderatorEmailMap = new Map<string, string>();

    // Process all other ACTIVE session types to find moderator assignments
    otherSessionTypes.forEach((sessionType: any) => {
      const stModeratorIds = sessionType.moderator_ids || [];
      const stTimezone = sessionType.timezone;

      if (stModeratorIds.length > 0 && stTimezone) {
        stModeratorIds.forEach((modId: string) => {
          if (!moderatorTimezoneMap.has(modId)) {
            moderatorTimezoneMap.set(modId, new Set());
          }
          moderatorTimezoneMap.get(modId)!.add(stTimezone);

          // Get moderator email for error messages
          if (!moderatorEmailMap.has(modId)) {
            const moderator = findModeratorById(projectModerators, modId);
            if (moderator) {
              moderatorEmailMap.set(
                modId,
                moderator.email_id || moderator.email || modId,
              );
            }
          }
        });
      }
    });

    // Check for conflicts: moderators being assigned to a different timezone
    const conflicts: Array<{
      moderatorId: string;
      moderatorEmail: string;
      existingTimezones: string[];
    }> = [];

    moderatorIds.forEach((moderatorId) => {
      const existingTimezones = moderatorTimezoneMap.get(moderatorId);

      if (existingTimezones && existingTimezones.size > 0) {
        // Check if the new timezone is different from all existing timezones
        const hasConflict = !existingTimezones.has(timezone);

        if (hasConflict) {
          // Get all existing timezones for the error message
          const existingTimezonesList = Array.from(existingTimezones);
          const moderatorEmail =
            moderatorEmailMap.get(moderatorId) || moderatorId; // Fallback to ID if email not found
          conflicts.push({
            moderatorId,
            moderatorEmail,
            existingTimezones: existingTimezonesList,
          });
        }
      }
    });

    if (conflicts.length === 0) {
      return {isValid: true};
    }

    // Build error message
    const errorMessages = conflicts.map((conflict) => {
      const timezonesStr = conflict.existingTimezones
        .map((tz) => `"${tz}"`)
        .join(' and ');
      return `${conflict.moderatorEmail} is already assigned to ${timezonesStr} for another session type`;
    });

    return {
      isValid: false,
      error: errorMessages.join('. '),
    };
  };

  // Booking-limit numeric fields (max sessions per participant, no-show
  // limit, max concurrent moderator sessions) for the Add form. Extracted out
  // of getAddSessionTypeValidationError to keep that function's cognitive
  // complexity low; behavior (incl. order of checks) is unchanged.
  const getAddSessionTypeBookingLimitsValidationError = ():
    | string
    | undefined => {
    const allowMultipleBookings = resolveAllowMultipleBookings(
      newSessionTypeWindowCap.enabled,
      newSessionTypeAllowMultiBooking,
    );
    if (
      allowMultipleBookings &&
      (newSessionTypeMaxSessionsPerParticipant.trim() === '' ||
        !Number.isInteger(Number(newSessionTypeMaxSessionsPerParticipant)) ||
        Number(newSessionTypeMaxSessionsPerParticipant) < 2)
    ) {
      // Required once enabled (OD-1171) — "more than one at once" with no cap isn't meaningful.
      return 'Max sessions per participant must be a whole number of 2 or greater';
    }
    if (
      newSessionTypeCapGroupBookingSize &&
      (newSessionTypeMaxGroupSize.trim() === '' ||
        !Number.isInteger(Number(newSessionTypeMaxGroupSize)) ||
        Number(newSessionTypeMaxGroupSize) < 2)
    ) {
      return 'Maximum group size is required and must be a whole number of 2 or greater';
    }
    if (
      newSessionTypeEnableNoShowLimit &&
      (newSessionTypeNoShowCancellationLimit.trim() === '' ||
        !Number.isInteger(Number(newSessionTypeNoShowCancellationLimit)) ||
        Number(newSessionTypeNoShowCancellationLimit) < 1)
    ) {
      // Required once enabled — an alert threshold with no limit would never fire.
      return 'No-show / cancellation limit must be a whole number of 1 or greater';
    }
    if (
      newSessionTypeAllowConcurrentModeratorBookings &&
      (newSessionTypeMaxConcurrentModeratorSessions.trim() === '' ||
        !Number.isInteger(Number(newSessionTypeMaxConcurrentModeratorSessions)) ||
        Number(newSessionTypeMaxConcurrentModeratorSessions) < 2)
    ) {
      // Required once enabled — "more than one at once" with no cap isn't meaningful.
      return 'Maximum concurrent sessions must be a whole number of 2 or greater';
    }
    return validateWindowCapForm(newSessionTypeWindowCap);
  };

  // Moderator/timezone conflict check for the Add form. Extracted out of
  // getAddSessionTypeValidationError for the same reason as above.
  const getAddSessionTypeModeratorTimezoneValidationError = ():
    | string
    | undefined => {
    if (selectedModeratorsForSession.length === 0 || !newSessionTypeTimezone) {
      return undefined;
    }
    const tz = validateModeratorTimezoneConflicts(
      selectedModeratorsForSession,
      newSessionTypeTimezone,
    );
    return tz.isValid ? undefined : tz.error;
  };

  // Handle add session type
  const getAddSessionTypeValidationError = (): string | undefined => {
    const typedDateError = invalidTypedDateMessage(
      newStartDateInvalidRef.current,
      newEndDateInvalidRef.current,
    );
    if (typedDateError) return typedDateError;
    const validation = validateSessionTypeFields({
      name: newSessionTypeName,
      jobId: newSessionTypeJobId,
      startDate: newSessionTypeStartDate,
      endDate: newSessionTypeEndDate,
      minimumNotice: newSessionTypeMinimumNotice,
      duration: newSessionTypeDuration,
      bufferBefore: newSessionTypeBufferBefore,
      bufferAfter: newSessionTypeBufferAfter,
      selectedTasks: selectedTasksForSession ?? '',
      availableTasksCount: availableTasks.length,
      selectedModerators: selectedModeratorsForSession,
      meetingPointName: meetingPointName,
      meetingPointStreetAddress: meetingPointStreetAddress,
      meetingPointCountry: meetingPointCountry,
      meetingPointState: meetingPointState,
      meetingPointCity: meetingPointCity,
      sessionType: newSessionTypeLocationType,
    });
    if (!validation.isValid) return validation.error;
    if (isBookingOptionalModeratorEnabled()) {
      const dailyOrder = validateDailyTimeRangeOrder(
        newSessionTypeStartDate,
        newSessionTypeEndDate,
        newSessionTypeDailyStartTime,
        newSessionTypeDailyEndTime,
      );
      if (!dailyOrder.ok) return dailyOrder.message;
    }
    const bookingLimitsError = getAddSessionTypeBookingLimitsValidationError();
    if (bookingLimitsError) return bookingLimitsError;
    const timezoneError = getAddSessionTypeModeratorTimezoneValidationError();
    if (timezoneError) return timezoneError;
    if (
      isProjectManager &&
      newSessionTypeSlotMode === 'moderator_availability' &&
      selectedModeratorsForSession.length === 0
    ) {
      return 'Assign at least one moderator to use Moderator Availability mode';
    }
    return undefined;
  };

  const createMeetingPointLocationForCreate = async (
    projectId: string,
  ): Promise<{id: string | null; error?: string}> => {
    const shouldCreate =
      newSessionTypeLocationType === 'onsite' ||
      (meetingPointName.trim() && meetingPointStreetAddress.trim());
    if (!shouldCreate) return {id: null};
    const addressParts = [
      meetingPointStreetAddress.trim(),
      meetingPointCity.trim(),
      meetingPointState.trim(),
      meetingPointZipCode.trim(),
      meetingPointCountry.trim(),
    ].filter(Boolean);
    const fullAddress = addressParts.join(', ');
    const locationData: any = {
      project_id: projectId,
      name: meetingPointName.trim(),
      address: fullAddress || meetingPointStreetAddress.trim(),
      street_address: meetingPointStreetAddress.trim(),
      city: meetingPointCity.trim(),
      state: meetingPointState.trim(),
      zip_code: meetingPointZipCode.trim(),
      country: meetingPointCountry.trim(),
      floor: null,
      capacity: null,
      is_active: true,
    };
    if (meetingPointInstructions.trim()) {
      locationData.instructions = meetingPointInstructions.trim();
    }
    // OD-1325 AC-4: store the zone this venue sits in, resolved from its own
    // address rather than inherited from the session type. With several venues
    // per session type the two are no longer the same question, and the
    // cross-timezone guard (AC-5) compares these stored values.
    locationData.timezone =
      (await getTimezoneForLocationNames(
        meetingPointCountry.trim(),
        meetingPointState.trim(),
        meetingPointCity.trim() || undefined,
      )) || null;
    const locationResponse =
      await schedulerApi.createScheduleLocation(locationData);
    const id = locationResponse.data?.data?.id ?? null;
    if (!id) return {id: null, error: 'Failed to create meeting point'};
    return {id};
  };

  const toMinutes = (value: string, unit: string): number => {
    const n = value?.trim() ? Number.parseInt(value) : 0;
    return unit === 'hours' ? n * 60 : n;
  };

  // Optional/derived create-payload fields that are only set when present or
  // applicable. Extracted out of buildCreateSessionTypePayload to keep that
  // function's cognitive complexity low; behavior (incl. field names/order
  // of the conditions) is unchanged.
  const buildOptionalCreateSessionTypeFields = (): Record<string, unknown> => {
    const extra: Record<string, unknown> = {};
    const allowMultipleBookings = resolveAllowMultipleBookings(
      newSessionTypeWindowCap.enabled,
      newSessionTypeAllowMultiBooking,
    );
    if (newSessionTypeStartDate) extra.sessionTypeStartDate = newSessionTypeStartDate;
    if (newSessionTypeEndDate) extra.sessionTypeEndDate = newSessionTypeEndDate;
    if (isBookingOptionalModeratorEnabled()) {
      const daily = computeDailyBookingTimes(
        newSessionTypeDailyStartTime,
        newSessionTypeDailyEndTime,
      );
      extra.bookingDailyStartTime = daily.start;
      extra.bookingDailyEndTime = daily.end;
    }
    if (newSessionTypeTimezone) extra.timezone = newSessionTypeTimezone;
    if (newSessionTypeParticipantBookingRules) {
      extra.participant_booking_rules = newSessionTypeParticipantBookingRules;
    }
    // OD-1097: only send the max when the toggle is on; the backend rejects a
    // stray max sent without its toggle, and defaults (both off) are implicit.
    extra.allow_multiple_bookings_per_participant = allowMultipleBookings;
    if (allowMultipleBookings) {
      // Blank means unlimited sessions for that participant.
      extra.max_sessions_per_participant =
        newSessionTypeMaxSessionsPerParticipant.trim() === ''
          ? null
          : parseInt(newSessionTypeMaxSessionsPerParticipant, 10);
    }
    // OD-1102: null disables the alert; a value is only ever sent (and only
    // ever required) while the toggle is on — validated above.
    // Unchecked => unlimited: the max is nulled so nothing stale lingers.
    extra.cap_group_booking_size = newSessionTypeCapGroupBookingSize;
    extra.max_group_size = newSessionTypeCapGroupBookingSize
      ? parseInt(newSessionTypeMaxGroupSize, 10)
      : null;
    extra.no_show_cancellation_limit = newSessionTypeEnableNoShowLimit
      ? parseInt(newSessionTypeNoShowCancellationLimit, 10)
      : null;
    // OD-1111: mirrors allow_multiple_bookings_per_participant above — max is
    // only ever sent (and only ever required) while the toggle is on.
    extra.allow_concurrent_moderator_bookings = newSessionTypeAllowConcurrentModeratorBookings;
    if (newSessionTypeAllowConcurrentModeratorBookings) {
      extra.max_concurrent_moderator_sessions = parseInt(
        newSessionTypeMaxConcurrentModeratorSessions,
        10,
      );
    }
    Object.assign(extra, windowCapFormToPayload(newSessionTypeWindowCap));
    if (selectedTasksForSession) extra.task_ids = [selectedTasksForSession];
    // Only PM/Admin may configure scheduling rules; other roles omit the field
    // so the backend never rejects their create. Serialized as a JSON string for
    // FormData; '' means no rules.
    if (isProjectManager) {
      const newRulesPayload = rulesToPayload(newSessionTypeSchedulingRules);
      extra.scheduling_rules = newRulesPayload ? JSON.stringify(newRulesPayload) : '';
      // OD-1002: PM/Admin only, same gate as scheduling_rules.
      extra.slot_generation_mode = newSessionTypeSlotMode;
    }
    return extra;
  };

  const buildCreateSessionTypePayload = (
    projectId: string,
    createdLocationId: string | null,
    secondaryLocationIds: string[] = [],
  ): any => {
    const durationValue = parseInt(newSessionTypeDuration);
    const durationMinutes =
      newSessionTypeDurationUnit === 'hours' ? durationValue * 60 : durationValue;
    const payload: any = {
      project_id: projectId,
      name: newSessionTypeName.trim(),
      job_id: newSessionTypeJobId,
      description: newSessionTypeDescription.trim(),
      duration_minutes: durationMinutes,
      location_ids: createdLocationId ? [createdLocationId] : [],
      moderator_ids:
        selectedModeratorsForSession.length > 0
          ? selectedModeratorsForSession
          : [],
      is_active: true,
      enable_walkin: false,
      minimum_notice: toMinutes(
        newSessionTypeMinimumNotice,
        newSessionTypeMinimumNoticeUnit,
      ),
      buffer_before: toMinutes(
        newSessionTypeBufferBefore,
        newSessionTypeBufferBeforeUnit,
      ),
      buffer_after: toMinutes(
        newSessionTypeBufferAfter,
        newSessionTypeBufferAfterUnit,
      ),
      session_type: newSessionTypeLocationType,
      participants_per_slot:
        newSessionTypeParticipantsPerSlot.trim() === ''
          ? null
          : parseInt(newSessionTypeParticipantsPerSlot, 10),
    };
    Object.assign(payload, buildOptionalCreateSessionTypeFields());
    // With secondaries the ordered shape is sent so the server knows which
    // venue is the primary; a single meeting point keeps the plain id list.
    if (createdLocationId && secondaryLocationIds.length > 0) {
      payload.meeting_points = [createdLocationId, ...secondaryLocationIds].map(
        (id, index) => ({
          location_id: Number(id),
          is_primary: index === 0,
          is_active: true,
          display_order: index,
        }),
      );
      payload.location_ids = payload.meeting_points.map(
        (p: any) => p.location_id,
      );
    }
    return payload;
  };

  const resetAddSessionTypeForm = () => {
    newStartDateInvalidRef.current = false;
    newEndDateInvalidRef.current = false;
    setNewSessionTypeName('');
    setNewSessionTypeJobId('');
    setNewSessionTypeDescription('');
    setAttachedFiles([]);
    setNewSessionTypeDuration('');
    setNewSessionTypeDurationUnit('minutes');
    setNewSessionTypeStartDate('');
    setNewSessionTypeEndDate('');
    setNewSessionTypeDailyStartTime('');
    setNewSessionTypeDailyEndTime('');
    setNewSessionTypeMinimumNotice('');
    setNewSessionTypeMinimumNoticeUnit('minutes');
    setNewSessionTypeBufferBefore('');
    setNewSessionTypeBufferBeforeUnit('minutes');
    setNewSessionTypeBufferAfter('');
    setNewSessionTypeBufferAfterUnit('minutes');
    setNewSessionTypeParticipantsPerSlot('');
    setNewSessionTypeSchedulingRules(emptySchedulingRules());
    setNewSessionTypeSlotMode(DEFAULT_SLOT_GENERATION_MODE);
    setNewSessionTypeTimezone('');
    setNewSessionTypeParticipantBookingRules(false);
    setNewSessionTypeAllowMultiBooking(false);
    setNewSessionTypeMaxSessionsPerParticipant('');
    setNewSessionTypeEnableNoShowLimit(false);
    setNewSessionTypeNoShowCancellationLimit('');
    setNewSessionTypeAllowConcurrentModeratorBookings(false);
    setNewSessionTypeMaxConcurrentModeratorSessions('');
    setNewSessionTypeWindowCap(emptyWindowCapForm());
    setNewSessionTypeLocationType('onsite');
    setSelectedModeratorsForSession([]);
    setSelectedTasksForSession(null);
    clearMeetingPointFields();
    setShowAddMeetingPointDialog(false);
    setIsEditingMeetingPoint(false);
    setIsCreatingNewSessionType(false);
    clearMeetingPointFields();
  };

  const handleAddSessionType = async () => {
    const validationError = getAddSessionTypeValidationError();
    if (validationError) {
      toast({title: 'Error', description: validationError, variant: 'destructive'});
      return;
    }
    setIsCreatingSessionType(true);
    try {
      const projectId =
        selectedProject || projectsState.currentProject?.id || projectIdFromUrl;
      if (!projectId || projectId === 'all') {
        setIsCreatingSessionType(false);
        toast({
          title: 'Error',
          description: 'Select a project before adding a session type',
          variant: 'destructive',
        });
        return;
      }
      // Checked before anything is written: a refusal after the locations exist
      // would leave them behind, still holding their names.
      const secondariesError = await getNewSecondaryMeetingPointsError();
      if (secondariesError) {
        setIsCreatingSessionType(false);
        toast({title: 'Error', description: secondariesError, variant: 'destructive'});
        return;
      }
      const location = await createMeetingPointLocationForCreate(projectId);
      if (location.error) {
        setIsCreatingSessionType(false);
        toast({title: 'Error', description: location.error, variant: 'destructive'});
        return;
      }
      let secondaryLocationIds: string[] = [];
      if (location.id && newSecondaryMeetingPoints.length > 0) {
        try {
          const saved = await materializeDraftMeetingPoints(
            newSecondaryMeetingPoints,
            String(projectId),
          );
          secondaryLocationIds = saved.map((p: any) => String(p.id));
        } catch (error: any) {
          setIsCreatingSessionType(false);
          toast({
            title: 'Error',
            description:
              error?.response?.data?.error ||
              error?.message ||
              'Failed to create meeting point',
            variant: 'destructive',
          });
          return;
        }
      }
      const payload = buildCreateSessionTypePayload(
        projectId,
        location.id,
        secondaryLocationIds,
      );
      if (attachedFiles && attachedFiles.length > 0) {
        await schedulerApi.createSessionType(createFormData(payload, attachedFiles));
      } else {
        await schedulerApi.createSessionType(payload);
      }
      toast({title: 'Success', description: 'Session type created successfully'});
      await queryClient.invalidateQueries({
        queryKey: sessionTypesDetailedByProjectQueryKey(projectId),
      });
      resetAddSessionTypeForm();
      setLoadingSessionTypes(true);
      try {
        // Post-mutation reload must see the just-created row, so fetch fresh
        // (not via the cache-serving useQuery) and push the result back into
        // the shared cache for any other co-mounted consumer.
        let sessionTypesList: any[];
        if (projectId) {
          sessionTypesList = await fetchDetailedSessionTypesByProject(projectId, {refresh: true});
          queryClient.setQueryData(
            sessionTypesDetailedByProjectQueryKey(projectId),
            sessionTypesList,
          );
        } else {
          const res = await schedulerApi.listDetailedSessionTypes();
          sessionTypesList = res.data?.data || [];
        }
        setSessionTypes(sessionTypesList.map(formatSessionTypeForDisplay));
      } finally {
        setLoadingSessionTypes(false);
      }
    } catch (error: any) {
      console.error('Error adding session type:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to add session type',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingSessionType(false);
    }
  };

  // Handle edit session type - expand view and enable inline editing
  // Applies a session type's linked meeting-point location to the edit form
  // fields. Extracted out of handleEditSessionType so that function's own
  // cognitive complexity stays low; behavior is unchanged.
  const applyLinkedLocationToEditForm = (linkedLocation: any) => {
    setEditMeetingPointName(linkedLocation.name || '');
    setEditMeetingPointStreetAddress(linkedLocation.street_address || '');
    setEditMeetingPointCity(linkedLocation.city || '');
    setEditMeetingPointState(linkedLocation.state || '');
    setEditMeetingPointZipCode(linkedLocation.zip_code || '');
    setEditMeetingPointCountry(linkedLocation.country || '');
    setEditMeetingPointInstructions(
      linkedLocation.instructions ||
        linkedLocation.metadata?.instructions ||
        '',
    );
    setEditingMeetingPoint(linkedLocation);
    sessionTimezoneUserOverrideRef.current = false;
  };

  const handleEditSessionType = (sessionType: any) => {
    const newExpanded = new Set(expandedSessionTypes);
    if (!newExpanded.has(sessionType.id)) {
      newExpanded.add(sessionType.id);
      setExpandedSessionTypes(newExpanded);
    }

    setEditingSessionTypeId(sessionType.id);
    setEditSessionTypeName(sessionType.name);
    setEditSessionTypeJobId(
      sessionType.job_id !== undefined &&
        sessionType.job_id !== null &&
        sessionType.job_id !== ''
        ? String(sessionType.job_id)
        : '',
    );
    setEditSessionTypeDescription(sessionType.description || '');
    setEditSessionTypeLocationType(
      (sessionType.session_type as 'onsite' | 'offsite') || 'onsite',
    );

    const duration = minutesToValueUnit(sessionType.duration || 60);
    setEditSessionTypeDuration(duration.value);
    setEditSessionTypeDurationUnit(duration.unit);

    setEditSelectedLocationForSession(
      sessionType.location_ids && sessionType.location_ids.length > 0
        ? String(sessionType.location_ids[0])
        : '',
    );

    const startDateToUse = sessionType.sessionTypeStartDate;
    const endDateToUse = sessionType.sessionTypeEndDate;
    editStartDateInvalidRef.current = false;
    editEndDateInvalidRef.current = false;
    setEditSessionTypeStartDate(
      formatSessionTypeCalendarDate(startDateToUse, 'yyyy-MM-dd'),
    );
    setEditSessionTypeEndDate(
      formatSessionTypeCalendarDate(endDateToUse, 'yyyy-MM-dd'),
    );
    setEditSessionTypeSchedulingRules(
      withStableRuleKeys(rulesFromSessionType(sessionType)),
    );
    setEditSessionTypeSlotMode(slotGenerationModeFromSessionType(sessionType));
    setEditSessionTypeDailyStartTime(
      bookingDailyTimeToInput(
        sessionType.bookingDailyStartTime ??
          sessionType.booking_daily_start_time,
      ),
    );
    setEditSessionTypeDailyEndTime(
      bookingDailyTimeToInput(
        sessionType.bookingDailyEndTime ?? sessionType.booking_daily_end_time,
      ),
    );

    const minimumNotice = minutesToValueUnit(sessionType.minimum_notice || 0);
    setEditSessionTypeMinimumNotice(minimumNotice.value);
    setEditSessionTypeMinimumNoticeUnit(minimumNotice.unit);

    const bufferBefore = minutesToValueUnit(sessionType.buffer_before || 0);
    setEditSessionTypeBufferBefore(bufferBefore.value);
    setEditSessionTypeBufferBeforeUnit(bufferBefore.unit);

    const bufferAfter = minutesToValueUnit(sessionType.buffer_after || 0);
    setEditSessionTypeBufferAfter(bufferAfter.value);
    setEditSessionTypeBufferAfterUnit(bufferAfter.unit);

    setEditSessionTypeParticipantsPerSlot(
      sessionType.participants_per_slot != null
        ? String(sessionType.participants_per_slot)
        : '',
    );

    const editBookingRules = bookingRulesFromSessionType(sessionType);
    setEditSessionTypeAllowMultiBooking(
      editBookingRules.allowMultipleBookingsPerParticipant,
    );
    setEditSessionTypeMaxSessionsPerParticipant(
      editBookingRules.maxSessionsPerParticipant != null
        ? String(editBookingRules.maxSessionsPerParticipant)
        : '',
    );
    setEditSessionTypeCapGroupBookingSize(editBookingRules.capGroupBookingSize);
    setEditSessionTypeMaxGroupSize(
      editBookingRules.maxGroupSize != null
        ? String(editBookingRules.maxGroupSize)
        : '',
    );
    setEditSessionTypeEnableNoShowLimit(
      editBookingRules.noShowCancellationLimit != null,
    );
    setEditSessionTypeNoShowCancellationLimit(
      editBookingRules.noShowCancellationLimit != null
        ? String(editBookingRules.noShowCancellationLimit)
        : '',
    );
    setEditSessionTypeAllowConcurrentModeratorBookings(
      editBookingRules.allowConcurrentModeratorBookings,
    );
    setEditSessionTypeMaxConcurrentModeratorSessions(
      editBookingRules.maxConcurrentModeratorSessions != null
        ? String(editBookingRules.maxConcurrentModeratorSessions)
        : '',
    );
    setEditSessionTypeWindowCap(windowCapFormFromBookingRules(editBookingRules));

    const linkedLocation = sessionType.locations?.[0];
    if (linkedLocation) {
      applyLinkedLocationToEditForm(linkedLocation);
    }
    setEditSessionTypeTimezone(sessionType.timezone || '');
    setEditSessionTypeParticipantBookingRules(
      sessionType.participant_booking_rules || false,
    );

    // Only seed ids that still resolve to an active project moderator (OD-1065) —
    // the checkbox list below only renders rows for the current roster, so a stale
    // id would otherwise sit invisibly in this state and get resubmitted on Save.
    // Skip the filter while the roster hasn't loaded yet (empty array) — filtering
    // against an empty set would wrongly drop every real id if Edit is opened
    // before projectModerators finishes fetching.
    setEditSelectedModeratorsForSession(
      resolveEditModeratorIds(sessionType, projectModerators),
    );

    setEditSelectedTasksForSession(firstTaskIdFromSessionType(sessionType));

    setEditExistingAttachmentPaths(
      parseAttachmentPaths(sessionType.email_attachment_path),
    );
    setEditAttachedFiles([]);
  };

  // Helper function to clear meeting point form fields
  const clearMeetingPointFields = () => {
    setMeetingPointName('');
    setMeetingPointStreetAddress('');
    setMeetingPointCity('');
    setMeetingPointState('');
    setMeetingPointZipCode('');
    setMeetingPointCountry('');
    setMeetingPointInstructions('');
    // A secondary cannot outlive the primary it was added beside.
    setNewSecondaryMeetingPoints([]);
    setNewSecondaryMeetingPointTarget(null);
    sessionTimezoneUserOverrideRef.current = false;
  };

  const copyMeetingPointToDraft = () => {
    setMeetingPointNameDraft(meetingPointName);
    setMeetingPointStreetAddressDraft(meetingPointStreetAddress);
    setMeetingPointCityDraft(meetingPointCity);
    setMeetingPointStateDraft(meetingPointState);
    setMeetingPointZipCodeDraft(meetingPointZipCode);
    setMeetingPointCountryDraft(meetingPointCountry);
    setMeetingPointInstructionsDraft(meetingPointInstructions);
  };

  const copyMeetingPointDraftToSaved = () => {
    setMeetingPointName(meetingPointNameDraft);
    setMeetingPointStreetAddress(meetingPointStreetAddressDraft);
    setMeetingPointCity(meetingPointCityDraft);
    setMeetingPointState(meetingPointStateDraft);
    setMeetingPointZipCode(meetingPointZipCodeDraft);
    setMeetingPointCountry(meetingPointCountryDraft);
    setMeetingPointInstructions(meetingPointInstructionsDraft);
    if (editedMeetingPointIsPrimary()) {
      void applyTimezoneFromMeetingPointLocation(
        meetingPointCountryDraft,
        meetingPointStateDraft,
        meetingPointCityDraft,
      );
    }
  };

  const clearMeetingPointDraftFields = () => {
    setMeetingPointNameDraft('');
    setMeetingPointStreetAddressDraft('');
    setMeetingPointCityDraft('');
    setMeetingPointStateDraft('');
    setMeetingPointZipCodeDraft('');
    setMeetingPointCountryDraft('');
    setMeetingPointInstructionsDraft('');
  };

  // Helper function to clear edit meeting point form fields
  const clearEditMeetingPointFields = () => {
    setEditMeetingPointName('');
    setEditMeetingPointStreetAddress('');
    setEditMeetingPointCity('');
    setEditMeetingPointState('');
    setEditMeetingPointZipCode('');
    setEditMeetingPointCountry('');
    setEditMeetingPointInstructions('');
  };

  // Helper function to build full address from parts
  const buildFullAddress = (
    streetAddress: string,
    city: string,
    state: string,
    zipCode: string,
    country: string,
  ): string => {
    const addressParts = [
      streetAddress.trim(),
      city.trim(),
      state.trim(),
      zipCode.trim(),
      country.trim(),
    ].filter(Boolean);
    return addressParts.join(', ');
  };

  // Helper function to check if meeting point fields are cleared
  const areMeetingPointFieldsCleared = (
    name: string,
    streetAddress: string,
  ): boolean => {
    return !name.trim() && !streetAddress.trim();
  };

  const applyTimezoneFromMeetingPointState = (timezone: string) => {
    if (!timezone) {
      sessionTimezoneUserOverrideRef.current = false;
    } else if (sessionTimezoneUserOverrideRef.current) {
      return;
    }
    const value = timezone || '';
    if (editingSessionTypeId) {
      setEditSessionTypeTimezone(value);
    } else {
      setNewSessionTypeTimezone(value);
    }
  };

  const handleSessionTimezoneManualChange = (timezone: string) => {
    sessionTimezoneUserOverrideRef.current = true;
    if (editingSessionTypeId) {
      setEditSessionTypeTimezone(timezone);
    } else {
      setNewSessionTypeTimezone(timezone);
    }
  };

  /**
   * Does the meeting point currently open in a dialog define the session
   * timezone? Only the primary does (OD-1325 AC-4).
   *
   * Adding counts as primary only when the session type has no meeting point
   * yet — the first one becomes the primary; later ones are secondaries and
   * must leave the session's zone alone.
   */
  const editedMeetingPointIsPrimary = (): boolean => {
    if (isCreatingNewSessionType || !editingSessionTypeId) {
      return newSecondaryMeetingPointTarget === null;
    }
    if (editingMeetingPoint) return Boolean(editingMeetingPoint.is_primary);
    const current = sessionTypes.find(
      (st: any) => String(st.id) === String(editingSessionTypeId),
    );
    return (current?.locations || []).length === 0;
  };

  /**
   * The cascading country/state/city dropdowns push a resolved zone up as soon
   * as a country is picked. Routed through this guard so choosing "Andorra" in
   * a SECONDARY's dialog cannot re-time the whole session type mid-edit — the
   * leak that survived gating the open and save paths, because this one fires
   * from inside the dropdown itself.
   */
  const applySessionTimezoneIfPrimary = (timezone: string) => {
    if (!editedMeetingPointIsPrimary()) return;
    applyTimezoneFromMeetingPointState(timezone);
  };

  const applyTimezoneFromMeetingPointLocation = async (
    country: string,
    state: string,
    city: string,
  ) => {
    if (!country.trim() || !state.trim()) {
      applyTimezoneFromMeetingPointState('');
      return;
    }
    const tz = await getTimezoneForLocationNames(
      country,
      state,
      city.trim() ? city : undefined,
    );
    applyTimezoneFromMeetingPointState(tz ?? '');
  };

  const getMeetingPointProjectId = (): string | null => {
    const projectId =
      selectedProject ||
      projectsState.currentProject?.id ||
      projectIdFromUrl;
    return projectId && projectId !== 'all' ? String(projectId) : null;
  };

  const buildEditMeetingPointUpdatePayload = (projectId: string) => {
    const fullAddress = buildFullAddress(
      editMeetingPointStreetAddress,
      editMeetingPointCity,
      editMeetingPointState,
      editMeetingPointZipCode,
      editMeetingPointCountry,
    );

    const updateData: Record<string, unknown> = {
      project_id: projectId,
      name: editMeetingPointName.trim(),
      address: fullAddress || editMeetingPointStreetAddress.trim(),
      street_address: editMeetingPointStreetAddress.trim(),
      city: editMeetingPointCity.trim(),
      state: editMeetingPointState.trim(),
      zip_code: editMeetingPointZipCode.trim(),
      country: editMeetingPointCountry.trim(),
      floor: null,
      capacity: null,
      is_active:
        editingMeetingPoint?.is_active !== undefined
          ? editingMeetingPoint.is_active
          : true,
    };

    if (editMeetingPointInstructions.trim()) {
      updateData.instructions = editMeetingPointInstructions.trim();
    } else {
      updateData.instructions = null;
    }

    return updateData;
  };

  const applyMeetingPointFieldsToForm = (source: Record<string, unknown>) => {
    setEditMeetingPointName(unknownFieldToString(source.name));
    setEditMeetingPointStreetAddress(unknownFieldToString(source.street_address));
    setEditMeetingPointCity(unknownFieldToString(source.city));
    setEditMeetingPointState(unknownFieldToString(source.state));
    setEditMeetingPointZipCode(unknownFieldToString(source.zip_code));
    setEditMeetingPointCountry(unknownFieldToString(source.country));
    setEditMeetingPointInstructions(
      unknownFieldToString(
        source.instructions ??
          (source.metadata as {instructions?: string} | undefined)
            ?.instructions,
      ),
    );
  };

  const mergeSavedMeetingPointIntoEditingLocation = (
    updateData: Record<string, unknown>,
  ) => {
    if (!editingMeetingPoint) return;
    const merged = {
      ...editingMeetingPoint,
      name: updateData.name,
      address: updateData.address,
      street_address: updateData.street_address,
      city: updateData.city,
      state: updateData.state,
      zip_code: updateData.zip_code,
      country: updateData.country,
      instructions: updateData.instructions ?? '',
      // OD-1325: the venue's zone is re-resolved whenever its address changes,
      // so it has to travel with the rest of the saved row. Left out, the list
      // kept rendering the zone the old address implied. Checked for presence
      // rather than truthiness — a null is a real answer, meaning the new
      // address resolves to no known zone, and must overwrite the old value.
      timezone:
        'timezone' in updateData
          ? updateData.timezone
          : (editingMeetingPoint as Record<string, unknown>).timezone,
    };
    setEditingMeetingPoint(merged);
    return merged;
  };

  const patchMeetingPointInSessionTypes = (
    locationId: string | number,
    patch: Record<string, unknown>,
  ) => {
    setSessionTypes((prev) =>
      prev.map((st) => patchSessionTypeLocation(st, locationId, patch)),
    );
  };

  /**
   * Meeting-point list operations (OD-1325).
   *
   * All four edit the list in local state and take effect when the session type
   * is saved, like every other field on this form — so a mis-click is undone by
   * cancelling the edit rather than needing a second corrective action.
   */
  const updateMeetingPoints = (
    sessionTypeId: string,
    transform: (points: any[]) => any[],
  ) => {
    setSessionTypes((prev) =>
      prev.map((st) => {
        if (String(st.id) !== String(sessionTypeId)) return st;
        const locations = transform(st.locations || []);
        return {
          ...st,
          locations,
          // `location_ids` is the shape the save-time validation and several
          // older readers still look at, so it is rebuilt from the list here
          // rather than left to drift. Every meeting-point edit goes through
          // this function, so syncing once covers add, remove, reorder,
          // make-primary and deactivate alike — leaving it stale made a session
          // type with freshly added venues fail "At least one meeting point is
          // required for onsite sessions".
          location_ids: locations.map((loc: any) => String(loc.id)),
        };
      }),
    );
  };

  /**
   * Number display_order over the list exactly as given — the caller's order is
   * the answer, not a starting point.
   */
  const numberInGivenOrder = (points: any[]): any[] => {
    let order = 0;
    return points.map((p) => ({
      ...p,
      display_order: p.is_primary ? 0 : ++order,
    }));
  };

  /**
   * Sort into canonical order (primary first, then secondaries, deactivated
   * last) and number from that.
   *
   * NOT for reordering: the sort reads the display_order the rows already
   * carry, so a caller that has just swapped two rows would have its swap
   * undone before the new numbers were assigned. Those callers number the
   * order they built, with numberInGivenOrder.
   */
  const renumber = (points: any[]): any[] =>
    numberInGivenOrder(sortMeetingPoints(points as MeetingPointRow[]));

  const handleMakeMeetingPointPrimary = (sessionType: any, point: any) => {
    updateMeetingPoints(sessionType.id, (points) =>
      renumber(
        points.map((p: any) => ({
          ...p,
          is_primary: String(p.id) === String(point.id),
        })),
      ),
    );
    // The thing a scheduler would otherwise assume: this does not move anyone
    // who has already booked (AC-6).
    toast({
      title: `${point.name || 'Meeting point'} is now the primary`,
      description: 'Sessions already booked keep the meeting point they were booked at.',
    });
  };

  const handleToggleMeetingPointActive = (sessionType: any, point: any) => {
    const deactivating = point.is_active !== false;
    updateMeetingPoints(sessionType.id, (points) =>
      renumber(
        points.map((p: any) =>
          String(p.id) === String(point.id) ? {...p, is_active: !deactivating} : p,
        ),
      ),
    );
    if (deactivating) {
      toast({
        title: `${point.name || 'Meeting point'} deactivated`,
        description:
          'It stays resolvable for sessions already booked there, and is no longer offered on new invites.',
      });
    }
  };

  const handleRemoveMeetingPoint = (sessionType: any, point: any) => {
    updateMeetingPoints(sessionType.id, (points) =>
      renumber(points.filter((p: any) => String(p.id) !== String(point.id))),
    );
  };

  const handleMoveMeetingPoint = (
    sessionType: any,
    point: any,
    direction: -1 | 1,
  ) => {
    updateMeetingPoints(sessionType.id, (points) => {
      const ordered = sortMeetingPoints(points as MeetingPointRow[]);
      const secondaries = ordered.filter(
        (p) => !p.is_primary && p.is_active !== false,
      );
      const from = secondaries.findIndex((p) => String(p.id) === String(point.id));
      const to = from + direction;
      if (from < 0 || to < 0 || to >= secondaries.length) return points;
      const reordered = [...secondaries];
      [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
      const primary = ordered.filter((p) => p.is_primary);
      const deactivated = ordered.filter((p) => !p.is_primary && p.is_active === false);
      // Numbered in the order just built — re-sorting here would read the old
      // display_order values and put the two rows straight back.
      return numberInGivenOrder([...primary, ...reordered, ...deactivated]);
    });
  };

  // Handle edit meeting point - open dialog
  const handleEditMeetingPoint = (location: any) => {
    meetingPointDialogCommittedRef.current = false;

    const source =
      editingMeetingPoint &&
      String(editingMeetingPoint.id) === String(location.id)
        ? editingMeetingPoint
        : location;

    setEditingMeetingPoint(source);
    applyMeetingPointFieldsToForm(source);

    // Only the primary defines the session timezone (OD-1325 AC-4). Deriving it
    // here regardless meant simply *opening* a secondary's edit dialog re-timed
    // the session type — before anything had even been changed, let alone saved.
    if (source.is_primary) {
      void applyTimezoneFromMeetingPointLocation(
        String(source.country ?? ''),
        String(source.state ?? ''),
        String(source.city ?? ''),
      );
    }

    setShowEditMeetingPointDialog(true);
  };

  // Handle update meeting point — persist location immediately
  const getOnsiteEditMeetingPointValidationError = (): string | undefined => {
    if (!editMeetingPointName.trim()) return 'Meeting Point Name is required';
    if (!editMeetingPointStreetAddress.trim()) {
      return 'Meeting Point Street Address is required';
    }
    return (
      getMeetingPointLocationValidationError(
        editMeetingPointCountry,
        editMeetingPointState,
        editMeetingPointCity,
      ) || undefined
    );
  };

  // Offsite: meeting point is optional, but a partial entry is invalid.
  const getOffsiteEditMeetingPointValidationError = (): string | undefined => {
    if (editMeetingPointName.trim() && !editMeetingPointStreetAddress.trim()) {
      return 'Meeting Point Street Address is required when Name is provided';
    }
    if (!editMeetingPointName.trim() && editMeetingPointStreetAddress.trim()) {
      return 'Meeting Point Name is required when Street Address is provided';
    }
    if (editMeetingPointName.trim() && editMeetingPointStreetAddress.trim()) {
      return (
        getMeetingPointLocationValidationError(
          editMeetingPointCountry,
          editMeetingPointState,
          editMeetingPointCity,
        ) || undefined
      );
    }
    return undefined;
  };

  // Split into onsite/offsite helpers above so each stays simple; this just
  // dispatches on location type (same behavior as before the split).
  const getEditMeetingPointValidationError = (): string | undefined => {
    const isOffsite = editSessionTypeLocationType === 'offsite';
    return isOffsite
      ? getOffsiteEditMeetingPointValidationError()
      : getOnsiteEditMeetingPointValidationError();
  };

  const handleUpdateMeetingPoint = async () => {
    const isOffsite = editSessionTypeLocationType === 'offsite';
    const fieldsCleared = areMeetingPointFieldsCleared(
      editMeetingPointName,
      editMeetingPointStreetAddress,
    );
    // Offsite allows clearing all fields to remove the meeting point.
    if (isOffsite && fieldsCleared) {
      toast({title: 'Success', description: 'Meeting point updated successfully'});
      setShowEditMeetingPointDialog(false);
      return;
    }

    const validationError = getEditMeetingPointValidationError();
    if (validationError) {
      toast({title: 'Error', description: validationError, variant: 'destructive'});
      return;
    }

    if (!editingMeetingPoint) {
      toast({
        title: 'Error',
        description: 'No meeting point selected for editing',
        variant: 'destructive',
      });
      return;
    }

    const projectId = getMeetingPointProjectId();
    if (!projectId) {
      toast({title: 'Error', description: 'Project ID is required', variant: 'destructive'});
      return;
    }

    // A meeting point that has not been created yet is edited in place; there
    // is no schedule_location row to PUT to, and creating one here would
    // reintroduce the orphan this flow exists to avoid.
    if ((editingMeetingPoint as Record<string, unknown>)?.__pending) {
      const draft = {
        name: editMeetingPointName.trim(),
        street_address: editMeetingPointStreetAddress.trim(),
        city: editMeetingPointCity.trim(),
        state: editMeetingPointState.trim(),
        zip_code: editMeetingPointZipCode.trim(),
        country: editMeetingPointCountry.trim(),
        instructions: editMeetingPointInstructions.trim() || null,
      };
      patchMeetingPointInSessionTypes(editingMeetingPoint.id, draft);
      setEditingMeetingPoint({...editingMeetingPoint, ...draft});
      meetingPointDialogCommittedRef.current = true;
      setShowEditMeetingPointDialog(false);
      return;
    }

    setIsSavingMeetingPoint(true);
    try {
      const updateData = buildEditMeetingPointUpdatePayload(projectId);
      // Editing the address can move the venue into another zone, so its stored
      // timezone is re-resolved with it (AC-4) rather than left at whatever the
      // old address implied.
      updateData.timezone =
        (await getTimezoneForLocationNames(
          editMeetingPointCountry.trim(),
          editMeetingPointState.trim(),
          editMeetingPointCity.trim() || undefined,
        )) || null;
      const response = await schedulerApi.updateScheduleLocation(
        editingMeetingPoint.id,
        updateData,
      );
      const savedRow = (response.data?.data ?? updateData) as Record<
        string,
        unknown
      >;
      const merged = mergeSavedMeetingPointIntoEditingLocation(savedRow);
      applyMeetingPointFieldsToForm(savedRow);
      if (merged) {
        patchMeetingPointInSessionTypes(editingMeetingPoint.id, merged);
      }
      meetingPointDialogCommittedRef.current = true;

      // The session timezone is resolved from the PRIMARY meeting point only
      // (OD-1325 AC-4). It used to follow whichever venue was edited last, so
      // editing a secondary in another zone silently re-timed every slot on the
      // session type — the exact failure AC-5 exists to prevent.
      const editedIsPrimary = Boolean(
        (editingMeetingPoint as Record<string, unknown>)?.is_primary,
      );
      if (editedIsPrimary) {
        await applyTimezoneFromMeetingPointLocation(
          unknownFieldToString(savedRow.country),
          unknownFieldToString(savedRow.state),
          unknownFieldToString(savedRow.city),
        );
      } else {
        // A secondary that has moved out of the session's zone cannot be saved
        // (the API refuses it), so say so here rather than letting the user
        // discover it on Save with no idea which venue caused it.
        const sessionTz = editSessionTypeTimezone || '';
        const pointTz = unknownFieldToString(savedRow.timezone);
        if (sessionTz && pointTz && pointTz !== sessionTz) {
          toast({
            title: 'Meeting point is in a different timezone',
            description: `${unknownFieldToString(savedRow.name) || 'This meeting point'} is in ${pointTz}, but this session type runs in ${sessionTz}. Every meeting point must share one timezone — saving will be refused until this is resolved.`,
            variant: 'destructive',
          });
        }
      }

      await queryClient.invalidateQueries({
        queryKey: sessionTypesDetailedByProjectQueryKey(projectId),
      });

      toast({title: 'Success', description: 'Meeting point updated successfully'});
      setShowEditMeetingPointDialog(false);
    } catch (error: any) {
      console.error('Error updating meeting point:', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.error || 'Failed to update meeting point',
        variant: 'destructive',
      });
    } finally {
      setIsSavingMeetingPoint(false);
    }
  };

  // Handle update session type
  // Perform the actual session type update
  // Success toast for a session type update; wording depends on whether the
  // change affects future sessions only (server-reported). Extracted out of
  // performSessionTypeUpdate to keep that function's cognitive complexity low.
  const showSessionTypeUpdateSuccessToast = (affectsFutureSessions: boolean) => {
    if (affectsFutureSessions) {
      toast({
        title: 'Success',
        description:
          'Session type updated. New schedules created from now on will use the new location.',
      });
    } else {
      toast({title: 'Success', description: 'Session type updated'});
    }
  };

  // Collapses a session type's expanded list card, if it has one open. A
  // no-op when id is falsy, mirroring the `if (stIdToCollapse)` guards this
  // replaces at both call sites in performSessionTypeUpdate.
  const collapseSessionTypeRow = (id: string | null | undefined) => {
    if (!id) return;
    const newExpanded = new Set(expandedSessionTypes);
    newExpanded.delete(id);
    setExpandedSessionTypes(newExpanded);
  };

  const performSessionTypeUpdate = async (updateData: any) => {
    setIsUpdatingSessionType(true);
    try {
      // Extract payload and file information
      const payload = updateData.payload || updateData; // Support both old format (just payload) and new format

      // Drafted meeting points become real rows here — after the confirm, on
      // the way to the save that links them (OD-1325). Doing it any earlier is
      // what used to leave orphaned locations behind when a save was abandoned.
      if (Array.isArray(payload.__draft_meeting_points)) {
        const projectId =
          selectedProject ||
          projectsState.currentProject?.id ||
          projectIdFromUrl;
        const materialized = await materializeDraftMeetingPoints(
          payload.__draft_meeting_points,
          String(projectId),
        );
        let order = 0;
        payload.meeting_points = materialized.map((p: any) => ({
          location_id: Number(p.id),
          is_primary: Boolean(p.is_primary),
          is_active: p.is_active !== false,
          display_order: p.is_primary ? 0 : ++order,
        }));
        payload.location_ids = payload.meeting_points.map(
          (p: any) => p.location_id,
        );
        delete payload.__draft_meeting_points;
        if (editingSessionTypeId) {
          updateMeetingPoints(editingSessionTypeId, () => materialized);
        }
      }
      const hasFiles = updateData.hasFiles || false;
      const files = updateData.files || [];
      const removeAttachment = updateData.removeAttachment || false;

      const finalPayload = resolveFinalUpdatePayload(
        payload,
        hasFiles,
        files,
        removeAttachment,
      );

      const response = await schedulerApi.updateSessionType(
        editingSessionTypeId!,
        finalPayload,
      );

      showSessionTypeUpdateSuccessToast(!!response.data?.affectsFutureSessions);

      // Store the session type ID to collapse it after reload (before clearing it)
      const stIdToCollapse = editingSessionTypeId;

      // Reset form and exit edit mode
      setEditingSessionTypeId(null);
      setEditSessionTypeName('');
      setEditSessionTypeJobId('');
      setEditSessionTypeDescription('');
      setEditSessionTypeDuration('');
      setEditSessionTypeDurationUnit('minutes');
      setEditSessionTypeStartDate('');
      setEditSessionTypeEndDate('');
      setEditSessionTypeSchedulingRules(emptySchedulingRules());
      setEditSessionTypeDailyStartTime('');
      setEditSessionTypeDailyEndTime('');
      setEditSessionTypeMinimumNotice('');
      setEditSessionTypeMinimumNoticeUnit('minutes');
      setEditSessionTypeBufferBefore('');
      setEditSessionTypeBufferBeforeUnit('minutes');
      setEditSessionTypeBufferAfter('');
      setEditSessionTypeBufferAfterUnit('minutes');
      setEditSessionTypeParticipantsPerSlot('');
      setEditSessionTypeTimezone('');
      setEditSessionTypeParticipantBookingRules(false);
      setEditSessionTypeAllowMultiBooking(false);
      setEditSessionTypeMaxSessionsPerParticipant('');
      setEditSessionTypeCapGroupBookingSize(false);
      setEditSessionTypeMaxGroupSize('');
      setEditSessionTypeEnableNoShowLimit(false);
      setEditSessionTypeNoShowCancellationLimit('');
      setEditSessionTypeAllowConcurrentModeratorBookings(false);
      setEditSessionTypeMaxConcurrentModeratorSessions('');
      setEditSessionTypeWindowCap(emptyWindowCapForm());
      setEditSessionTypeLocationType('onsite');
      setEditSelectedLocationForSession('');
      setEditSelectedModeratorsForSession([]);
      setEditSelectedTasksForSession(null);
      setEditAttachedFiles([]);
      setEditExistingAttachmentPaths([]);
      setModeratorSearchEdit({});
      
      // Clear meeting point edit state after successful save
      setEditingMeetingPoint(null);
      clearEditMeetingPointFields();
      setNewlyCreatedLocationId(null);
      
      // Collapse the expanded section after successful update
      collapseSessionTypeRow(stIdToCollapse);

      const projectId =
        selectedProject ||
        projectsState.currentProject?.id ||
        projectIdFromUrl;
      if (projectId && projectId !== 'all') {
        await queryClient.invalidateQueries({
          queryKey: sessionTypesDetailedByProjectQueryKey(projectId),
        });
      }
      // The save is what writes the meeting-point history entries (OD-1325
      // AC-8), so the log this screen shows is stale the moment it succeeds.
      // Without this the panel keeps serving the pre-save page and the change
      // the user just made appears to have gone unrecorded.
      await queryClient.invalidateQueries({
        queryKey: queryKeys.history.forSessionType(String(stIdToCollapse)),
      });

      // Reload session types (fresh, not cache-served — see the create-flow
      // comment above; push into the shared cache for other consumers too).
      setLoadingSessionTypes(true);
      try {
        const sessionTypesList = await fetchDetailedSessionTypesByProject(projectId, {refresh: true});
        queryClient.setQueryData(
          sessionTypesDetailedByProjectQueryKey(projectId),
          sessionTypesList,
        );
        const formattedSessionTypes = sessionTypesList.map(
          formatSessionTypeForDisplay,
        );
        setSessionTypes(formattedSessionTypes);

        // Collapse the updated session type
        collapseSessionTypeRow(stIdToCollapse);
      } finally {
        setLoadingSessionTypes(false);
      }
    } catch (error: any) {
      console.error('Error updating session type:', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.error ||
          error.message ||
          'Failed to update session type',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsUpdatingSessionType(false);
    }
  };

  // Booking-limit numeric fields (max sessions per participant, no-show
  // limit, max concurrent moderator sessions) for the Update/Edit form.
  // Extracted out of getUpdateSessionTypeValidationError to keep that
  // function's cognitive complexity low; behavior (incl. order of checks)
  // is unchanged. Mirrors getAddSessionTypeBookingLimitsValidationError,
  // using the edit-form fields instead of the add-form ones.
  const getUpdateSessionTypeBookingLimitsValidationError = ():
    | string
    | undefined => {
    const allowMultipleBookings = resolveAllowMultipleBookings(
      editSessionTypeWindowCap.enabled,
      editSessionTypeAllowMultiBooking,
    );
    if (
      allowMultipleBookings &&
      (editSessionTypeMaxSessionsPerParticipant.trim() === '' ||
        !Number.isInteger(Number(editSessionTypeMaxSessionsPerParticipant)) ||
        Number(editSessionTypeMaxSessionsPerParticipant) < 2)
    ) {
      // Required once enabled (OD-1171) — "more than one at once" with no cap isn't meaningful.
      return 'Max sessions per participant must be a whole number of 2 or greater';
    }
    if (
      editSessionTypeCapGroupBookingSize &&
      (editSessionTypeMaxGroupSize.trim() === '' ||
        !Number.isInteger(Number(editSessionTypeMaxGroupSize)) ||
        Number(editSessionTypeMaxGroupSize) < 2)
    ) {
      return 'Maximum group size is required and must be a whole number of 2 or greater';
    }
    if (
      editSessionTypeEnableNoShowLimit &&
      (editSessionTypeNoShowCancellationLimit.trim() === '' ||
        !Number.isInteger(Number(editSessionTypeNoShowCancellationLimit)) ||
        Number(editSessionTypeNoShowCancellationLimit) < 1)
    ) {
      // Required once enabled — an alert threshold with no limit would never fire.
      return 'No-show / cancellation limit must be a whole number of 1 or greater';
    }
    if (
      editSessionTypeAllowConcurrentModeratorBookings &&
      (editSessionTypeMaxConcurrentModeratorSessions.trim() === '' ||
        !Number.isInteger(Number(editSessionTypeMaxConcurrentModeratorSessions)) ||
        Number(editSessionTypeMaxConcurrentModeratorSessions) < 2)
    ) {
      // Required once enabled — "more than one at once" with no cap isn't meaningful.
      return 'Maximum concurrent sessions must be a whole number of 2 or greater';
    }
    return validateWindowCapForm(editSessionTypeWindowCap);
  };

  // Resolves which moderators/timezone to run the conflict check against:
  // the edit form's own selection when present, otherwise falling back to
  // the session type's currently saved values. Extracted out of
  // getUpdateSessionTypeValidationError alongside the checks below so both
  // the timezone-conflict check and the moderator-availability-mode check
  // can share the same resolved values (matches the original behavior).
  const resolveUpdateModeratorsAndTimezoneToCheck = (
    currentSessionType: any,
  ): {moderatorsToCheck: string[]; timezoneToCheck: string} => {
    const moderatorsToCheck =
      editSelectedModeratorsForSession.length > 0
        ? editSelectedModeratorsForSession
        : currentSessionType?.moderator_ids || [];
    const timezoneToCheck =
      editSessionTypeTimezone || currentSessionType?.timezone || '';
    return {moderatorsToCheck, timezoneToCheck};
  };

  // Moderator/timezone conflict check for the Update/Edit form. Extracted
  // out of getUpdateSessionTypeValidationError for the same reason as above.
  const getUpdateSessionTypeModeratorTimezoneValidationError = (
    moderatorsToCheck: string[],
    timezoneToCheck: string,
  ): string | undefined => {
    if (moderatorsToCheck.length === 0 || !timezoneToCheck) return undefined;
    const tz = validateModeratorTimezoneConflicts(
      moderatorsToCheck,
      timezoneToCheck,
      editingSessionTypeId,
    );
    return tz.isValid ? undefined : tz.error;
  };

  const getUpdateSessionTypeValidationError = (
    existingLocationIds: any[],
    currentSessionType: any,
  ): string | undefined => {
    const typedDateError = invalidTypedDateMessage(
      editStartDateInvalidRef.current,
      editEndDateInvalidRef.current,
    );
    if (typedDateError) return typedDateError;
    const validation = validateSessionTypeFields({
      name: editSessionTypeName,
      jobId: editSessionTypeJobId,
      startDate: editSessionTypeStartDate,
      endDate: editSessionTypeEndDate,
      minimumNotice: editSessionTypeMinimumNotice,
      duration: editSessionTypeDuration,
      bufferBefore: editSessionTypeBufferBefore,
      bufferAfter: editSessionTypeBufferAfter,
      selectedTasks: editSelectedTasksForSession ?? '',
      availableTasksCount: availableTasks.length,
      selectedModerators: editSelectedModeratorsForSession,
      existingLocationIds: existingLocationIds,
      sessionType: editSessionTypeLocationType,
    });
    if (!validation.isValid) return validation.error;
    if (isBookingOptionalModeratorEnabled()) {
      const dailyOrder = validateDailyTimeRangeOrder(
        editSessionTypeStartDate,
        editSessionTypeEndDate,
        editSessionTypeDailyStartTime,
        editSessionTypeDailyEndTime,
      );
      if (!dailyOrder.ok) return dailyOrder.message;
    }
    const bookingLimitsError = getUpdateSessionTypeBookingLimitsValidationError();
    if (bookingLimitsError) return bookingLimitsError;
    const {moderatorsToCheck, timezoneToCheck} =
      resolveUpdateModeratorsAndTimezoneToCheck(currentSessionType);
    const timezoneError = getUpdateSessionTypeModeratorTimezoneValidationError(
      moderatorsToCheck,
      timezoneToCheck,
    );
    if (timezoneError) return timezoneError;
    if (
      isProjectManager &&
      editSessionTypeSlotMode === 'moderator_availability' &&
      moderatorsToCheck.length === 0
    ) {
      return 'Assign at least one moderator to use Moderator Availability mode';
    }
    return undefined;
  };

  /**
   * The meeting-point list to save (OD-1325): which venues, in what order, and
   * which is the primary. Sent instead of bare `location_ids` so re-designating
   * the primary or deactivating a venue survives the round-trip — an id list
   * cannot express either.
   *
   * A meeting point just created through the dialog is appended rather than
   * replacing the list, which is what makes a *second* venue possible at all;
   * it becomes the primary only when there is nothing else to be primary.
   */
  const resolveUpdateMeetingPoints = (
    currentSessionType: any,
    fieldsCleared: boolean,
    isOffsite: boolean,
  ): Array<Record<string, unknown>> => {
    if (fieldsCleared && isOffsite) return [];

    const existing: MeetingPointRow[] = currentSessionType?.locations || [];
    const points: MeetingPointRow[] = [...existing];

    if (
      newlyCreatedLocationId &&
      !points.some((p) => String(p.id) === String(newlyCreatedLocationId))
    ) {
      points.push({
        id: newlyCreatedLocationId,
        is_primary: points.length === 0,
        is_active: true,
        display_order: points.length,
      });
    }

    const ordered = sortMeetingPoints(points);

    // A drafted meeting point has no id yet, so the list cannot be expressed as
    // location ids at all. It travels as drafts and is resolved on the way to
    // the save (see performSessionTypeUpdate), which is what keeps an abandoned
    // edit from leaving a location row behind.
    if (ordered.some((p: any) => p.__pending)) {
      return ordered as Array<Record<string, unknown>>;
    }

    let order = 0;
    return ordered.map((p) => ({
      location_id: Number(p.id),
      is_primary: Boolean(p.is_primary),
      is_active: p.is_active !== false,
      display_order: p.is_primary ? 0 : ++order,
    }));
  };

  // Sets cap_group_booking_size + max_group_size on the payload. Always sent
  // (like participants_per_slot), so the form's current state is the source of
  // truth; max is null while the cap is off, meaning unlimited group size.
  const applyGroupSizeCapFields = (payload: any): void => {
    payload.cap_group_booking_size = editSessionTypeCapGroupBookingSize;
    payload.max_group_size = editSessionTypeCapGroupBookingSize
      ? parseInt(editSessionTypeMaxGroupSize, 10)
      : null;
  };

  // Sets email_attachment_path (cleared) or keep_existing_paths (preserved)
  // on the payload, mutually exclusive depending on whether any attachments remain.
  const applyAttachmentPathFields = (payload: any): void => {
    if (editExistingAttachmentPaths.length === 0 && !editAttachedFiles.length) {
      payload.email_attachment_path = null;
    } else {
      payload.keep_existing_paths = JSON.stringify(editExistingAttachmentPaths);
    }
  };

  const buildUpdateSessionTypePayload = (
    projectId: string,
    existingLocationIds: any[],
    currentSessionType: any,
    fieldsCleared: boolean,
    isOffsite: boolean,
  ): any => {
    const resolvedMeetingPoints = resolveUpdateMeetingPoints(
      currentSessionType,
      fieldsCleared,
      isOffsite,
    );
    // Drafts have no ids to send yet; they are created and substituted after
    // the confirm, in performSessionTypeUpdate.
    const hasDraftMeetingPoints = resolvedMeetingPoints.some(
      (p: any) => p.__pending,
    );
    const meetingPointsToUse = hasDraftMeetingPoints ? [] : resolvedMeetingPoints;
    // Derived from the list rather than computed separately: the server reads
    // meeting_points when present, and two independently-built views of the
    // same thing is how they end up disagreeing.
    const locationIdsToUse = meetingPointsToUse.map((p) => p.location_id as number);
    const allowMultipleBookings = resolveAllowMultipleBookings(
      editSessionTypeWindowCap.enabled,
      editSessionTypeAllowMultiBooking,
    );
    const payload: any = {
      project_id: projectId,
      name: editSessionTypeName.trim(),
      job_id: editSessionTypeJobId,
      description: editSessionTypeDescription.trim(),
      duration_minutes: toMinutes(
        editSessionTypeDuration,
        editSessionTypeDurationUnit,
      ),
      location_ids: locationIdsToUse,
      // The ordered shape wins server-side when present; location_ids stays for
      // any caller still reading it off this payload.
      meeting_points: meetingPointsToUse,
      ...(hasDraftMeetingPoints
        ? {__draft_meeting_points: resolvedMeetingPoints}
        : {}),
      moderator_ids: editSelectedModeratorsForSession,
      minimum_notice: toMinutes(
        editSessionTypeMinimumNotice,
        editSessionTypeMinimumNoticeUnit,
      ),
      buffer_before: toMinutes(
        editSessionTypeBufferBefore,
        editSessionTypeBufferBeforeUnit,
      ),
      buffer_after: toMinutes(
        editSessionTypeBufferAfter,
        editSessionTypeBufferAfterUnit,
      ),
      session_type: editSessionTypeLocationType,
      participants_per_slot:
        editSessionTypeParticipantsPerSlot.trim() === ''
          ? null
          : parseInt(editSessionTypeParticipantsPerSlot, 10),
      // OD-1097: always sent (like participants_per_slot above) so the form's
      // current state is the source of truth; max omitted when the toggle is
      // off (the backend rejects a stray max sent without its toggle).
      allow_multiple_bookings_per_participant: allowMultipleBookings,
    };
    if (allowMultipleBookings) {
      // Blank means unlimited sessions for that participant.
      payload.max_sessions_per_participant =
        editSessionTypeMaxSessionsPerParticipant.trim() === ''
          ? null
          : parseInt(editSessionTypeMaxSessionsPerParticipant, 10);
    }
    applyGroupSizeCapFields(payload);
    // OD-1102: no separate toggle column — always sent (like
    // participants_per_slot above), null disables the alert.
    payload.no_show_cancellation_limit = editSessionTypeEnableNoShowLimit
      ? parseInt(editSessionTypeNoShowCancellationLimit, 10)
      : null;
    // OD-1111: mirrors allow_multiple_bookings_per_participant above — max is
    // only ever sent (and only ever required) while the toggle is on.
    payload.allow_concurrent_moderator_bookings = editSessionTypeAllowConcurrentModeratorBookings;
    if (editSessionTypeAllowConcurrentModeratorBookings) {
      payload.max_concurrent_moderator_sessions = parseInt(
        editSessionTypeMaxConcurrentModeratorSessions,
        10,
      );
    }
    Object.assign(payload, windowCapFormToPayload(editSessionTypeWindowCap));
    if (editSessionTypeStartDate) payload.sessionTypeStartDate = editSessionTypeStartDate;
    if (editSessionTypeEndDate) payload.sessionTypeEndDate = editSessionTypeEndDate;
    if (isBookingOptionalModeratorEnabled()) {
      payload.bookingDailyStartTime = editSessionTypeDailyStartTime.trim();
      payload.bookingDailyEndTime = editSessionTypeDailyEndTime.trim();
    }
    if (editSessionTypeTimezone !== undefined) {
      payload.timezone =
        editSessionTypeTimezone || currentSessionType?.timezone || '';
    }
    if (editSessionTypeParticipantBookingRules !== undefined) {
      payload.participant_booking_rules = editSessionTypeParticipantBookingRules;
    }
    // Only PM/Admin may change scheduling rules; for other roles omit the field
    // entirely so the backend leaves the column untouched (and never 403s).
    if (isProjectManager) {
      const editRulesPayload = rulesToPayload(editSessionTypeSchedulingRules);
      payload.scheduling_rules = editRulesPayload
        ? JSON.stringify(editRulesPayload)
        : '';
      // OD-1002: PM/Admin only, same gate as scheduling_rules.
      payload.slot_generation_mode = editSessionTypeSlotMode;
    }
    payload.task_ids = editSelectedTasksForSession
      ? [editSelectedTasksForSession]
      : [];
    applyAttachmentPathFields(payload);
    return payload;
  };

  const handleUpdateSessionType = async () => {
    try {
      const projectId =
        selectedProject || projectsState.currentProject?.id || projectIdFromUrl;
      if (!projectId || projectId === 'all') {
        toast({
          title: 'Error',
          description: 'Select a project before updating a session type',
          variant: 'destructive',
        });
        return;
      }
      const currentSessionType = sessionTypes.find(
        (st: any) => st.id === editingSessionTypeId,
      );
      const existingLocationIds = currentSessionType?.location_ids || [];

      const validationError = getUpdateSessionTypeValidationError(
        existingLocationIds,
        currentSessionType,
      );
      if (validationError) {
        toast({title: 'Error', description: validationError, variant: 'destructive'});
        return;
      }

      // Persist any pending meeting-point edits before saving the session type.
      const isOffsite = editSessionTypeLocationType === 'offsite';
      const fieldsCleared = Boolean(
        editingMeetingPoint &&
          areMeetingPointFieldsCleared(
            editMeetingPointName,
            editMeetingPointStreetAddress,
          ),
      );
      if (editingMeetingPoint && !fieldsCleared && editMeetingPointName.trim()) {
        const locationUpdate = buildEditMeetingPointUpdatePayload(projectId);
        await schedulerApi.updateScheduleLocation(
          editingMeetingPoint.id,
          locationUpdate,
        );
      }

      if (!editingSessionTypeId) {
        toast({
          title: 'Error',
          description: 'No session type selected for editing',
          variant: 'destructive',
        });
        return;
      }

      const payload = buildUpdateSessionTypePayload(
        projectId,
        existingLocationIds,
        currentSessionType,
        fieldsCleared,
        isOffsite,
      );
      // Confirm before applying any session-type update.
      setPendingUpdatePayload({
        payload,
        hasFiles: editAttachedFiles && editAttachedFiles.length > 0,
        files: editAttachedFiles,
        removeAttachment:
          editExistingAttachmentPaths.length === 0 && !editAttachedFiles.length,
      });
      setShowSessionTypeUpdateConfirm(true);
    } catch (error: any) {
      console.error('Error updating session type:', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.error || 'Failed to update session type',
        variant: 'destructive',
      });
    }
  };

  // Handle disable/enable session type
  const handleDisableSessionType = async (sessionType: any) => {
    const isCurrentlyEnabled = sessionType.enabled;
    const action = isCurrentlyEnabled ? 'disable' : 're-enable';

    try {
      // If re-enabling, first clear moderators and open edit dialog
      if (!isCurrentlyEnabled) {
        // Clear moderators by updating the session type
        try {
          await schedulerApi.updateSessionType(sessionType.id, {
            moderator_ids: [],
          });

          await queryClient.invalidateQueries({
            queryKey: sessionTypesDetailedByProjectQueryKey(selectedProject),
          });

          // Reload session types to get updated data (fresh, not cache-served)
          setLoadingSessionTypes(true);
          try {
            const sessionTypesList =
              await fetchDetailedSessionTypesByProject(selectedProject, {refresh: true});
            queryClient.setQueryData(
              sessionTypesDetailedByProjectQueryKey(selectedProject),
              sessionTypesList,
            );
            const formattedSessionTypes = sessionTypesList.map(
              formatSessionTypeForDisplay,
            );
            setSessionTypes(formattedSessionTypes);

            // Collapse the updated session type
            const newExpanded = new Set(expandedSessionTypes);
            newExpanded.delete(sessionType.id);
            setExpandedSessionTypes(newExpanded);
            
            // Find the updated session type
            const updatedSessionType = formattedSessionTypes.find((st: any) => st.id === sessionType.id);
            if (updatedSessionType) {
              // Open edit dialog with moderators cleared
              toast({
                title: 'Moderators Cleared',
                description: 'Please assign moderators and save to enable the session type.',
              });
              handleEditSessionType(updatedSessionType);
            }
          } finally {
            setLoadingSessionTypes(false);
          }
          return; // Don't proceed with toggle - user must assign moderators first
        } catch (updateError: any) {
          console.error('Error clearing moderators:', updateError);
          toast({
            title: 'Error',
            description: updateError.response?.data?.error || 'Failed to clear moderators',
            variant: 'destructive',
          });
          return;
        }
      }

      // For disabling, proceed with toggle
      await schedulerApi.toggleSessionType(sessionType.id);

      toast({
        title: 'Success',
        description: `Session type ${action}d`,
      });

      await queryClient.invalidateQueries({
        queryKey: sessionTypesDetailedByProjectQueryKey(selectedProject),
      });

      // Store the session type ID to collapse it after reload
      const stIdToCollapse = sessionType.id;

      // Reload session types (fresh, not cache-served)
      setLoadingSessionTypes(true);
      try {
        const sessionTypesList =
          await fetchDetailedSessionTypesByProject(selectedProject, {refresh: true});
        queryClient.setQueryData(
          sessionTypesDetailedByProjectQueryKey(selectedProject),
          sessionTypesList,
        );
        const formattedSessionTypes = sessionTypesList.map(
          formatSessionTypeForDisplay,
        );
      setSessionTypes(formattedSessionTypes);
      
      // Collapse the updated session type
      if (stIdToCollapse) {
        const newExpanded = new Set(expandedSessionTypes);
        newExpanded.delete(stIdToCollapse);
        setExpandedSessionTypes(newExpanded);
      }
      } finally {
        setLoadingSessionTypes(false);
      }
    } catch (error: any) {
      console.error(`Error ${action}ing session type:`, error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.error || `Failed to ${action} session type`,
        variant: 'destructive',
      });
    }
  };

  const handleModeratorSearchEditChange = (
    sessionTypeId: string,
    value: string,
  ) => {
    setModeratorSearchEdit((prev) => ({...prev, [sessionTypeId]: value}));
  };

  const handleToggleEditModerator = (
    moderatorUserId: string,
    checked: boolean,
  ) => {
    setEditSelectedModeratorsForSession((prev) =>
      checked
        ? [...prev, moderatorUserId]
        : prev.filter((id: any) => String(id) !== moderatorUserId),
    );
  };

  const handleToggleCreateModerator = (
    moderatorUserId: string,
    checked: boolean,
  ) => {
    setSelectedModeratorsForSession((prev) =>
      checked
        ? [...prev, moderatorUserId]
        : prev.filter((id: any) => String(id) !== moderatorUserId),
    );
  };

  const renderCreateModeratorList = () => {
    const filteredModerators = getFilteredModeratorsForCreate();
    return (
      <div className="space-y-3">
        <div className="relative">
          <svg
            className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="m21 21-4.34-4.34" />
            <circle cx="11" cy="11" r="8" />
          </svg>
          <Input
            type="text"
            placeholder="Search moderators by name..."
            value={moderatorSearchCreate}
            onChange={(e) => setModeratorSearchCreate(e.target.value)}
            className="pl-10"
          />
        </div>
        {filteredModerators.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No moderators found
            {moderatorSearchCreate ? ' matching your search' : ' in this project'}
            .
          </p>
        ) : (
          <>
            <div className="space-y-2 max-h-[140px] overflow-y-auto">
              {filteredModerators.map((moderator: any) => {
                const moderatorUserId = String(
                  moderator.user_id || moderator.id || '',
                );
                const isChecked = isModeratorSelected(
                  selectedModeratorsForSession,
                  moderatorUserId,
                );
                const email = moderator.email || moderator.email_id || '';
                return (
                  <label
                    key={moderator.id || moderator.user_id}
                    className="flex items-center space-x-3 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="rounded w-4 h-4"
                      checked={isChecked}
                      onChange={(e) =>
                        handleToggleCreateModerator(
                          moderatorUserId,
                          e.target.checked,
                        )
                      }
                    />
                    <span className="text-sm">
                      {moderator.name}
                      {email ? ` (${email})` : ''}
                    </span>
                  </label>
                );
              })}
            </div>
            {selectedModeratorsForSession.length === 0 &&
              filteredModerators.length > 0 && (
                <p className="text-sm text-gray-400 pt-2">
                  No moderators assigned yet
                </p>
              )}
          </>
        )}
      </div>
    );
  };


  const renderEditFileUploadField = (sessionType: any, isEditing: boolean) => (
                                <div className="space-y-1.5">
                                  <Label className="text-sm font-normal text-foreground">
                                    Attach file <span className="text-muted-foreground font-normal">(optional)</span>
                                  </Label>
                                  {isEditing ? (
                                    <div className="space-y-2">
                                      {/* Show existing attachments if available */}
                                      {editExistingAttachmentPaths.length >
                                        0 && (
                                        <div className="space-y-2">
                                          {editExistingAttachmentPaths.map(
                                            (path, index) => (
                                            <div
                                              key={index}
                                              className="flex items-center justify-between p-2 bg-gray-50 rounded-md border"
                                            >
                                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <File className="h-4 w-4 text-gray-600 flex-shrink-0" />
                                                <span className="text-sm text-foreground truncate">
                                                    {path.split('/').pop() ||
                                                      'Existing attachment'}
                                                </span>
                                              </div>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  setEditExistingAttachmentPaths(
                                                    removeAt(
                                                      editExistingAttachmentPaths,
                                                      index,
                                                    ),
                                                  )
                                                }
                                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                                              >
                                                <X className="h-4 w-4" />
                                              </Button>
                                            </div>
                                            ),
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Show new file if selected */}
                                      {editAttachedFiles.length > 0 && (
                                        <div className="space-y-2">
                                          {editAttachedFiles.map(
                                            (file, index) => (
                                            <div
                                              key={index}
                                              className="flex items-center justify-between p-2 bg-gray-50 rounded-md border"
                                            >
                                              <span className="text-sm text-foreground truncate flex-1 mr-2">
                                                {file.name}
                                              </span>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  setEditAttachedFiles(
                                                    removeAt(editAttachedFiles, index),
                                                  )
                                                }
                                                className="h-8 w-8 p-0"
                                              >
                                                <X className="h-4 w-4" />
                                              </Button>
                                            </div>
                                            ),
                                          )}
                                        </div>
                                      )}

                                      {/* File upload input */}
                                      <div className="space-y-2">
                                        <label
                                          htmlFor="edit-file-upload"
                                          aria-label="Choose files"
                                          className="cursor-pointer"
                                        >
                                          <div className="flex items-center gap-2">
                                            <Button
                                              type="button"
                                              variant="outline"
                                              onClick={() => {
                                                const input =
                                                  document.getElementById(
                                                    'edit-file-upload',
                                                  ) as HTMLInputElement;
                                                input?.click();
                                              }}
                                              disabled={
                                                getFileCount(
                                                  editAttachedFiles,
                                                  editExistingAttachmentPaths,
                                                ) >= MAX_ATTACHMENT_FILES
                                              }
                                              className="bg-white"
                                            >
                                              Choose files
                                            </Button>
                                            <span className="text-sm text-muted-foreground">
                                              {getFileStatusText(
                                                editAttachedFiles,
                                                editExistingAttachmentPaths,
                                              )}
                                            </span>
                                          </div>
                                        </label>
                                        <input
                                          id="edit-file-upload"
                                          type="file"
                                          multiple
                                          onChange={(e) => {
                                            const files = Array.from(
                                              e.target.files || [],
                                            );
                                            
                                            // Validate file sizes
                                            const invalidFiles = files.filter(
                                              (file) =>
                                                !validateFileSize(file).valid,
                                            );
                                            if (invalidFiles.length > 0) {
                                              invalidFiles.forEach((file) => {
                                                const validation =
                                                  validateFileSize(file);
                                                if (!validation.valid) {
                                                  toast({
                                                    title: 'File too large',
                                                    description:
                                                      validation.error,
                                                    variant: 'destructive',
                                                  });
                                                }
                                              });
                                              // Reset input value
                                              e.target.value = '';
                                              return;
                                            }
                                            
                                            const remainingSlots =
                                              getRemainingSlots(
                                                editAttachedFiles,
                                                editExistingAttachmentPaths,
                                              );
                                            
                                            if (remainingSlots > 0) {
                                              const filesToAdd = files.slice(
                                                0,
                                                remainingSlots,
                                              );
                                              setEditAttachedFiles([
                                                ...editAttachedFiles,
                                                ...filesToAdd,
                                              ]);

                                              if (
                                                files.length > remainingSlots
                                              ) {
                                                toast({
                                                  title:
                                                    'Maximum files reached',
                                                  description: `You can only attach up to ${MAX_ATTACHMENT_FILES} files total.`,
                                                  variant: 'destructive',
                                                });
                                              }
                                            } else {
                                              toast({
                                                title: 'Maximum files reached',
                                                description: `You can only attach up to ${MAX_ATTACHMENT_FILES} files total.`,
                                                variant: 'destructive',
                                              });
                                            }
                                            // Reset input value to allow selecting the same file again
                                            e.target.value = '';
                                          }}
                                          disabled={
                                            getFileCount(
                                              editAttachedFiles,
                                              editExistingAttachmentPaths,
                                            ) >= MAX_ATTACHMENT_FILES
                                          }
                                          className="hidden"
                                          accept="*/*"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                          You can attach up to{' '}
                                          {MAX_ATTACHMENT_FILES} files total
                                          {getFileCount(
                                            editAttachedFiles,
                                            editExistingAttachmentPaths,
                                          ) > 0 && (
                                            <span className="ml-1">
                                              (
                                              {getFileCount(
                                                editAttachedFiles,
                                                editExistingAttachmentPaths,
                                              )}
                                              /{MAX_ATTACHMENT_FILES} selected)
                                            </span>
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                  ) : (
                                    <div>
                                      {(() => {
                                        // Parse email_attachment_path (it's stored as JSON string array)
                                        let attachmentPaths: string[] = [];
                                        if (sessionType.email_attachment_path) {
                                          try {
                                            const parsed =
                                              typeof sessionType.email_attachment_path ===
                                              'string'
                                                ? JSON.parse(
                                                    sessionType.email_attachment_path,
                                                  )
                                              : sessionType.email_attachment_path;
                                            attachmentPaths = Array.isArray(
                                              parsed,
                                            )
                                              ? parsed
                                              : [parsed];
                                          } catch (e) {
                                            // If parsing fails, treat as single string path
                                            console.warn('Error parsing email attachment path:', e);
                                            attachmentPaths = [
                                              sessionType.email_attachment_path,
                                            ];
                                          }
                                        }
                                        
                                        return attachmentPaths.length > 0 ? (
                                          <div className="space-y-2">
                                            {attachmentPaths.map(
                                              (path, index) => (
                                                <div
                                                  key={index}
                                                  className="flex items-center gap-2 p-2 bg-gray-50 rounded-md border"
                                                >
                                                <File className="h-4 w-4 text-gray-600 flex-shrink-0" />
                                                <span className="text-sm text-gray-700 truncate">
                                                    {path.split('/').pop() ||
                                                      'Attachment'}
                                                </span>
                                              </div>
                                              ),
                                            )}
                                          </div>
                                        ) : (
                                          <p className="text-sm text-muted-foreground">
                                            No file attached
                                          </p>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
  );

  const renderBasicInfoSection = (sessionType: any, isEditing: boolean) => (
    <>
                            {/* Basic Info */}
                            <div className="space-y-4 bg-white rounded-lg p-4">
                              <h4 className="font-semibold text-base">
                                Basic Info
                              </h4>
                              <div className="space-y-4">
                                <div className="space-y-1.5">
                                  <Label className="text-sm font-normal text-foreground">
                                    Session name <span className="text-red-500">*</span>
                                  </Label>
                                  <Input
                                    value={
                                      isEditing
                                        ? editSessionTypeName
                                        : sessionType.name
                                    }
                                    onChange={
                                      isEditing
                                        ? (e) =>
                                            setEditSessionTypeName(
                                              e.target.value,
                                            )
                                        : undefined
                                    }
                                    readOnly={!isEditing}
                                    className={
                                      isEditing
                                        ? 'bg-white'
                                        : 'bg-gray-50 text-gray-700'
                                    }
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label className="text-sm font-normal text-foreground">
                                    Session type <span className="text-red-500">*</span>
                                  </Label>
                                  {isEditing ? (
                                    <div>
                                      <ToggleGroup
                                        type="single"
                                        value={editSessionTypeLocationType}
                                        onValueChange={(value) => {
                                          if (value)
                                            setEditSessionTypeLocationType(
                                              value as 'onsite' | 'offsite',
                                            );
                                        }}
                                        className="inline-flex rounded-lg border border-input bg-transparent"
                                      >
                                        <ToggleGroupItem
                                          value="onsite"
                                          aria-label="Onsite"
                                          className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                                        >
                                          Onsite
                                        </ToggleGroupItem>
                                        <ToggleGroupItem
                                          value="offsite"
                                          aria-label="Offsite"
                                          className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                                        >
                                          Offsite
                                        </ToggleGroupItem>
                                      </ToggleGroup>
                                    </div>
                                  ) : (
                                    <div className="text-sm text-gray-700">
                                      {(sessionType.session_type as string) ||
                                        'onsite'}
                                    </div>
                                  )}
                                </div>
                                <div className="space-y-1.5">
                                  <Label className="text-sm font-normal text-foreground">
                                    Job <span className="text-red-500">*</span>
                                  </Label>
                                  {/* Job is always read-only (display only). */}
                                  <Input
                                    value={getJobDisplayName(
                                      sessionType.job_id,
                                      sessionType.job_name,
                                    )}
                                    readOnly
                                    className="bg-gray-50 text-gray-700"
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label className="text-sm font-normal text-foreground">
                                    Session description <span className="text-muted-foreground font-normal">(optional)</span>
                                  </Label>
                                  <Textarea
                                    value={
                                      isEditing
                                        ? editSessionTypeDescription
                                        : sessionType.description || ''
                                    }
                                    onChange={
                                      isEditing
                                        ? (e) =>
                                            setEditSessionTypeDescription(
                                              e.target.value,
                                            )
                                        : undefined
                                    }
                                    readOnly={!isEditing}
                                    className={
                                      isEditing
                                        ? 'bg-white resize-none'
                                        : 'bg-gray-50 text-gray-700 resize-none'
                                    }
                                    rows={3}
                                  />
                                </div>
                                {renderEditFileUploadField(sessionType, isEditing)}
                              </div>
                            </div>
    </>
  );

  const renderSchedulingRow1 = (sessionType: any, isEditing: boolean) => (
    <>
                                {isProjectManager && (
                                  <SlotGenerationModeField
                                    idPrefix={`edit-slot-mode-${sessionType.id}`}
                                    value={
                                      isEditing
                                        ? editSessionTypeSlotMode
                                        : slotGenerationModeFromSessionType(sessionType)
                                    }
                                    onChange={setEditSessionTypeSlotMode}
                                    disabled={!isEditing}
                                  />
                                )}

                                {/* Row 1: Start Date and End Date */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-1.5">
                                    <Label className="text-sm font-normal text-foreground">
                                      Start date <span className="text-red-500">*</span>
                                    </Label>
                                    {isEditing ? (
                                      <CalendarDateInput
                                        name="edit-session-type-start-date"
                                        value={editSessionTypeStartDate}
                                        onValueChange={setEditSessionTypeStartDate}
                                        invalidRef={editStartDateInvalidRef}
                                        min={format(new Date(), 'yyyy-MM-dd')}
                                      />
                                    ) : (
                                      <Input
                                        type="text"
                                        value={formatSessionTypeCalendarDate(
                                          sessionType.sessionTypeStartDate,
                                          'yyyy-MM-dd',
                                        )}
                                        readOnly
                                        className="bg-gray-50 text-gray-700"
                                      />
                                    )}
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label className="text-sm font-normal text-foreground">
                                      End date <span className="text-red-500">*</span>
                                    </Label>
                                    {isEditing ? (
                                      <CalendarDateInput
                                        name="edit-session-type-end-date"
                                        value={editSessionTypeEndDate}
                                        onValueChange={setEditSessionTypeEndDate}
                                        invalidRef={editEndDateInvalidRef}
                                        minFromStartDate={
                                          editSessionTypeStartDate ||
                                          format(new Date(), 'yyyy-MM-dd')
                                        }
                                      />
                                    ) : (
                                      <Input
                                        type="text"
                                        value={formatSessionTypeCalendarDate(
                                          sessionType.sessionTypeEndDate,
                                          'yyyy-MM-dd',
                                        )}
                                        readOnly
                                        className="bg-gray-50 text-gray-700"
                                      />
                                    )}
                                  </div>
                                </div>

                                {isBookingOptionalModeratorEnabled() && (
                                  <SessionTypeDailyTimeRangeField
                                    startTime={
                                      isEditing
                                        ? editSessionTypeDailyStartTime
                                        : sessionType.bookingDailyStartTime ||
                                          ''
                                    }
                                    endTime={
                                      isEditing
                                        ? editSessionTypeDailyEndTime
                                        : sessionType.bookingDailyEndTime || ''
                                    }
                                    onTimesChange={({start, end}) => {
                                      setEditSessionTypeDailyStartTime(start);
                                      setEditSessionTypeDailyEndTime(end);
                                    }}
                                    disabled={!isEditing}
                                  />
                                )}

                                {isProjectManager && (
                                  <SchedulingRulesFields
                                    idPrefix={`edit-${sessionType.id_guid ?? sessionType.id ?? 'st'}`}
                                    value={
                                      isEditing
                                        ? editSessionTypeSchedulingRules
                                        : rulesFromSessionType(sessionType)
                                    }
                                    onChange={setEditSessionTypeSchedulingRules}
                                    disabled={!isEditing}
                                  />
                                )}
    </>
  );

  const renderSchedulingRow2 = (sessionType: any, isEditing: boolean) => (
    <>
                                {/* Row 2: Minimum notice before start and Duration */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-1.5">
                                    <Label className="text-sm font-normal text-foreground flex items-center gap-1.5">
                                      Self-Booking Lead Time <span className="text-muted-foreground font-normal">(optional)</span>
                                      <InfoHint text={SELF_BOOKING_LEAD_TIME_HINT} />
                                    </Label>
                                    <div className="flex gap-2">
                                      <Input
                                        type="number"
                                        value={
                                          isEditing
                                            ? editSessionTypeMinimumNotice
                                            : (() => {
                                                const minimumNotice =
                                                  sessionType.minimum_notice;
                                                if (
                                                  !minimumNotice ||
                                                  minimumNotice === 0
                                                )
                                                  return '';
                                                if (
                                                  minimumNotice >= 60 &&
                                                  minimumNotice % 60 === 0
                                                ) {
                                                  return (
                                                    minimumNotice / 60
                                                  ).toString();
                                          } else {
                                            return minimumNotice.toString();
                                          }
                                              })()
                                        }
                                        placeholder={
                                          !isEditing &&
                                          (!sessionType.minimum_notice ||
                                            sessionType.minimum_notice === 0)
                                            ? '0'
                                            : ''
                                        }
                                        onChange={
                                          isEditing
                                            ? (e) =>
                                                setEditSessionTypeMinimumNotice(
                                                  e.target.value,
                                                )
                                            : undefined
                                        }
                                        readOnly={!isEditing}
                                        className={
                                          isEditing
                                            ? 'flex-1 bg-white'
                                            : 'flex-1 bg-gray-50 text-gray-700'
                                        }
                                      />
                                      {isEditing ? (
                                        <Select 
                                          value={
                                            editSessionTypeMinimumNoticeUnit
                                          }
                                          onValueChange={
                                            setEditSessionTypeMinimumNoticeUnit
                                          }
                                        >
                                          <SelectTrigger className="w-[120px] bg-white">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="hours">
                                              hours
                                            </SelectItem>
                                            <SelectItem value="minutes">
                                              minutes
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <Select 
                                          value={(() => {
                                            const minimumNotice =
                                              sessionType.minimum_notice || 0;
                                            return minimumNotice >= 60 &&
                                              minimumNotice % 60 === 0
                                              ? 'hours'
                                              : 'minutes';
                                          })()}
                                          disabled
                                        >
                                          <SelectTrigger className="w-[120px] bg-gray-50 text-gray-700">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="hours">
                                              hours
                                            </SelectItem>
                                            <SelectItem value="minutes">
                                              minutes
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      )}
                                    </div>
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label className="text-sm font-normal text-foreground">
                                      Duration <span className="text-red-500">*</span>
                                    </Label>
                                    <div className="flex gap-2">
                                      {isEditing ? (
                                        <>
                                          <Input
                                            type="number"
                                            placeholder=""
                                            value={editSessionTypeDuration}
                                            onChange={(e) =>
                                              setEditSessionTypeDuration(
                                                e.target.value,
                                              )
                                            }
                                            className="flex-1 bg-white"
                                            min="0"
                                          />
                                          <Select 
                                            value={editSessionTypeDurationUnit}
                                            onValueChange={
                                              setEditSessionTypeDurationUnit
                                            }
                                          >
                                            <SelectTrigger className="w-[120px] bg-white">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="hours">
                                                hours
                                              </SelectItem>
                                              <SelectItem value="minutes">
                                                minutes
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </>
                                      ) : (
                                        <>
                                          <Input
                                            type="number"
                                            value={(() => {
                                              const durationMinutes =
                                                sessionType.duration || 0;
                                              if (
                                                durationMinutes >= 60 &&
                                                durationMinutes % 60 === 0
                                              ) {
                                                return (
                                                  durationMinutes / 60
                                                ).toString();
                                              } else {
                                                return durationMinutes.toString();
                                              }
                                            })()}
                                            readOnly
                                            className="flex-1 bg-gray-50 text-gray-700"
                                          />
                                          <Select 
                                            value={(() => {
                                              const durationMinutes =
                                                sessionType.duration || 0;
                                              return durationMinutes >= 60 &&
                                                durationMinutes % 60 === 0
                                                ? 'hours'
                                                : 'minutes';
                                            })()}
                                            disabled
                                          >
                                            <SelectTrigger className="w-[120px] bg-gray-50 text-gray-700">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="hours">
                                                hours
                                              </SelectItem>
                                              <SelectItem value="minutes">
                                                minutes
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
    </>
  );

  const renderSchedulingRow3 = (sessionType: any, isEditing: boolean) => (
    <>
                                {/* Row 3: Buffer before session and Buffer after session */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-1.5">
                                    <Label className="text-sm font-normal text-foreground">
                                      Buffer time before session <span className="text-muted-foreground font-normal">(optional)</span>
                                    </Label>
                                    <div className="flex gap-2">
                                      {isEditing ? (
                                        <>
                                          <Input
                                            type="number"
                                            placeholder=""
                                            value={editSessionTypeBufferBefore}
                                            onChange={(e) =>
                                              setEditSessionTypeBufferBefore(
                                                e.target.value,
                                              )
                                            }
                                            className="flex-1 bg-white"
                                            min="0"
                                          />
                                          <Select 
                                            value={
                                              editSessionTypeBufferBeforeUnit
                                            }
                                            onValueChange={
                                              setEditSessionTypeBufferBeforeUnit
                                            }
                                          >
                                            <SelectTrigger className="w-[120px] bg-white">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="hours">
                                                hours
                                              </SelectItem>
                                              <SelectItem value="minutes">
                                                minutes
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </>
                                      ) : (
                                        <>
                                          <Input
                                            type="number"
                                            value={(() => {
                                              const bufferMinutes =
                                                sessionType.buffer_before;
                                              if (
                                                !bufferMinutes ||
                                                bufferMinutes === 0
                                              )
                                                return '';
                                              if (
                                                bufferMinutes >= 60 &&
                                                bufferMinutes % 60 === 0
                                              ) {
                                                return (
                                                  bufferMinutes / 60
                                                ).toString();
                                              } else {
                                                return bufferMinutes.toString();
                                              }
                                            })()}
                                            placeholder={
                                              !sessionType.buffer_before ||
                                              sessionType.buffer_before === 0
                                                ? '0'
                                                : ''
                                            }
                                            readOnly
                                            className="flex-1 bg-gray-50 text-gray-700"
                                          />
                                          <Select 
                                            value={(() => {
                                              const bufferMinutes =
                                                sessionType.buffer_before;
                                              if (
                                                !bufferMinutes ||
                                                bufferMinutes === 0
                                              )
                                                return 'minutes';
                                              return bufferMinutes >= 60 &&
                                                bufferMinutes % 60 === 0
                                                ? 'hours'
                                                : 'minutes';
                                            })()}
                                            disabled
                                          >
                                            <SelectTrigger className="w-[120px] bg-gray-50 text-gray-700">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="hours">
                                                hours
                                              </SelectItem>
                                              <SelectItem value="minutes">
                                                minutes
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label className="text-sm font-normal text-foreground">
                                      Buffer time after session <span className="text-muted-foreground font-normal">(optional)</span>
                                    </Label>
                                    <div className="flex gap-2">
                                      {isEditing ? (
                                        <>
                                          <Input
                                            type="number"
                                            placeholder=""
                                            value={editSessionTypeBufferAfter}
                                            onChange={(e) =>
                                              setEditSessionTypeBufferAfter(
                                                e.target.value,
                                              )
                                            }
                                            className="flex-1 bg-white"
                                            min="0"
                                          />
                                          <Select 
                                            value={
                                              editSessionTypeBufferAfterUnit
                                            }
                                            onValueChange={
                                              setEditSessionTypeBufferAfterUnit
                                            }
                                          >
                                            <SelectTrigger className="w-[120px] bg-white">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="hours">
                                                hours
                                              </SelectItem>
                                              <SelectItem value="minutes">
                                                minutes
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </>
                                      ) : (
                                        <>
                                          <Input
                                            type="number"
                                            value={(() => {
                                              const bufferMinutes =
                                                sessionType.buffer_after;
                                              if (
                                                !bufferMinutes ||
                                                bufferMinutes === 0
                                              )
                                                return '';
                                              if (
                                                bufferMinutes >= 60 &&
                                                bufferMinutes % 60 === 0
                                              ) {
                                                return (
                                                  bufferMinutes / 60
                                                ).toString();
                                              } else {
                                                return bufferMinutes.toString();
                                              }
                                            })()}
                                            placeholder={
                                              !sessionType.buffer_after ||
                                              sessionType.buffer_after === 0
                                                ? '0'
                                                : ''
                                            }
                                            readOnly
                                            className="flex-1 bg-gray-50 text-gray-700"
                                          />
                                          <Select 
                                            value={(() => {
                                              const bufferMinutes =
                                                sessionType.buffer_after;
                                              if (
                                                !bufferMinutes ||
                                                bufferMinutes === 0
                                              )
                                                return 'minutes';
                                              return bufferMinutes >= 60 &&
                                                bufferMinutes % 60 === 0
                                                ? 'hours'
                                                : 'minutes';
                                            })()}
                                            disabled
                                          >
                                            <SelectTrigger className="w-[120px] bg-gray-50 text-gray-700">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="hours">
                                                hours
                                              </SelectItem>
                                              <SelectItem value="minutes">
                                                minutes
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
    </>
  );

  const renderParticipantsPerSlotField = (sessionType: any, isEditing: boolean) => {
    // OD-1002 / AC3: in Moderator Availability mode capacity is derived from the
    // number of available moderators, so Participants per Slot is disabled & greyed.
    // The value is retained in state so switching back restores it (AC8).
    const participantsDisabled = isModeratorAvailabilityMode(
      isEditing
        ? editSessionTypeSlotMode
        : slotGenerationModeFromSessionType(sessionType),
    );
    return (
    <>
                                {/* Participants per Slot (per-slot flat cap) */}
                                <div className="space-y-1.5">
                                  <Label className="flex items-center gap-1.5 text-sm font-normal text-foreground">
                                    Participants per Slot <span className="text-muted-foreground font-normal">(optional)</span>
                                    <InfoHint text={PARTICIPANTS_PER_SLOT_HINT} />
                                  </Label>
                                  {isEditing ? (
                                    <Input
                                      type="number"
                                      min="1"
                                      placeholder="Enter participant limit per slot"
                                      value={editSessionTypeParticipantsPerSlot}
                                      disabled={participantsDisabled}
                                      onChange={(e) =>
                                        setEditSessionTypeParticipantsPerSlot(
                                          e.target.value,
                                        )
                                      }
                                      className={participantsDisabled ? 'bg-gray-100 text-gray-400' : 'bg-white'}
                                    />
                                  ) : (
                                    <Input
                                      readOnly
                                      value={
                                        sessionType.participants_per_slot != null
                                          ? String(sessionType.participants_per_slot)
                                          : ''
                                      }
                                      placeholder="No limit"
                                      className="bg-gray-50 text-gray-700"
                                    />
                                  )}
                                </div>
    </>
    );
  };

  const renderSchedulingSection = (sessionType: any, isEditing: boolean) => (
    <>
                            {/* Scheduling Window & Rules */}
                            <div className="space-y-4 bg-white rounded-lg p-4">
                               <h4 className="font-semibold text-base">
                                 Scheduling Window & Rules
                               </h4>
                              <div className="space-y-4">
                                {renderSchedulingRow1(sessionType, isEditing)}
                                {renderSchedulingRow2(sessionType, isEditing)}
                                {renderSchedulingRow3(sessionType, isEditing)}
                                {renderParticipantsPerSlotField(sessionType, isEditing)}

                              </div>
                            </div>
    </>
  );

  // OD-1097 (+ OD-1102 / OD-1111, added alongside as those stories land): booking
  // rules independent of the scheduling window — each is off/unset by default so
  // existing session types are unaffected until a PM/Admin opts in.
  const renderBookingRulesSection = (sessionType: any, isEditing: boolean) => {
    const allowConcurrentModeratorBookings = resolveBookingRuleToggle(
      isEditing,
      editSessionTypeAllowConcurrentModeratorBookings,
      sessionType.allow_concurrent_moderator_bookings,
    );
    const maxConcurrentModeratorSessions = resolveBookingRuleFieldValue(
      isEditing,
      editSessionTypeMaxConcurrentModeratorSessions,
      sessionType.max_concurrent_moderator_sessions,
    );
    const allowMultiBooking = resolveBookingRuleToggle(
      isEditing,
      editSessionTypeAllowMultiBooking,
      sessionType.allow_multiple_bookings_per_participant,
    );
    const maxSessionsPerParticipant = resolveBookingRuleFieldValue(
      isEditing,
      editSessionTypeMaxSessionsPerParticipant,
      sessionType.max_sessions_per_participant,
    );
    const capGroupBookingSize = resolveBookingRuleToggle(
      isEditing,
      editSessionTypeCapGroupBookingSize,
      sessionType.cap_group_booking_size,
    );
    const maxGroupSize = resolveBookingRuleFieldValue(
      isEditing,
      editSessionTypeMaxGroupSize,
      sessionType.max_group_size,
    );
    const enableNoShowLimit = resolveBookingRuleToggleFromPresence(
      isEditing,
      editSessionTypeEnableNoShowLimit,
      sessionType.no_show_cancellation_limit,
    );
    const noShowCancellationLimit = resolveBookingRuleFieldValue(
      isEditing,
      editSessionTypeNoShowCancellationLimit,
      sessionType.no_show_cancellation_limit,
    );
    return (
      <div className="space-y-4 bg-white rounded-lg p-4">
        <div>
          <h4 className="font-semibold text-base">Booking Rules</h4>
          <p className="text-sm text-muted-foreground mt-1">
            Opt-in behaviours that override standard one-slot, one-booking limits.
          </p>
        </div>
        <div className="divide-y divide-gray-100">
          <BookingRuleRow
            id={`allow-concurrent-moderator-${sessionType.id}`}
            title="Allow concurrent moderator bookings"
            description="Moderator can run more than one session at once."
            hint={CONCURRENT_MODERATOR_HINT}
            checked={allowConcurrentModeratorBookings}
            onCheckedChange={setEditSessionTypeAllowConcurrentModeratorBookings}
            isEditing={isEditing}
            fieldLabel="Maximum concurrent sessions"
            fieldValue={maxConcurrentModeratorSessions}
            onFieldChange={setEditSessionTypeMaxConcurrentModeratorSessions}
            fieldPlaceholder="e.g. 2"
            fieldMin="2"
          />
          <BookingRuleRow
            id={`allow-multi-booking-${sessionType.id}`}
            title="Allow multiple bookings per participant"
            description="A participant can hold more than one session for this job."
            hint={ALLOW_MULTI_BOOKING_HINT}
            checked={resolveAllowMultipleBookings(
              editSessionTypeWindowCap.enabled,
              allowMultiBooking,
            )}
            onCheckedChange={setEditSessionTypeAllowMultiBooking}
            isEditing={isEditing}
            toggleDisabled={editSessionTypeWindowCap.enabled}
            fieldLabel="Max sessions per participant"
            fieldValue={maxSessionsPerParticipant}
            onFieldChange={setEditSessionTypeMaxSessionsPerParticipant}
            fieldPlaceholder="e.g. 10"
            fieldMin="2"
          />
          <BookingRuleRow
            id={`cap-group-booking-size-${sessionType.id}`}
            title="Cap group booking size"
            description="Set a maximum number of participants per group booking. If unchecked, group booking size is unlimited."
            hint={GROUP_SIZE_CAP_HINT}
            checked={capGroupBookingSize}
            onCheckedChange={setEditSessionTypeCapGroupBookingSize}
            isEditing={isEditing}
            control="checkbox"
            fieldLabel="Maximum group size"
            fieldValue={maxGroupSize}
            onFieldChange={setEditSessionTypeMaxGroupSize}
            fieldPlaceholder="e.g. 3"
            fieldMin="2"
          />

          <BookingRuleRow
            id={`enable-no-show-limit-${sessionType.id}`}
            title="Flag participants after repeated no-shows or cancellations"
            description="Alert the PM when a participant reaches the set number of no-shows or cancellations."
            hint={NO_SHOW_LIMIT_HINT}
            checked={enableNoShowLimit}
            onCheckedChange={setEditSessionTypeEnableNoShowLimit}
            isEditing={isEditing}
            fieldLabel="No-show / cancellation limit"
            fieldValue={noShowCancellationLimit}
            onFieldChange={setEditSessionTypeNoShowCancellationLimit}
            fieldPlaceholder="e.g. 3"
            fieldMin="1"
          />
          <ParticipantWindowCapRule
            idPrefix={`window-cap-${sessionType.id}`}
            value={
              isEditing
                ? editSessionTypeWindowCap
                : windowCapFormFromBookingRules(
                    bookingRulesFromSessionType(sessionType),
                  )
            }
            onChange={setEditSessionTypeWindowCap}
            isEditing={isEditing}
          />
        </div>
      </div>
    );
  };

  const renderMeetingPointSection = (sessionType: any, isEditing: boolean) => {
    // Pending edits to a meeting point live in local state until the session
    // type is saved, so the row shows them rather than the stored values.
    const withPendingEdits = (loc: any) => {
      const pending =
        editingMeetingPoint &&
        editingMeetingPoint.id === loc.id &&
        editingSessionTypeId === sessionType.id;
      if (!pending) return loc;
      return {
        ...loc,
        name: editMeetingPointName,
        street_address: editMeetingPointStreetAddress,
        city: editMeetingPointCity,
        state: editMeetingPointState,
        zip_code: editMeetingPointZipCode,
        country: editMeetingPointCountry,
        instructions: editMeetingPointInstructions,
      };
    };

    const meetingPoints = (sessionType.locations || []).map(withPendingEdits);

    return (
      <>
        <MeetingPointsList
          meetingPoints={meetingPoints}
          isEditing={isEditing}
          onAdd={() => {
            clearMeetingPointFields();
            clearMeetingPointDraftFields();
            setIsEditingMeetingPoint(false);
            // Adding is not editing: a meeting point left over from a previous
            // edit would otherwise still be the one the primary-guard reads.
            setEditingMeetingPoint(null);
            setShowAddMeetingPointDialog(true);
          }}
          onEdit={(point) => handleEditMeetingPoint(point)}
          onMakePrimary={(point) => handleMakeMeetingPointPrimary(sessionType, point)}
          onToggleActive={(point) => handleToggleMeetingPointActive(sessionType, point)}
          onRemove={(point) => handleRemoveMeetingPoint(sessionType, point)}
          onMove={(point, direction) =>
            handleMoveMeetingPoint(sessionType, point, direction)
          }
        />
        <div className="bg-white rounded-lg px-4 pb-4">
          <SessionTimezoneField
            timezone={isEditing ? editSessionTypeTimezone : sessionType.timezone || ''}
            onTimezoneChange={handleSessionTimezoneManualChange}
            showHelper={isEditing}
            disabled={!isEditing}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Resolved from the primary meeting point. Every meeting point on this session
            type must be in this timezone.
          </p>
        </div>
        {/* OD-1325 AC-8: the meeting-point entries of the session type's log,
            inline beside the list they describe. */}
        <div className="bg-white rounded-lg px-4 pb-4">
          <MeetingPointHistory sessionTypeId={String(sessionType.id)} />
        </div>
      </>
    );
  };

  const renderEditModeratorList = (sessionType: any) => {
                                    const filteredModerators =
                                      getFilteredModeratorsForEdit(
                                        sessionType.id,
                                      );

                                    return (
                                      <div className="space-y-3">
                                        {/* Search Input */}
                                        <div className="relative">
                                          <svg
                                            className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                          >
                                            <path d="m21 21-4.34-4.34" />
                                            <circle cx="11" cy="11" r="8" />
                                          </svg>
                                          <Input
                                            type="text"
                                            placeholder="Search moderators by name..."
                                            value={
                                              moderatorSearchEdit[
                                                sessionType.id
                                              ] || ''
                                            }
                                            onChange={(e) =>
                                              handleModeratorSearchEditChange(
                                                sessionType.id,
                                                e.target.value,
                                              )
                                            }
                                            className="pl-10"
                                          />
                                        </div>

                                        {/* Moderator List - Scrollable, shows 5 items at a time */}
                                        {filteredModerators.length === 0 ? (
                                          <p className="text-sm text-muted-foreground">
                                            No moderators found
                                            {moderatorSearchEdit[sessionType.id]
                                              ? ' matching your search'
                                              : ' in this project'}
                                            .
                                          </p>
                                      ) : (
                                        <>
                                            <div className="space-y-2 max-h-[140px] overflow-y-auto">
                                              {filteredModerators.map(
                                                (moderator: any) => {
                                                  const moderatorUserId =
                                                    String(
                                                      moderator.user_id ||
                                                        moderator.id ||
                                                        '',
                                                    );
                                                  const isChecked =
                                                    isModeratorSelected(
                                                      editSelectedModeratorsForSession,
                                                      moderatorUserId,
                                                    );
                                                  const email =
                                                    moderator.email ||
                                                    moderator.email_id ||
                                                    '';
                                          return (
                                            <label
                                                      key={
                                                        moderator.id ||
                                                        moderator.user_id
                                                      }
                                              className="flex items-center space-x-3 cursor-pointer"
                                            >
                                              <input
                                                type="checkbox"
                                                className="rounded w-4 h-4"
                                                checked={isChecked}
                                                onChange={(e) =>
                                                  handleToggleEditModerator(
                                                    moderatorUserId,
                                                    e.target.checked,
                                                  )
                                                }
                                              />
                                              <span className="text-sm">
                                                        {moderator.name}
                                                        {email
                                                          ? ` (${email})`
                                                          : ''}
                                              </span>
                                            </label>
                                          );
                                                },
                                              )}
                                            </div>
                                            {editSelectedModeratorsForSession.length ===
                                              0 &&
                                              filteredModerators.length > 0 && (
                                                <p className="text-sm text-gray-400 pt-2">
                                                  No moderators assigned yet
                                                </p>
                                          )}
                                        </>
                                        )}
                                      </div>
                                      );
  };

  const renderAssignModeratorsSection = (sessionType: any, isEditing: boolean) => (
    <>
                            {/* Assign Moderators */}
                            <div className="space-y-4 bg-white rounded-lg p-4">
                              <div>
                                <h4 className="font-semibold text-base mb-1">
                                  Assign Moderators <span className="text-red-500">*</span>
                                  <span className="ml-1 inline-flex align-middle">
                                    <InfoHint text={MODERATOR_AVAILABILITY_SYNC_TOOLTIP} />
                                  </span>
                                </h4>
                                <p className="text-sm font-normal text-foreground">
                                  Select moderators for this session type
                                </p>
                              </div>
                                {isEditing ? (
                                  renderEditModeratorList(sessionType)
                                ) : (
                                  <>
                                    {(() => {
                                    const startDate =
                                      sessionType.sessionTypeStartDate ||
                                      sessionType.schedulingStartDate ||
                                      sessionType.start_date ||
                                      null;
                                    const endDate =
                                      sessionType.sessionTypeEndDate ||
                                      sessionType.schedulingEndDate ||
                                      sessionType.end_date ||
                                      null;
                                    const selectedModeratorIds = (
                                      sessionType.moderator_ids || []
                                    ).map((id: any) => String(id));
                                    const filteredModerators =
                                      getFilteredModeratorsForReadOnly(
                                        startDate,
                                        endDate,
                                        selectedModeratorIds,
                                      );
                                      
                                      if (filteredModerators.length === 0) {
                                        return (
                                        <p className="text-sm text-muted-foreground">
                                          No moderators available in this
                                          project.
                                        </p>
                                        );
                                      }
                                      
                                      return (
                                        <>
                                        <div className="space-y-2 max-h-[140px] overflow-y-auto">
                                          {filteredModerators.map(
                                            (moderator: any) => {
                                              const moderatorUserId = String(
                                                moderator.user_id ||
                                                  moderator.id ||
                                                  '',
                                              );
                                              const isChecked =
                                                sessionType.moderator_ids &&
                                                sessionType.moderator_ids.includes(
                                                  String(
                                                    moderator.user_id ||
                                                      moderator.id,
                                                  ),
                                                );
                                              const email =
                                                moderator.email ||
                                                moderator.email_id ||
                                                '';
                                            return (
                                                <div
                                                  key={
                                                    moderator.id ||
                                                    moderator.user_id
                                                  }
                                                  className="flex items-center space-x-3"
                                                >
                                                  <input
                                                    type="checkbox"
                                                    className="rounded w-4 h-4"
                                                    checked={isChecked}
                                                    disabled
                                                  />
                                                <span className="text-sm">
                                                    {moderator.name}
                                                    {email ? ` (${email})` : ''}
                                                </span>
                                              </div>
                                            );
                                            },
                                          )}
                                        </div>
                                        {(!sessionType.moderator_ids ||
                                          sessionType.moderator_ids.length ===
                                            0) &&
                                          filteredModerators.length > 0 && (
                                            <p className="text-sm text-gray-400 pt-2">
                                              No moderators assigned yet
                                            </p>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </>
                                )}
                            </div>
    </>
  );

  const renderAssignTasksSection = (sessionType: any, isEditing: boolean) => (
    <>
                            {/* Assign Tasks */}
                            <div className="space-y-4 bg-white rounded-lg p-4">
                              <div>
                                <h4 className="font-semibold text-base mb-1">
                                  Assign Task <span className="text-red-500">*</span>
                                </h4>
                                <p className="text-sm font-normal text-foreground">
                                  Select one task for this session type
                                </p>
                              </div>
                              <div className="space-y-2">
                                {isEditing ? (
                                  <>
                                    {availableTasks.length === 0 ? (
                                      <p className="text-sm text-muted-foreground">
                                        No tasks available.
                                      </p>
                                    ) : (
                                      <>
                                        {availableTasks.map((task) => {
                                          const isSelected =
                                            editSelectedTasksForSession === task.id;
                                          return (
                                            <label
                                              key={task.id}
                                              className="flex items-center space-x-3 cursor-pointer"
                                            >
                                              <input
                                                type="radio"
                                                name={`assign-task-edit-${sessionType.id}`}
                                                className="w-4 h-4"
                                                checked={isSelected}
                                                onChange={() => {
                                                  setEditSelectedTasksForSession(task.id);
                                                }}
                                              />
                                              <span className="text-sm">
                                                {task.title}
                                              </span>
                                            </label>
                                          );
                                        })}
                                        {!editSelectedTasksForSession &&
                                          availableTasks.length > 0 && (
                                            <p className="text-sm text-gray-400">
                                              No task assigned yet
                                            </p>
                                        )}
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {availableTasks.length === 0 ? (
                                      <p className="text-sm text-muted-foreground">
                                        No tasks available.
                                      </p>
                                    ) : (
                                      <>
                                        {availableTasks.map((task: any) => {
                                          const taskId = task.id || task;
                                          const taskTitle = task.title || task;
                                          // For radio buttons, check if this is the selected task
                                          const taskIds = sessionType.task_ids || [];
                                          const selectedTaskId = Array.isArray(taskIds) && taskIds.length > 0 ? taskIds[0] : (typeof taskIds === 'string' ? taskIds : null);
                                          const isSelected = selectedTaskId === taskId;
                                          return (
                                            <div
                                              key={taskId}
                                              className="flex items-center space-x-3"
                                            >
                                              <input
                                                type="radio"
                                                className="w-4 h-4"
                                                checked={isSelected}
                                                disabled
                                              />
                                              <span className="text-sm">
                                                {taskTitle}
                                              </span>
                                            </div>
                                          );
                                        })}
                                        {(!sessionType.task_ids ||
                                          (Array.isArray(sessionType.task_ids) && sessionType.task_ids.length === 0) ||
                                          (!Array.isArray(sessionType.task_ids) && !sessionType.task_ids)) &&
                                          availableTasks.length > 0 && (
                                            <p className="text-sm text-gray-400">
                                              No task assigned yet
                                            </p>
                                        )}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
    </>
  );

  const handleCancelEditSessionType = (sessionType: any) => {
    editStartDateInvalidRef.current = false;
    editEndDateInvalidRef.current = false;
    setEditingSessionTypeId(null);
    setModeratorSearchEdit((prev) => {
      const updated = {...prev};
      delete updated[sessionType.id];
      return updated;
    });
    const originalSessionType = sessionTypes.find(
      (st: any) => st.id === sessionType.id,
    );
    if (originalSessionType) {
      setEditSessionTypeName(originalSessionType.name);
      setEditSessionTypeJobId(
        sessionTypeJobIdString(originalSessionType.job_id),
      );
      setEditSessionTypeDescription(originalSessionType.description || '');
      const duration = minutesToValueUnit(originalSessionType.duration || 60);
      setEditSessionTypeDuration(duration.value);
      setEditSessionTypeDurationUnit(duration.unit);
      setEditSessionTypeStartDate(
        formatYmdOrEmpty(originalSessionType.sessionTypeStartDate),
      );
      setEditSessionTypeEndDate(
        formatYmdOrEmpty(originalSessionType.sessionTypeEndDate),
      );
      setEditSessionTypeSchedulingRules(
        withStableRuleKeys(rulesFromSessionType(originalSessionType)),
      );
      setEditSessionTypeSlotMode(
        slotGenerationModeFromSessionType(originalSessionType),
      );
      setEditSessionTypeDailyStartTime(
        bookingDailyTimeToInput(
          originalSessionType.bookingDailyStartTime ??
            originalSessionType.booking_daily_start_time,
        ),
      );
      setEditSessionTypeDailyEndTime(
        bookingDailyTimeToInput(
          originalSessionType.bookingDailyEndTime ??
            originalSessionType.booking_daily_end_time,
        ),
      );
      const minimumNotice = minutesToValueUnit(
        originalSessionType.minimum_notice || 0,
      );
      setEditSessionTypeMinimumNotice(minimumNotice.value);
      setEditSessionTypeMinimumNoticeUnit(minimumNotice.unit);
      const bufferBefore = minutesToValueUnit(
        originalSessionType.buffer_before || 0,
      );
      setEditSessionTypeBufferBefore(bufferBefore.value);
      setEditSessionTypeBufferBeforeUnit(bufferBefore.unit);
      const bufferAfter = minutesToValueUnit(
        originalSessionType.buffer_after || 0,
      );
      setEditSessionTypeBufferAfter(bufferAfter.value);
      setEditSessionTypeBufferAfterUnit(bufferAfter.unit);
      setEditSessionTypeTimezone(originalSessionType.timezone || '');
      setEditSessionTypeParticipantBookingRules(
        originalSessionType.participant_booking_rules || false,
      );
      const originalBookingRules = bookingRulesFromSessionType(originalSessionType);
      setEditSessionTypeAllowMultiBooking(
        originalBookingRules.allowMultipleBookingsPerParticipant,
      );
      setEditSessionTypeMaxSessionsPerParticipant(
        originalBookingRules.maxSessionsPerParticipant != null
          ? String(originalBookingRules.maxSessionsPerParticipant)
          : '',
      );
      setEditSessionTypeEnableNoShowLimit(
        originalBookingRules.noShowCancellationLimit != null,
      );
      setEditSessionTypeNoShowCancellationLimit(
        originalBookingRules.noShowCancellationLimit != null
          ? String(originalBookingRules.noShowCancellationLimit)
          : '',
      );
      setEditSessionTypeAllowConcurrentModeratorBookings(
        originalBookingRules.allowConcurrentModeratorBookings,
      );
      setEditSessionTypeMaxConcurrentModeratorSessions(
        originalBookingRules.maxConcurrentModeratorSessions != null
          ? String(originalBookingRules.maxConcurrentModeratorSessions)
          : '',
      );
      setEditSessionTypeWindowCap(windowCapFormFromBookingRules(originalBookingRules));
      setEditSelectedLocationForSession(
        firstLocationIdString(originalSessionType),
      );
      // Same active-roster filter as the initial edit-open path (OD-1065), with
      // the same empty-roster guard so this can't wipe ids while still loading.
      const activeModeratorIdsForReset =
        projectModerators.length > 0
          ? new Set(projectModerators.map((m: any) => String(m.user_id || m.id)))
          : null;
      const moderatorIds = (originalSessionType.moderator_ids || [])
        .map(String)
        .filter((id: string) => !activeModeratorIdsForReset || activeModeratorIdsForReset.has(id));
      setEditSelectedModeratorsForSession(moderatorIds);
      setEditSelectedTasksForSession(
        firstTaskIdFromSessionType(originalSessionType),
      );
    }
    setEditingMeetingPoint(null);
    clearEditMeetingPointFields();
    setShowEditMeetingPointDialog(false);
    setNewlyCreatedLocationId(null);
  };


  const renderEditSaveCancel = (sessionType: any, isEditing: boolean) => (
    <>
                            {/* Save/Cancel Buttons when editing */}
                            {isEditing && (
                              <div className="flex items-center justify-end gap-2 pt-4">
                                <Button
                                  variant="outline"
                                  onClick={() =>
                                    handleCancelEditSessionType(sessionType)
                                  }
                                  className="bg-white"
                                >
                                  Cancel
                                </Button>
                                <Button 
                                  onClick={handleUpdateSessionType}
                                  disabled={isUpdatingSessionType}
                                  className="bg-black text-white hover:bg-black/90 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isUpdatingSessionType ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                      Saving...
                                    </>
                                  ) : (
                                    'Save changes'
                                  )}
                                </Button>
                              </div>
                            )}
    </>
  );

  const renderSessionTypeExpandedView = (sessionType: any) => {
    const isEditing = editingSessionTypeId === sessionType.id;
    return (
                          <>
                            {renderBasicInfoSection(sessionType, isEditing)}
                            {renderSchedulingSection(sessionType, isEditing)}
                            {renderBookingRulesSection(sessionType, isEditing)}
                            {renderMeetingPointSection(sessionType, isEditing)}
                            {renderAssignModeratorsSection(sessionType, isEditing)}
                            {renderAssignTasksSection(sessionType, isEditing)}
                            {renderEditSaveCancel(sessionType, isEditing)}
                          </>
                        );
  };

  const getAddMeetingPointDraftError = (): string | undefined => {
    if (!meetingPointNameDraft.trim()) return 'Meeting Point Name is required';
    if (!meetingPointStreetAddressDraft.trim()) {
      return 'Meeting Point Street Address is required';
    }
    return (
      getMeetingPointLocationValidationError(
        meetingPointCountryDraft,
        meetingPointStateDraft,
        meetingPointCityDraft,
      ) || undefined
    );
  };

  /** The dialog's fields as a meeting-point row, before it has any id. */
  const buildMeetingPointDraftFromDialog = (): Record<string, unknown> => {
    const addressParts = [
      meetingPointStreetAddressDraft.trim(),
      meetingPointCityDraft.trim(),
      meetingPointStateDraft.trim(),
      meetingPointZipCodeDraft.trim(),
      meetingPointCountryDraft.trim(),
    ].filter(Boolean);
    return {
      name: meetingPointNameDraft.trim(),
      address: addressParts.join(', ') || meetingPointStreetAddressDraft.trim(),
      street_address: meetingPointStreetAddressDraft.trim(),
      city: meetingPointCityDraft.trim(),
      state: meetingPointStateDraft.trim(),
      zip_code: meetingPointZipCodeDraft.trim(),
      country: meetingPointCountryDraft.trim(),
      instructions: meetingPointInstructionsDraft.trim() || null,
      floor: null,
      capacity: null,
      is_active: true,
    };
  };

  /**
   * Turn drafted meeting points into real schedule_location rows, at the moment
   * the session type is saved (OD-1325).
   *
   * This is the only place a meeting point is created from the edit flow, so a
   * session type edit that is cancelled — or a confirm dialog that is dismissed
   * — leaves nothing behind. The venue's timezone is resolved here too, for the
   * same reason it is on every other create path: it is what the
   * cross-timezone guard compares.
   *
   * Returns the list with every draft replaced by its saved row.
   */
  const materializeDraftMeetingPoints = async (
    points: any[],
    projectId: string,
  ): Promise<any[]> => {
    if (!points.some((p) => p?.__pending)) return points;
    const resolved: any[] = [];
    for (const point of points) {
      if (!point?.__pending) {
        resolved.push(point);
        continue;
      }
      const payload: Record<string, unknown> = {
        project_id: projectId,
        name: point.name,
        address: point.address,
        street_address: point.street_address,
        city: point.city,
        state: point.state,
        zip_code: point.zip_code,
        country: point.country,
        instructions: point.instructions ?? null,
        floor: null,
        capacity: null,
        is_active: true,
        timezone:
          (await getTimezoneForLocationNames(
            String(point.country ?? ''),
            String(point.state ?? ''),
            String(point.city ?? '') || undefined,
          )) || null,
      };
      const response = await schedulerApi.createScheduleLocation(payload);
      const saved = response.data?.data;
      if (!saved?.id) {
        throw new Error(`Failed to create meeting point "${point.name}"`);
      }
      const {__pending: _pending, ...rest} = point;
      resolved.push({...rest, ...saved, id: saved.id});
    }
    return resolved;
  };

  /**
   * Meeting points on the create form (OD-1325).
   *
   * The primary is held in the meetingPoint* fields, as it always was, so the
   * create-path validation and timezone auto-population read it unchanged.
   * Secondaries are drafts in newSecondaryMeetingPoints and are only written
   * when the session type is created, like the drafts on the edit form.
   */
  const buildNewPrimaryMeetingPointRow = (): MeetingPointRow | null => {
    if (!meetingPointName.trim() && !meetingPointStreetAddress.trim()) return null;
    return {
      id: NEW_PRIMARY_MEETING_POINT_ID as unknown as number,
      name: meetingPointName,
      street_address: meetingPointStreetAddress,
      city: meetingPointCity,
      state: meetingPointState,
      zip_code: meetingPointZipCode,
      country: meetingPointCountry,
      instructions: meetingPointInstructions || null,
      is_primary: true,
      is_active: true,
      display_order: 0,
      booked_session_count: 0,
    };
  };

  const numberNewSecondaries = (points: any[]): any[] =>
    points.map((p, index) => ({...p, display_order: index + 1}));

  const resolveMeetingPointTimezone = async (point: {
    country?: unknown;
    state?: unknown;
    city?: unknown;
  }): Promise<string | null> =>
    (await getTimezoneForLocationNames(
      String(point.country ?? '').trim(),
      String(point.state ?? '').trim(),
      String(point.city ?? '').trim() || undefined,
    )) || null;

  /**
   * The refusals the API would make on create, made before any location is
   * written: two meeting points with one name, or venues in more than one
   * timezone (AC-5).
   */
  const getNewSecondaryMeetingPointsError = async (
    secondaries: any[] = newSecondaryMeetingPoints,
  ): Promise<string | undefined> => {
    const primary = buildNewPrimaryMeetingPointRow();
    if (!primary || secondaries.length === 0) return undefined;
    const all = [primary, ...secondaries];
    const names = all.map((p) => String(p.name ?? '').trim().toLowerCase());
    if (new Set(names).size !== names.length) {
      return 'Each meeting point on a session type needs its own name';
    }
    const zones = await Promise.all(all.map(resolveMeetingPointTimezone));
    const distinct = [...new Set(zones.filter(Boolean))];
    if (distinct.length > 1) {
      return (
        'Every meeting point on a session type must be in the same timezone. ' +
        `Found ${distinct.join(' and ')}.`
      );
    }
    return undefined;
  };

  const openNewSessionTypeMeetingPointDialog = () => {
    clearMeetingPointDraftFields();
    setIsEditingMeetingPoint(false);
    if (buildNewPrimaryMeetingPointRow()) {
      // A primary is already set, so this one is a secondary and the primary's
      // fields are left as they are.
      setNewSecondaryMeetingPointTarget(NEW_SECONDARY_MEETING_POINT);
    } else {
      clearMeetingPointFields();
    }
    setShowAddMeetingPointDialog(true);
  };

  const editNewSessionTypeMeetingPoint = (point: any) => {
    setIsEditingMeetingPoint(true);
    if (point.is_primary) {
      setNewSecondaryMeetingPointTarget(null);
      copyMeetingPointToDraft();
    } else {
      setNewSecondaryMeetingPointTarget(String(point.id));
      setMeetingPointNameDraft(point.name || '');
      setMeetingPointStreetAddressDraft(point.street_address || '');
      setMeetingPointCityDraft(point.city || '');
      setMeetingPointStateDraft(point.state || '');
      setMeetingPointZipCodeDraft(point.zip_code || '');
      setMeetingPointCountryDraft(point.country || '');
      setMeetingPointInstructionsDraft(point.instructions || '');
    }
    setShowAddMeetingPointDialog(true);
  };

  const saveNewSecondaryMeetingPointFromDialog = async () => {
    const target = newSecondaryMeetingPointTarget;
    const adding = target === NEW_SECONDARY_MEETING_POINT;
    const draft = buildMeetingPointDraftFromDialog();
    const next = adding
      ? [
          ...newSecondaryMeetingPoints,
          {
            ...draft,
            id: `${PENDING_MEETING_POINT_PREFIX}${Date.now()}-${newSecondaryMeetingPoints.length}`,
            __pending: true,
            is_primary: false,
            is_active: true,
            booked_session_count: 0,
          },
        ]
      : newSecondaryMeetingPoints.map((p) =>
          String(p.id) === String(target) ? {...p, ...draft} : p,
        );
    // Refused here, with the dialog still open, rather than at Create where
    // the user would have to work out which venue was the problem.
    const error = await getNewSecondaryMeetingPointsError(next);
    if (error) {
      toast({title: 'Error', description: error, variant: 'destructive'});
      return;
    }
    setNewSecondaryMeetingPoints(numberNewSecondaries(next));
    clearMeetingPointDraftFields();
    setNewSecondaryMeetingPointTarget(null);
    setIsEditingMeetingPoint(false);
    setShowAddMeetingPointDialog(false);
    toast({
      title: adding ? 'Meeting point added' : 'Meeting point updated',
      description: 'It is created when you create the session type.',
    });
  };

  const makeNewSessionTypeMeetingPointPrimary = (point: any) => {
    const oldPrimary = buildNewPrimaryMeetingPointRow();
    if (!oldPrimary) return;
    const demoted = {
      name: oldPrimary.name,
      address: formatMeetingPointAddress(oldPrimary),
      street_address: oldPrimary.street_address,
      city: oldPrimary.city,
      state: oldPrimary.state,
      zip_code: oldPrimary.zip_code,
      country: oldPrimary.country,
      instructions: oldPrimary.instructions,
      floor: null,
      capacity: null,
      id: `${PENDING_MEETING_POINT_PREFIX}${Date.now()}-primary`,
      __pending: true,
      is_primary: false,
      is_active: true,
      booked_session_count: 0,
    };
    setNewSecondaryMeetingPoints((prev) =>
      numberNewSecondaries(
        prev.map((p) => (String(p.id) === String(point.id) ? demoted : p)),
      ),
    );
    setMeetingPointName(point.name || '');
    setMeetingPointStreetAddress(point.street_address || '');
    setMeetingPointCity(point.city || '');
    setMeetingPointState(point.state || '');
    setMeetingPointZipCode(point.zip_code || '');
    setMeetingPointCountry(point.country || '');
    setMeetingPointInstructions(point.instructions || '');
    void applyTimezoneFromMeetingPointLocation(
      String(point.country ?? ''),
      String(point.state ?? ''),
      String(point.city ?? ''),
    );
  };

  const removeNewSessionTypeMeetingPoint = (point: any) => {
    setNewSecondaryMeetingPoints((prev) =>
      numberNewSecondaries(prev.filter((p) => String(p.id) !== String(point.id))),
    );
  };

  const moveNewSessionTypeMeetingPoint = (point: any, direction: -1 | 1) => {
    setNewSecondaryMeetingPoints((prev) => {
      const from = prev.findIndex((p) => String(p.id) === String(point.id));
      const to = from + direction;
      if (from < 0 || to < 0 || to >= prev.length) return prev;
      const reordered = [...prev];
      [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
      return numberNewSecondaries(reordered);
    });
  };

  const handleAddMeetingPointDialogSubmit = async () => {
    const validationError = getAddMeetingPointDraftError();
    if (validationError) {
      toast({title: 'Error', description: validationError, variant: 'destructive'});
      return;
    }

    // A secondary on the create form: held as a draft beside the primary.
    if (newSecondaryMeetingPointTarget !== null) {
      await saveNewSecondaryMeetingPointFromDialog();
      return;
    }

    // Editing an existing meeting point: defer the update to session-type save.
    if (isEditingMeetingPoint) {
      copyMeetingPointDraftToSaved();
      setIsEditingMeetingPoint(false);
      setShowAddMeetingPointDialog(false);
      return;
    }

    // Editing a saved session type: hold the meeting point as a draft.
    //
    // It used to be written to schedule_location the moment this dialog was
    // confirmed, while the link to the session type waited for Save changes.
    // Abandoning the save then left a location row belonging to no session
    // type: invisible in the UI, but still occupying its name, so re-adding it
    // was refused with "A meeting point with this name already exists" and the
    // user had no way to see or reach the thing blocking them.
    //
    // Nothing is written now until the session type itself is saved, which is
    // also when every other edit on this form takes effect.
    if (editingSessionTypeId && !isCreatingNewSessionType) {
      const draft = buildMeetingPointDraftFromDialog();
      updateMeetingPoints(editingSessionTypeId, (points) => [
        ...points,
        {
          ...draft,
          id: `${PENDING_MEETING_POINT_PREFIX}${Date.now()}-${points.length}`,
          __pending: true,
          is_primary: points.length === 0,
          is_active: true,
          display_order: points.length,
          booked_session_count: 0,
        },
      ]);
      clearMeetingPointFields();
      clearMeetingPointDraftFields();
      setIsEditingMeetingPoint(false);
      setShowAddMeetingPointDialog(false);
      toast({
        title: 'Meeting point added',
        description: 'It is created when you save the session type.',
      });
      return;
    }

    // New session type: keep draft fields in state for use on creation.
    copyMeetingPointDraftToSaved();
    toast({title: 'Success', description: 'Meeting point added successfully'});
    setIsEditingMeetingPoint(false);
    setShowAddMeetingPointDialog(false);
  };

  // Kebab menu (Edit / Disable-Re-enable) for a session type's list card.
  // PM/Admin only; hidden for moderators. Extracted from the list-card map so
  // that map callback's cognitive complexity stays manageable.
  const renderSessionTypeRowActions = (sessionType: any) => {
    if (!isProjectManager || isModerator) return null;
    return (
      <div className="flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => handleEditSessionType(sessionType)}
              disabled={!sessionType.enabled}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            {sessionType.enabled ? (
              <DropdownMenuItem
                onClick={() => handleDisableSessionType(sessionType)}
              >
                <X className="h-4 w-4 mr-2" />
                Disable
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={() => handleDisableSessionType(sessionType)}
              >
                <ArrowUp className="h-4 w-4 mr-2" />
                Re-enable
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  return (
    <>
      {isProjectManager && !isModerator && (
      <div className="-mt-[52px] mb-4 flex justify-end">
        <Button
          onClick={() => {
            // Reset all fields when opening inline form
            setEditingSessionTypeId(null);
            setNewlyCreatedLocationId(null);
            newStartDateInvalidRef.current = false;
            newEndDateInvalidRef.current = false;
            setNewSessionTypeName('');
            setNewSessionTypeJobId('');
            setNewSessionTypeDescription('');
            setAttachedFiles([]);
            setNewSessionTypeDuration('');
            setNewSessionTypeDurationUnit('minutes');
            setNewSessionTypeStartDate('');
            setNewSessionTypeEndDate('');
            setNewSessionTypeDailyStartTime('');
            setNewSessionTypeDailyEndTime('');
            setNewSessionTypeMinimumNotice('');
            setNewSessionTypeMinimumNoticeUnit('minutes');
            setNewSessionTypeBufferBefore('');
            setNewSessionTypeBufferBeforeUnit('minutes');
            setNewSessionTypeBufferAfter('');
            setNewSessionTypeBufferAfterUnit('minutes');
            setNewSessionTypeParticipantsPerSlot('');
            setNewSessionTypeTimezone('');
            setNewSessionTypeParticipantBookingRules(false);
            setNewSessionTypeAllowMultiBooking(false);
            setNewSessionTypeMaxSessionsPerParticipant('');
            setNewSessionTypeEnableNoShowLimit(false);
            setNewSessionTypeNoShowCancellationLimit('');
            setNewSessionTypeAllowConcurrentModeratorBookings(false);
            setNewSessionTypeMaxConcurrentModeratorSessions('');
            setSelectedModeratorsForSession([]);
            setSelectedTasksForSession(null);
            setShowAddMeetingPointDialog(false);
              clearMeetingPointFields();
            setIsEditingMeetingPoint(false);
            setIsCreatingNewSessionType(true);
          }}
          className="gap-2 bg-black text-white hover:bg-black/90 rounded-lg px-4 py-2 h-10"
        >
          <Plus className="h-4 w-4" />
          New Session Type
        </Button>
      </div>
      )}

      {/* Inline New Session Type Form */}
      {isCreatingNewSessionType && (
        <Card className="overflow-hidden shadow-sm rounded-lg border mb-4">
          <CardContent className="p-0">
            <div className="border-t bg-gray-100 p-5 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-base text-foreground">
                  New Session Type
                </h3>
              </div>

              {/* Basic Info */}
              <div className="space-y-4 bg-white rounded-lg p-4">
                <h4 className="font-semibold text-base">Basic Info</h4>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-foreground">
                      Session name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={newSessionTypeName}
                      onChange={(e) => setNewSessionTypeName(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-foreground">
                      Session type <span className="text-red-500">*</span>
                    </Label>
                    <div>
                      <ToggleGroup
                        type="single"
                        value={newSessionTypeLocationType}
                        onValueChange={(value) => {
                          if (value)
                            setNewSessionTypeLocationType(
                              value as 'onsite' | 'offsite',
                            );
                        }}
                        className="inline-flex rounded-lg border border-input bg-transparent"
                      >
                        <ToggleGroupItem
                          value="onsite"
                          aria-label="Onsite"
                          className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                        >
                          Onsite
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          value="offsite"
                          aria-label="Offsite"
                          className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                        >
                          Offsite
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-foreground">
                      Job <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={newSessionTypeJobId}
                      onValueChange={setNewSessionTypeJobId}
                      disabled={loadingJobs || availableJobs.length === 0}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue
                          placeholder={
                            loadingJobs
                              ? 'Loading jobs...'
                              : availableJobs.length === 0
                                ? 'No jobs available'
                                : 'Select job'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {getAssignableJobOptions().map((job) => (
                          <SelectItem key={job.job_id} value={job.job_id}>
                            {getJobDisplayName(job.job_id, job.job_name)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-foreground">
                      Session description <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Textarea
                      value={newSessionTypeDescription}
                      onChange={(e) =>
                        setNewSessionTypeDescription(e.target.value)
                      }
                      rows={3}
                      className="bg-white resize-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-foreground">
                      Attach file <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <div className="space-y-2">
                      <label
                        htmlFor="add-file-upload"
                        aria-label="Choose files"
                        className="cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const input = document.getElementById(
                                'add-file-upload',
                              ) as HTMLInputElement;
                              input?.click();
                            }}
                            disabled={
                              attachedFiles.length >= MAX_ATTACHMENT_FILES
                            }
                            className="bg-white"
                          >
                            Choose files
                          </Button>
                          <span className="text-sm text-muted-foreground">
                            {getFileStatusText(attachedFiles)}
                          </span>
                        </div>
                      </label>
                      <input
                        id="add-file-upload"
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          
                          // Validate file sizes
                          const invalidFiles = files.filter(
                            (file) => !validateFileSize(file).valid,
                          );
                          if (invalidFiles.length > 0) {
                            invalidFiles.forEach((file) => {
                              const validation = validateFileSize(file);
                              if (!validation.valid) {
                                toast({
                                  title: 'File too large',
                                  description: validation.error,
                                  variant: 'destructive',
                                });
                              }
                            });
                            // Reset input value
                            e.target.value = '';
                            return;
                          }
                          
                          const remainingSlots =
                            getRemainingSlots(attachedFiles);
                          if (remainingSlots > 0) {
                            const filesToAdd = files.slice(0, remainingSlots);
                            setAttachedFiles([...attachedFiles, ...filesToAdd]);
                            if (files.length > remainingSlots) {
                              toast({
                                title: 'Maximum files reached',
                                description: `You can only attach up to ${MAX_ATTACHMENT_FILES} files.`,
                                variant: 'destructive',
                              });
                            }
                          } else {
                            toast({
                              title: 'Maximum files reached',
                              description: `You can only attach up to ${MAX_ATTACHMENT_FILES} files.`,
                              variant: 'destructive',
                            });
                          }
                          // Reset input value to allow selecting the same file again
                          e.target.value = '';
                        }}
                        disabled={attachedFiles.length >= MAX_ATTACHMENT_FILES}
                        className="hidden"
                        accept="*/*"
                      />
                      {attachedFiles.length > 0 && (
                        <div className="space-y-2">
                          {attachedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded-md border"
                            >
                              <span className="text-sm text-foreground truncate flex-1 mr-2">
                                {file.name}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setAttachedFiles(
                                    attachedFiles.filter((_, i) => i !== index),
                                  );
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      {attachedFiles.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {attachedFiles.length} of {MAX_ATTACHMENT_FILES} files
                          attached
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Scheduling Window & Rules */}
              <div className="space-y-4 bg-white rounded-lg p-4">
                <h4 className="font-semibold text-base">
                  Scheduling Window & Rules
                </h4>
                <div className="space-y-4">
                  {isProjectManager && (
                    <SlotGenerationModeField
                      idPrefix="new-slot-mode"
                      value={newSessionTypeSlotMode}
                      onChange={setNewSessionTypeSlotMode}
                    />
                  )}

                  {/* Row 1: Start Date and End Date */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-foreground">
                        Start date <span className="text-red-500">*</span>
                      </Label>
                      <CalendarDateInput
                        name="new-session-type-start-date"
                        value={newSessionTypeStartDate}
                        onValueChange={setNewSessionTypeStartDate}
                        invalidRef={newStartDateInvalidRef}
                        min={format(new Date(), 'yyyy-MM-dd')}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-foreground">
                        End date <span className="text-red-500">*</span>
                      </Label>
                      <CalendarDateInput
                        name="new-session-type-end-date"
                        value={newSessionTypeEndDate}
                        onValueChange={setNewSessionTypeEndDate}
                        invalidRef={newEndDateInvalidRef}
                        minFromStartDate={
                          newSessionTypeStartDate ||
                          format(new Date(), 'yyyy-MM-dd')
                        }
                      />
                    </div>
                  </div>

                  {isBookingOptionalModeratorEnabled() && (
                    <SessionTypeDailyTimeRangeField
                      startTime={newSessionTypeDailyStartTime}
                      endTime={newSessionTypeDailyEndTime}
                      onTimesChange={({start, end}) => {
                        setNewSessionTypeDailyStartTime(start);
                        setNewSessionTypeDailyEndTime(end);
                      }}
                    />
                  )}

                  {isProjectManager && (
                    <SchedulingRulesFields
                      idPrefix="new"
                      value={newSessionTypeSchedulingRules}
                      onChange={setNewSessionTypeSchedulingRules}
                    />
                  )}

                  {/* Row 2: Minimum notice before start and Duration */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-foreground flex items-center gap-1.5">
                        Self-Booking Lead Time <span className="text-muted-foreground font-normal">(optional)</span>
                        <InfoHint text={SELF_BOOKING_LEAD_TIME_HINT} />
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder=""
                          value={newSessionTypeMinimumNotice}
                          onChange={(e) =>
                            setNewSessionTypeMinimumNotice(e.target.value)
                          }
                          className="flex-1 bg-white"
                        />
                        <Select 
                          value={newSessionTypeMinimumNoticeUnit}
                          onValueChange={setNewSessionTypeMinimumNoticeUnit}
                        >
                          <SelectTrigger className="w-[120px] bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hours">hours</SelectItem>
                            <SelectItem value="minutes">minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-foreground">
                        Duration <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder=""
                          value={newSessionTypeDuration}
                          onChange={(e) =>
                            setNewSessionTypeDuration(e.target.value)
                          }
                          className="flex-1 bg-white"
                          min="0"
                        />
                        <Select 
                          value={newSessionTypeDurationUnit}
                          onValueChange={setNewSessionTypeDurationUnit}
                        >
                          <SelectTrigger className="w-[120px] bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hours">hours</SelectItem>
                            <SelectItem value="minutes">minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Row 3: Buffer before session and Buffer after session */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-foreground">
                        Buffer time before session <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder=""
                          value={newSessionTypeBufferBefore}
                          onChange={(e) =>
                            setNewSessionTypeBufferBefore(e.target.value)
                          }
                          className="flex-1 bg-white"
                          min="0"
                        />
                        <Select 
                          value={newSessionTypeBufferBeforeUnit}
                          onValueChange={setNewSessionTypeBufferBeforeUnit}
                        >
                          <SelectTrigger className="w-[120px] bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hours">hours</SelectItem>
                            <SelectItem value="minutes">minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-foreground">
                        Buffer time after session <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder=""
                          value={newSessionTypeBufferAfter}
                          onChange={(e) =>
                            setNewSessionTypeBufferAfter(e.target.value)
                          }
                          className="flex-1 bg-white"
                          min="0"
                        />
                        <Select 
                          value={newSessionTypeBufferAfterUnit}
                          onValueChange={setNewSessionTypeBufferAfterUnit}
                        >
                          <SelectTrigger className="w-[120px] bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hours">hours</SelectItem>
                            <SelectItem value="minutes">minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Participants per Slot (per-slot flat cap) */}
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5 text-sm font-normal text-foreground">
                      Participants per Slot <span className="text-muted-foreground font-normal">(optional)</span>
                      <InfoHint text={PARTICIPANTS_PER_SLOT_HINT} />
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Enter participant limit per slot"
                      value={newSessionTypeParticipantsPerSlot}
                      disabled={isModeratorAvailabilityMode(newSessionTypeSlotMode)}
                      onChange={(e) =>
                        setNewSessionTypeParticipantsPerSlot(e.target.value)
                      }
                      className={
                        isModeratorAvailabilityMode(newSessionTypeSlotMode)
                          ? 'bg-gray-100 text-gray-400'
                          : 'bg-white'
                      }
                    />
                  </div>

                </div>
              </div>

              {/* Booking Rules (OD-1097 + OD-1102 / OD-1111, added alongside as
                  those stories land) — off/unset by default. */}
              <div className="space-y-4 bg-white rounded-lg p-4">
                <div>
                  <h4 className="font-semibold text-base">Booking Rules</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Opt-in behaviours that override standard one-slot, one-booking limits.
                  </p>
                </div>
                <div className="divide-y divide-gray-100">
                  <BookingRuleRow
                    id="new-allow-concurrent-moderator"
                    title="Allow concurrent moderator bookings"
                    description="Moderator can run more than one session at once."
                    hint={CONCURRENT_MODERATOR_HINT}
                    checked={newSessionTypeAllowConcurrentModeratorBookings}
                    onCheckedChange={setNewSessionTypeAllowConcurrentModeratorBookings}
                    isEditing
                    fieldLabel="Maximum concurrent sessions"
                    fieldValue={newSessionTypeMaxConcurrentModeratorSessions}
                    onFieldChange={setNewSessionTypeMaxConcurrentModeratorSessions}
                    fieldPlaceholder="e.g. 2"
                    fieldMin="2"
                  />
                  <BookingRuleRow
                    id="new-allow-multi-booking"
                    title="Allow multiple bookings per participant"
                    description="A participant can hold more than one session for this job."
                    hint={ALLOW_MULTI_BOOKING_HINT}
                    checked={resolveAllowMultipleBookings(
                      newSessionTypeWindowCap.enabled,
                      newSessionTypeAllowMultiBooking,
                    )}
                    onCheckedChange={setNewSessionTypeAllowMultiBooking}
                    isEditing
                    toggleDisabled={newSessionTypeWindowCap.enabled}
                    fieldLabel="Max sessions per participant"
                    fieldValue={newSessionTypeMaxSessionsPerParticipant}
                    onFieldChange={setNewSessionTypeMaxSessionsPerParticipant}
                    fieldPlaceholder="e.g. 10"
                    fieldMin="2"
                  />
                  <BookingRuleRow
                    id="new-cap-group-booking-size"
                    title="Cap group booking size"
                    description="Set a maximum number of participants per group booking. If unchecked, group booking size is unlimited."
                    hint={GROUP_SIZE_CAP_HINT}
                    checked={newSessionTypeCapGroupBookingSize}
                    onCheckedChange={setNewSessionTypeCapGroupBookingSize}
                    isEditing
                    control="checkbox"
                    fieldLabel="Maximum group size"
                    fieldValue={newSessionTypeMaxGroupSize}
                    onFieldChange={setNewSessionTypeMaxGroupSize}
                    fieldPlaceholder="e.g. 3"
                    fieldMin="2"
                  />

                  <BookingRuleRow
                    id="new-enable-no-show-limit"
                    title="Flag participants after repeated no-shows or cancellations"
                    description="Alert the PM when a participant reaches the set number of no-shows or cancellations."
                    hint={NO_SHOW_LIMIT_HINT}
                    checked={newSessionTypeEnableNoShowLimit}
                    onCheckedChange={setNewSessionTypeEnableNoShowLimit}
                    isEditing
                    fieldLabel="No-show / cancellation limit"
                    fieldValue={newSessionTypeNoShowCancellationLimit}
                    onFieldChange={setNewSessionTypeNoShowCancellationLimit}
                    fieldPlaceholder="e.g. 3"
                    fieldMin="1"
                  />
                  <ParticipantWindowCapRule
                    idPrefix="new-window-cap"
                    value={newSessionTypeWindowCap}
                    onChange={setNewSessionTypeWindowCap}
                    isEditing
                  />
                </div>
              </div>

              {/* Meeting Points: the primary first, then any secondaries */}
              {buildNewPrimaryMeetingPointRow() ? (
                <>
                  <MeetingPointsList
                    meetingPoints={[
                      buildNewPrimaryMeetingPointRow() as MeetingPointRow,
                      ...newSecondaryMeetingPoints,
                    ]}
                    isEditing
                    onAdd={openNewSessionTypeMeetingPointDialog}
                    onEdit={editNewSessionTypeMeetingPoint}
                    onMakePrimary={makeNewSessionTypeMeetingPointPrimary}
                    // Nothing is booked against a venue that does not exist
                    // yet, so a secondary is only ever removed, never deactivated.
                    onToggleActive={removeNewSessionTypeMeetingPoint}
                    onRemove={removeNewSessionTypeMeetingPoint}
                    onMove={moveNewSessionTypeMeetingPoint}
                  />
                  <div className="bg-white rounded-lg px-4 pb-4">
                    <SessionTimezoneField
                      timezone={newSessionTypeTimezone}
                      onTimezoneChange={handleSessionTimezoneManualChange}
                      showHelper
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Resolved from the primary meeting point. Every meeting point on this
                      session type must be in this timezone.
                    </p>
                  </div>
                </>
              ) : (
                <div className="space-y-4 bg-white rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-base">
                      Meeting Point {newSessionTypeLocationType === 'onsite' && <span className="text-red-500 font-normal">*</span>}
                    </h4>
                    <Button
                      onClick={openNewSessionTypeMeetingPointDialog}
                      className="gap-2 bg-black text-white hover:bg-black/90 rounded-lg px-4 py-2 h-auto"
                    >
                      <Plus className="h-4 w-4" />
                      Add meeting point
                    </Button>
                  </div>
                  <div className="text-center py-8">
                    <p className="text-sm text-muted-foreground">
                      No meeting point added yet
                    </p>
                  </div>
                </div>
              )}

              {/* Assign Moderators */}
              <div className="space-y-4 bg-white rounded-lg p-4">
                <div>
                  <h4 className="font-semibold text-base mb-1">
                    Assign Moderators <span className="text-red-500">*</span>
                    <span className="ml-1 inline-flex align-middle">
                      <InfoHint text={MODERATOR_AVAILABILITY_SYNC_TOOLTIP} />
                    </span>
                  </h4>
                  <p className="text-sm font-normal text-foreground">
                    Select moderators for this session type
                  </p>
                </div>
                {renderCreateModeratorList()}
              </div>

              {/* Assign Tasks */}
              <div className="space-y-4 bg-white rounded-lg p-4">
                <div>
                  <h4 className="font-semibold text-base mb-1">Assign Task <span className="text-red-500">*</span></h4>
                  <p className="text-sm font-normal text-foreground">
                    Select one task for this session type
                  </p>
                </div>
                <div className="space-y-2">
                  {availableTasks.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No tasks available.
                    </p>
                  ) : (
                    <>
                      {availableTasks.map((task) => {
                        const isSelected = selectedTasksForSession === task.id;
                        return (
                          <label
                            key={task.id}
                            className="flex items-center space-x-3 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="assign-task"
                              className="w-4 h-4"
                              checked={isSelected}
                              onChange={() => {
                                setSelectedTasksForSession(task.id);
                              }}
                            />
                            <span className="text-sm">{task.title}</span>
                          </label>
                        );
                      })}
                      {!selectedTasksForSession &&
                        availableTasks.length > 0 && (
                          <p className="text-sm text-gray-400">
                            No task assigned yet
                          </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Save/Cancel Buttons */}
              <div className="flex items-center justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreatingNewSessionType(false);
                    newStartDateInvalidRef.current = false;
                    newEndDateInvalidRef.current = false;
                    setNewSessionTypeName('');
                    setNewSessionTypeJobId('');
                    setNewSessionTypeDescription('');
                    setAttachedFiles([]);
                    setModeratorSearchCreate('');
                    setNewSessionTypeDuration('');
                    setNewSessionTypeDurationUnit('minutes');
                    setNewSessionTypeStartDate('');
                    setNewSessionTypeEndDate('');
                    setNewSessionTypeDailyStartTime('');
                    setNewSessionTypeDailyEndTime('');
                    setNewSessionTypeMinimumNotice('');
                    setNewSessionTypeMinimumNoticeUnit('minutes');
                    setNewSessionTypeBufferBefore('');
                    setNewSessionTypeBufferBeforeUnit('minutes');
                    setNewSessionTypeBufferAfter('');
                    setNewSessionTypeBufferAfterUnit('minutes');
                    setNewSessionTypeParticipantsPerSlot('');
                    setNewSessionTypeTimezone('');
                    setNewSessionTypeParticipantBookingRules(false);
                    setNewSessionTypeAllowMultiBooking(false);
                    setNewSessionTypeMaxSessionsPerParticipant('');
                    setNewSessionTypeEnableNoShowLimit(false);
                    setNewSessionTypeNoShowCancellationLimit('');
                    setNewSessionTypeAllowConcurrentModeratorBookings(false);
                    setNewSessionTypeMaxConcurrentModeratorSessions('');
                    setSelectedModeratorsForSession([]);
                    setSelectedTasksForSession(null);
                    setShowAddMeetingPointDialog(false);
                    clearMeetingPointFields();
                    setIsEditingMeetingPoint(false);
                  }}
                  className="bg-white"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddSessionType}
                  disabled={isCreatingSessionType}
                  className="bg-black text-white hover:bg-black/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingSessionType ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    'Create Session Type'
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {(() => {
        // Apply the Schedule page's dynamic filter on top of the locally
        // loaded session types. `allowedSessionTypeNames === undefined` means
        // no page-level filter is active; otherwise we only render rows whose
        // name passed the parent's filter predicate.
        const visibleSessionTypes = allowedSessionTypeNames
          ? sessionTypes.filter((st: any) =>
              allowedSessionTypeNames.has(String(st?.name || '').trim()),
            )
          : sessionTypes;
        if (loadingSessionTypes) {
          return (
            <Card>
              <CardContent className="pt-6">
                <PageLoader message="Loading" minHeight="min-h-[200px]" />
              </CardContent>
            </Card>
          );
        }
        if (visibleSessionTypes.length === 0 && !isCreatingNewSessionType) {
          return (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    {allowedSessionTypeNames
                      ? 'No session types match the current filter.'
                      : 'No session types found. Create your first session type.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        }
        // Active project moderator ids (OD-1065), computed once for all cards below.
        // null while the roster is still loading — the badge shows a placeholder in
        // that case (see moderatorsCount below) rather than a raw/unfiltered count
        // that would visibly flicker to the correct value once the roster arrives.
        const activeModeratorIdsForCount = projectModeratorsLoading
          ? null
          : new Set(projectModerators.map((m: any) => String(m.user_id || m.id)));
        return (
          <div className="space-y-4">
          {visibleSessionTypes.map((sessionType: any) => {
            const isExpanded = expandedSessionTypes.has(sessionType.id);
            const locations = sessionType.locations || [];
            const meetingPoint =
              locations.length > 0
                ? locations[0].name || locations[0].address || 'N/A'
              : 'No meeting point assigned';

            return (
              <Card
                key={sessionType.id}
                className="overflow-hidden shadow-sm rounded-lg border"
              >
                <CardContent className="p-0">
                  {/* Collapsed View */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-base text-foreground">
                            {sessionType.name}
                          </h3>
                          {!sessionType.enabled && (
                            <DisabledStatusBadge sessionType={sessionType} />
                          )}
                          <button
                            onClick={() => {
                              const newExpanded = new Set(expandedSessionTypes);
                              if (isExpanded) {
                                newExpanded.delete(sessionType.id);
                              } else {
                                newExpanded.add(sessionType.id);
                              }
                              setExpandedSessionTypes(newExpanded);
                            }}
                            className="ml-1 font-semibold text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        {!isExpanded && (
                          <>
                            <p className="text-sm text-muted-foreground mb-3">
                              {sessionType.description ||
                                'No description provided'}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                              <Badge
                                variant="secondary"
                                className="font-normal bg-gray-100 text-gray-700 px-2.5 py-0.5 rounded-full text-xs"
                              >
                                {getSessionTypeDateRangeLabel(sessionType)}
                              </Badge>
                              <span>
                                Meeting point: <span>{meetingPoint}</span>
                              </span>
                              <span>
                                Moderators:{' '}
                                <span>
                                  {getModeratorsCountDisplay(
                                    sessionType,
                                    activeModeratorIdsForCount,
                                    projectModeratorsLoading,
                                  )}
                                </span>
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      {renderSessionTypeRowActions(sessionType)}
                    </div>
                  </div>

                  {/* Expanded View */}
                  {isExpanded && (
                    <div className="border-t bg-gray-100">
                      <Tabs defaultValue="details" className="w-full">
                        <TabsList className="mx-5 mt-4 w-fit">
                          <TabsTrigger value="details">Details</TabsTrigger>
                          <TabsTrigger value="history">History</TabsTrigger>
                        </TabsList>
                        <TabsContent
                          value="details"
                          forceMount
                          className="mt-0 space-y-4 p-5 pt-4 data-[state=inactive]:hidden"
                        >
                          {renderSessionTypeExpandedView(sessionType)}
                        </TabsContent>
                        <TabsContent
                          value="history"
                          forceMount
                          className="mt-0 p-5 pt-4 data-[state=inactive]:hidden"
                        >
                          <SessionTypeHistoryPanel sessionTypeId={String(sessionType.id)} />
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        );
      })()}

      {/* Add/Edit Meeting Point Dialog */}
      <Dialog
        open={showAddMeetingPointDialog}
        onOpenChange={(open) => {
          if (open) {
            copyMeetingPointToDraft();
          }
          if (!open) {
            // Reset fields when dialog is closed/cancelled (unless it was saved)
            // Only reset if:
            // 1. Not in editing mode (editing mode means it was already added)
            // 2. AND we're editing an existing session type (not creating a new one)
            // For new session types, keep the fields so they can be used when creating the session type
            // A cancelled secondary leaves the primary's fields alone.
            if (
              !isEditingMeetingPoint &&
              editingSessionTypeId &&
              newSecondaryMeetingPointTarget === null
            ) {
              clearMeetingPointFields();
            }
            copyMeetingPointToDraft();
            setNewSecondaryMeetingPointTarget(null);
          }
          setShowAddMeetingPointDialog(open);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditingMeetingPoint
                ? 'Edit Meeting Point'
                : 'Add Meeting Point'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                Name <span className="text-red-500">*</span>
              </Label>
              {/* Hidden password field to trick Chrome - makes it think this is a login form, not address form */}
              <input
                type="password"
                autoComplete="new-password"
                style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none', width: '1px', height: '1px' }}
                tabIndex={-1}
                readOnly
                aria-hidden="true"
              />
              <Input
                type="text"
                placeholder=""
                value={meetingPointNameDraft}
                onChange={(e) => setMeetingPointNameDraft(e.target.value)}
                onFocus={(e) => {
                  e.currentTarget.removeAttribute('readonly');
                }}
                onMouseDown={(e) => {
                  (e.currentTarget as HTMLInputElement).removeAttribute(
                    'readonly',
                  );
                }}
                className="bg-white"
                autoComplete="off"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                name="new-meeting-point-name"
                readOnly
                spellCheck="false"
                autoCorrect="off"
                autoCapitalize="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                Street address <span className="text-red-500">*</span>
              </Label>
              {/* Hidden password field to trick Chrome - makes it think this is a login form, not address form */}
              <input
                type="password"
                autoComplete="new-password"
                style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none', width: '1px', height: '1px' }}
                tabIndex={-1}
                readOnly
                aria-hidden="true"
              />
              <Input
                type="text"
                placeholder=""
                value={meetingPointStreetAddressDraft}
                onChange={(e) =>
                  setMeetingPointStreetAddressDraft(e.target.value)
                }
                onFocus={(e) => {
                  e.currentTarget.removeAttribute('readonly');
                }}
                onMouseDown={(e) => {
                  (e.currentTarget as HTMLInputElement).removeAttribute(
                    'readonly',
                  );
                }}
                className="bg-white"
                autoComplete="off"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                name="new-meeting-point-street-address"
                readOnly
                spellCheck="false"
                autoCorrect="off"
                autoCapitalize="off"
              />
            </div>
            <CountryStateCityDropdown
              country={meetingPointCountryDraft}
              state={meetingPointStateDraft}
              city={meetingPointCityDraft}
              onCountryChange={setMeetingPointCountryDraft}
              onStateChange={setMeetingPointStateDraft}
              onCityChange={setMeetingPointCityDraft}
              onTimezoneFromState={applySessionTimezoneIfPrimary}
              timezoneUserOverrideRef={sessionTimezoneUserOverrideRef}
              countryRequired
              stateRequired
              cityRequired
            />
            <SessionTimezoneField
              timezone={
                editingSessionTypeId
                  ? editSessionTypeTimezone
                  : newSessionTypeTimezone
              }
              onTimezoneChange={handleSessionTimezoneManualChange}
              showHelper
            />
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                ZIP code
              </Label>
              <Input
                type="text"
                placeholder=""
                value={meetingPointZipCodeDraft}
                onChange={(e) => setMeetingPointZipCodeDraft(e.target.value)}
                onFocus={(e) => {
                  e.currentTarget.removeAttribute('readonly');
                }}
                onMouseDown={(e) => {
                  (e.currentTarget as HTMLInputElement).removeAttribute(
                    'readonly',
                  );
                }}
                className="bg-white"
                autoComplete="off"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                name="new-meeting-point-zip"
                readOnly
                spellCheck="false"
                autoCorrect="off"
                autoCapitalize="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                Instructions to enter (Optional)
              </Label>
              <Textarea
                placeholder=""
                value={meetingPointInstructionsDraft}
                onChange={(e) =>
                  setMeetingPointInstructionsDraft(e.target.value)
                }
                rows={3}
                className="bg-white resize-none"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  // Reset fields when cancelled (unless it was already saved)
                  // Only clear if editing an existing session type
                  // For new session types, keep the fields so they can be used when creating the session type
                  if (
                    !isEditingMeetingPoint &&
                    editingSessionTypeId &&
                    newSecondaryMeetingPointTarget === null
                  ) {
                    clearMeetingPointFields();
                  }
                  copyMeetingPointToDraft();
                  setNewSecondaryMeetingPointTarget(null);
                  setShowAddMeetingPointDialog(false);
                }}
                className="bg-white"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddMeetingPointDialogSubmit}
                className="bg-black text-white hover:bg-black/90"
              >
                {isEditingMeetingPoint
                  ? 'Update meeting point'
                  : 'Add meeting point'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Meeting Point Dialog */}
      <Dialog
        open={showEditMeetingPointDialog}
        onOpenChange={(open) => {
          if (open) {
            meetingPointDialogCommittedRef.current = false;
          } else if (
            !meetingPointDialogCommittedRef.current &&
            editingMeetingPoint
          ) {
            // Revert only when dismissed without a successful save (overlay / Escape)
            applyMeetingPointFieldsToForm(editingMeetingPoint);
          }
          setShowEditMeetingPointDialog(open);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Meeting Point</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                Name <span className="text-red-500">*</span>
              </Label>
              {/* Hidden password field to trick Chrome - makes it think this is a login form, not address form */}
              <input
                type="password"
                autoComplete="new-password"
                style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none', width: '1px', height: '1px' }}
                tabIndex={-1}
                readOnly
                aria-hidden="true"
              />
              <Input
                type="text"
                placeholder=""
                value={editMeetingPointName}
                onChange={(e) => setEditMeetingPointName(e.target.value)}
                onFocus={(e) => {
                  e.currentTarget.removeAttribute('readonly');
                }}
                onMouseDown={(e) => {
                  (e.currentTarget as HTMLInputElement).removeAttribute(
                    'readonly',
                  );
                }}
                className="bg-white"
                autoComplete="new-password"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                name="mp-name-edit-field"
                readOnly
                spellCheck="false"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                Street address <span className="text-red-500">*</span>
              </Label>
              {/* Hidden password field to trick Chrome - makes it think this is a login form, not address form */}
              <input
                type="password"
                autoComplete="new-password"
                style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none', width: '1px', height: '1px' }}
                tabIndex={-1}
                readOnly
                aria-hidden="true"
              />
              <Input
                type="text"
                placeholder=""
                value={editMeetingPointStreetAddress}
                onChange={(e) =>
                  setEditMeetingPointStreetAddress(e.target.value)
                }
                onFocus={(e) => {
                  e.currentTarget.removeAttribute('readonly');
                }}
                onMouseDown={(e) => {
                  (e.currentTarget as HTMLInputElement).removeAttribute(
                    'readonly',
                  );
                }}
                className="bg-white"
                autoComplete="new-password"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                name="mp-street-edit-field"
                readOnly
                spellCheck="false"
              />
            </div>
            <CountryStateCityDropdown
              key={`edit-mp-${editingMeetingPoint?.id ?? 'new'}-${editMeetingPointCountry}-${editMeetingPointState}-${editMeetingPointCity}`}
              country={editMeetingPointCountry}
              state={editMeetingPointState}
              city={editMeetingPointCity}
              onCountryChange={setEditMeetingPointCountry}
              onStateChange={setEditMeetingPointState}
              onCityChange={setEditMeetingPointCity}
              onTimezoneFromState={applySessionTimezoneIfPrimary}
              timezoneUserOverrideRef={sessionTimezoneUserOverrideRef}
              countryRequired
              stateRequired
              cityRequired
            />
            <SessionTimezoneField
              timezone={editSessionTypeTimezone}
              onTimezoneChange={handleSessionTimezoneManualChange}
              showHelper
            />
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                ZIP code
              </Label>
              <Input
                type="text"
                placeholder=""
                value={editMeetingPointZipCode}
                onChange={(e) => setEditMeetingPointZipCode(e.target.value)}
                onFocus={(e) => {
                  e.currentTarget.removeAttribute('readonly');
                }}
                onMouseDown={(e) => {
                  (e.currentTarget as HTMLInputElement).removeAttribute(
                    'readonly',
                  );
                }}
                className="bg-white"
                autoComplete="new-password"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                name="mp-zip-edit-field"
                readOnly
                spellCheck="false"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                Instructions to enter (Optional)
              </Label>
              <Textarea
                placeholder=""
                value={editMeetingPointInstructions}
                onChange={(e) =>
                  setEditMeetingPointInstructions(e.target.value)
                }
                rows={3}
                className="bg-white resize-none"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-4">
              <Button
                variant="outline"
                disabled={isSavingMeetingPoint}
                onClick={() => {
                  meetingPointDialogCommittedRef.current = false;
                  if (editingMeetingPoint) {
                    applyMeetingPointFieldsToForm(editingMeetingPoint);
                  }
                  setShowEditMeetingPointDialog(false);
                }}
                className="bg-white"
              >
                Cancel
              </Button>
              <Button
                onClick={() => void handleUpdateMeetingPoint()}
                disabled={isSavingMeetingPoint}
                className="bg-black text-white hover:bg-black/90"
              >
                {isSavingMeetingPoint ? 'Saving...' : 'Update meeting point'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Session Type Update Confirmation Dialog */}
      <AlertDialog
        open={showSessionTypeUpdateConfirm}
        onOpenChange={setShowSessionTypeUpdateConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Session Type</AlertDialogTitle>
            <AlertDialogDescription>
              This change affects future sessions only. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setShowSessionTypeUpdateConfirm(false);
                setPendingUpdatePayload(null);
              }}
              disabled={isUpdatingSessionType}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                setShowSessionTypeUpdateConfirm(false);
                if (!pendingUpdatePayload) return;
                try {
                  await performSessionTypeUpdate(pendingUpdatePayload);
                } catch {
                  // performSessionTypeUpdate has already surfaced the failure in
                  // a toast and rethrows for any caller that needs to know. This
                  // one is fire-and-forget: React does not await an onClick, so
                  // letting the rejection escape leaves an unhandled rejection —
                  // console noise in the browser, and a failed CI run even when
                  // every test passes.
                } finally {
                  // Cleared either way, so a failed save cannot leave a stale
                  // payload behind to be resubmitted later.
                  setPendingUpdatePayload(null);
                }
              }}
              disabled={isUpdatingSessionType}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdatingSessionType ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                'Continue'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SessionTypesTab;
